import { escapeHtml, isSafeUrl, sanitizeUrl, renderMicroMarkdown } from "@profolio/security";
import { getAccessibleForeground, getContrastRatio } from "./contrast";
export * from "./primitives";
import {
  renderSvgIcon,
  cleanDisplayUrl,
  renderSiteNavigation,
  renderFilterBar,
  resolvePlatformInfo,
  renderDetailDialog,
} from "./primitives";
import type {
  ColorPalette,
  SemanticPalette,
  ThemeEffects,
  CustomColors,
  FontPairingConfig,
  ProfileData,
  SectionData,
  LayoutOverrides,
  HeroOverrides,
  SectionElementOverrides,
  MediaContent,
  PublicationItem,
  ResearchAreaItem,
  ExperienceItem,
  TeachingItem,
  ProjectItem,
  SkillItem,
  SkillGroupItem,
  DownloadItem,
  SocialLinkItem,
  HighlightItem,
} from "./types";

// ─── Color Math Utilities ───────────────────────────────────────────────────

/**
 * Determines if a hex color is dark according to standard perceived luminance (ITU-R BT.709).
 */
export function isColorDark(hex: string): boolean {
  const cleanHex = hex.replace("#", "");
  const fullHex =
    cleanHex.length === 3
      ? cleanHex
          .split("")
          .map((c) => c + c)
          .join("")
      : cleanHex;
  const rgb = parseInt(fullHex, 16);
  if (isNaN(rgb)) return false;
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = (rgb >> 0) & 0xff;
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luma < 128;
}

/**
 * Returns white or slate-900 foreground color to guarantee WCAG AA text contrast against any background.
 */
export function getContrastColor(hex: string): string {
  return isColorDark(hex) ? "#ffffff" : "#0f172a";
}

/**
 * Adjusts brightness of a hex color (positive amount to lighten, negative to darken).
 */
export function adjustBrightness(hex: string, amount: number): string {
  let color = hex.replace("#", "");
  if (color.length === 3) {
    color = color
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const num = parseInt(color, 16);
  if (isNaN(num)) return hex;
  let r = (num >> 16) + Math.round(255 * amount);
  let g = ((num >> 8) & 0x00ff) + Math.round(255 * amount);
  let b = (num & 0x0000ff) + Math.round(255 * amount);
  r = Math.max(Math.min(255, r), 0);
  g = Math.max(Math.min(255, g), 0);
  b = Math.max(Math.min(255, b), 0);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

/**
 * Appends alpha hex transparency to a 6-digit hex color.
 */
export function adjustOpacity(hex: string, opacity: number): string {
  let color = hex.replace("#", "");
  if (color.length === 3) {
    color = color
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const alpha = Math.max(0, Math.min(255, Math.round(opacity * 255)))
    .toString(16)
    .padStart(2, "0");
  return `#${color}${alpha}`;
}

/**
 * Validates whether a color string is a safe hex color code.
 */
function isValidHexColor(color: string | undefined): boolean {
  if (!color) return false;
  return /^#[0-9a-fA-F]{3,8}$/.test(color);
}

/**
 * Validates whether a color string is a safe CSS color (hex, rgb, rgba, hsl, hsla, transparent, currentColor).
 */
export function isSafeCssColor(color: string | undefined | null): boolean {
  if (!color) return false;
  const trimmed = color.trim();
  if (/^#[0-9a-fA-F]{3,8}$/.test(trimmed)) return true;
  if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(\s*,\s*[\d.]+%?\s*)?\)$/i.test(trimmed)) return true;
  if (/^hsla?\(\s*[\d.]+\s*,\s*[\d.]+%?\s*,\s*[\d.]+%?(\s*,\s*[\d.]+%?\s*)?\)$/i.test(trimmed)) return true;
  if (trimmed === "transparent" || trimmed === "currentColor") return true;
  return false;
}

/**
 * Normalizes and sanitizes overlay tint colors for linear gradients.
 */
export function normalizeOverlayColor(val?: string | null): string {
  if (!val || val === "none" || val === "transparent") return "rgba(0,0,0,0)";
  const trimmed = val.trim();
  if (isSafeCssColor(trimmed)) {
    return trimmed;
  }
  return "rgba(0,0,0,0)";
}

/**
 * Sanitizes a string into a safe CSS identifier (letters, numbers, hyphens, underscores).
 */
function toSafeCssIdentifier(id: string): string {
  return id.replace(/[^a-zA-Z0-9_-]/g, "");
}

/**
 * Creates a safe URL string for use inside CSS url().
 * Validates the URL protocol via isSafeUrl(), then escapes only
 * CSS-significant characters (backslash, single-quote, closing paren, newlines).
 * Does NOT apply HTML entity encoding — CSS url() values inside <style>
 * tags are not HTML-parsed, so &amp; would be sent literally to the server.
 */
export function toSafeCssUrl(url: unknown): string {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (!isSafeUrl(trimmed)) return '';
  // Escape only characters that can break CSS url('...') syntax
  return trimmed
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/\)/g, '\\)')
    .replace(/\n/g, '\\a ')
    .replace(/\r/g, '\\d ')
    .replace(/[\x00]/g, '');
}

const DEFAULT_FALLBACK_PALETTE: ColorPalette = {
  id: "default",
  name: "Default",
  primary: "#1e3a8a",
  primaryForeground: "#ffffff",
  secondary: "#0f172a",
  secondaryForeground: "#ffffff",
  accent: "#d97706",
  accentForeground: "#ffffff",
  background: "#ffffff",
  foreground: "#0f172a",
  muted: "#f1f5f9",
  mutedForeground: "#64748b",
  border: "#e2e8f0",
  isDark: false,
};

/**
 * Merges custom brand hex overrides into a curated base ColorPalette.
 * Automatically computes derived contrast tokens (foreground, highlight, muted, border, elevated surfaces).
 */
export function mergeCustomColors(
  basePalette: ColorPalette | null | undefined,
  custom?: CustomColors | null
): ColorPalette {
  const palette = basePalette || DEFAULT_FALLBACK_PALETTE;
  if (!custom || Object.keys(custom).length === 0) return palette;

  const merged = { ...palette };

  if (custom.primary && isValidHexColor(custom.primary)) {
    merged.primary = custom.primary;
    merged.primaryForeground = getContrastColor(custom.primary);
    merged.highlight = adjustOpacity(custom.primary, 0.15);
  }

  if (custom.accent && isValidHexColor(custom.accent)) {
    merged.accent = custom.accent;
    merged.accentForeground = getContrastColor(custom.accent);
  }

  if (custom.background && isValidHexColor(custom.background)) {
    merged.background = custom.background;
    merged.isDark = isColorDark(custom.background);
    merged.surfaceElevated = merged.isDark
      ? "rgba(255, 255, 255, 0.08)"
      : "rgba(255, 255, 255, 0.7)";
    merged.surfaceTinted = merged.isDark
      ? "rgba(255, 255, 255, 0.03)"
      : "rgba(0, 0, 0, 0.02)";
    merged.muted = adjustBrightness(custom.background, merged.isDark ? 0.1 : -0.05);
    merged.border = adjustBrightness(custom.background, merged.isDark ? 0.15 : -0.12);

    // If custom.foreground is not explicitly specified, auto-heal foreground and text tokens to ensure WCAG AA contrast against new background
    if (!custom.foreground) {
      const autoFg = merged.isDark ? "#f8fafc" : "#0f172a";
      merged.foreground = autoFg;
      merged.textPrimary = autoFg;
      merged.textStrong = merged.isDark ? "#ffffff" : "#020617";
      merged.textMuted = merged.isDark ? "#94a3b8" : "#475569";
      merged.mutedForeground = merged.isDark ? "#94a3b8" : "#475569";
    }
  }

  if (custom.foreground && isValidHexColor(custom.foreground)) {
    // If the provided foreground fails minimum WCAG AA contrast (4.5:1) against the active background, auto-heal it
    const activeBg = merged.background;
    const ratio = getContrastRatio(custom.foreground, activeBg);
    const safeForeground = ratio >= 4.5 ? custom.foreground : getAccessibleForeground(activeBg);

    merged.foreground = safeForeground;
    merged.textPrimary = safeForeground;
    merged.textStrong = safeForeground;
    merged.mutedForeground = adjustOpacity(safeForeground, 0.6);
    merged.textMuted = adjustOpacity(safeForeground, 0.6);
  }

  return merged;
}

/**
 * Resolves a complete SemanticPalette from a given ColorPalette, providing deterministic
 * defaults for any missing additive semantic tokens or effects.
 */
export function resolveSemanticPalette(palette: ColorPalette): SemanticPalette {
  const isDark = palette.isDark ?? isColorDark(palette.background);
  const surface0 = palette.surface0 || palette.background;

  // Default surface scale based on isDark
  const surface1 =
    palette.surface1 ||
    palette.surfaceElevated ||
    (isDark ? "rgba(255, 255, 255, 0.06)" : "#ffffff");

  const surface2 =
    palette.surface2 ||
    (isDark ? "rgba(255, 255, 255, 0.10)" : "#f8fafc");

  const surfaceTinted =
    palette.surfaceTinted ||
    (isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)");

  let textPrimary = palette.textPrimary || palette.textStrong || palette.foreground;
  // Guarantee WCAG AA minimum 4.5:1 contrast against surface0 (canvas)
  if (getContrastRatio(textPrimary, surface0) < 4.5) {
    textPrimary = getAccessibleForeground(surface0);
  }

  let textSecondary = palette.textSecondary || palette.textMuted || palette.mutedForeground;
  // Guarantee WCAG AA minimum 3.0:1 contrast for secondary/muted text against surface0
  if (getContrastRatio(textSecondary, surface0) < 3.0) {
    textSecondary = isDark ? "#94a3b8" : "#475569";
  }

  const textTertiary = palette.textTertiary || adjustOpacity(textSecondary, 0.75);

  const borderSubtle = palette.borderSubtle || (isDark ? "rgba(255, 255, 255, 0.08)" : "#f1f5f9");
  const borderStrong = palette.borderStrong || palette.border;
  const focus = palette.focus || palette.primary;

  const defaultEffects: ThemeEffects = {
    level: isDark ? "subtle" : "subtle",
    glass: isDark,
    glow: isDark,
    hoverLift: true,
    ambientBackground: false,
  };

  const effects = palette.effects
    ? {
        level: palette.effects.level || "subtle",
        glass: palette.effects.glass ?? defaultEffects.glass,
        glow: palette.effects.glow ?? defaultEffects.glow,
        hoverLift: palette.effects.hoverLift ?? defaultEffects.hoverLift,
        ambientBackground: palette.effects.ambientBackground ?? defaultEffects.ambientBackground,
      }
    : defaultEffects;

  return {
    ...palette,
    isDark,
    surface0,
    surface1,
    surface2,
    surfaceTinted,
    textPrimary,
    textSecondary,
    textTertiary,
    borderSubtle,
    borderStrong,
    focus,
    effects,
    surfaceElevated: palette.surfaceElevated || surface1,
    highlight: palette.highlight || palette.primary,
    textStrong: palette.textStrong || textPrimary,
    textMuted: palette.textMuted || textSecondary,
  };
}

// ─── Theme CSS Generator ─────────────────────────────────────────────────────

export function generateSiteThemeCss(
  palette: ColorPalette,
  _fontPairing?: FontPairingConfig,
  customColors?: CustomColors | null,
  layoutOverrides?: LayoutOverrides | Record<string, unknown> | null
): string {
  const finalPalette = mergeCustomColors(palette, customColors);
  const semantic = resolveSemanticPalette(finalPalette);
  const rawMaxWidth = (layoutOverrides as LayoutOverrides)?.contentMaxWidth;
  const contentWidth =
    typeof rawMaxWidth === "number" && rawMaxWidth >= 600 && rawMaxWidth <= 1600
      ? rawMaxWidth
      : 960;

  const isDark = finalPalette.isDark ?? false;
  const surfaceElevated =
    finalPalette.surfaceElevated ||
    (isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(255, 255, 255, 0.7)");
  const surfaceTinted =
    finalPalette.surfaceTinted ||
    (isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)");
  const highlight = finalPalette.highlight || finalPalette.primary;
  const textStrong = finalPalette.textStrong || finalPalette.foreground;
  const textMuted = finalPalette.textMuted || finalPalette.mutedForeground;

  return `
    :root {
      --max-w-content: ${contentWidth}px;

      /* ── Semantic Scale (M1 Token Contract) ── */
      --site-surface-0: ${semantic.surface0};
      --site-surface-1: ${semantic.surface1};
      --site-surface-2: ${semantic.surface2};
      --site-surface-tinted: ${semantic.surfaceTinted};
      --site-text-primary: ${semantic.textPrimary};
      --site-text-secondary: ${semantic.textSecondary};
      --site-text-tertiary: ${semantic.textTertiary};
      --site-accent: ${semantic.accent || semantic.primary};
      --site-accent-fg: ${semantic.accentForeground || semantic.primaryForeground};
      --site-border-subtle: ${semantic.borderSubtle};
      --site-border-strong: ${semantic.borderStrong};
      --site-focus: ${semantic.focus};

      /* ── Legacy Aliases (Preserved for compatibility) ── */
      --site-primary: ${finalPalette.primary};
      --site-primary-fg: ${finalPalette.primaryForeground};
      --site-secondary: ${finalPalette.secondary};
      --site-secondary-fg: ${finalPalette.secondaryForeground};
      --site-bg: ${finalPalette.background};
      --site-text: ${finalPalette.foreground};
      --site-muted: ${finalPalette.muted};
      --site-muted-fg: ${finalPalette.mutedForeground};
      --site-border: ${finalPalette.border};
      --site-surface-elevated: ${surfaceElevated};
      --site-surface-tinted: ${surfaceTinted};
      --site-highlight: ${highlight};
      --site-text-strong: ${textStrong};
      --site-text-muted: ${textMuted};

      /* Motion Tokens */
      --site-motion-fast: 150ms;
      --site-motion-base: 250ms;
      --site-motion-slow: 350ms;
      --site-ease-soft: cubic-bezier(0.2, 0.8, 0.2, 1);
      --site-ease-spring: cubic-bezier(0.175, 0.885, 0.32, 1.275);
    }

    /* ── Accessibility: Skip Link & Focus Rings ── */
    .skip-to-content {
      position: absolute;
      top: -100px;
      left: 1.5rem;
      z-index: 9999;
      padding: 0.6rem 1.2rem;
      background: var(--site-accent, var(--site-primary));
      color: var(--site-accent-fg, var(--site-primary-fg, #ffffff));
      font-weight: 700;
      font-size: 0.9rem;
      border-radius: var(--radius, 0.375rem);
      text-decoration: none;
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);
      transition: top 0.2s ease;
    }
    .skip-to-content:focus, .skip-to-content:focus-visible {
      top: 1rem;
      outline: 3px solid var(--site-focus, var(--site-accent));
      outline-offset: 2px;
    }

    section[data-section-type], section.section-shell, section[id] {
      scroll-margin-top: 5.5rem;
    }

    /* ── Progressive Disclosure (<details> / <summary>) ── */
    details.disclosure-panel,
    details.pub-abstract-details,
    details.project-details-expand,
    details.course-desc-details {
      margin-top: 0.65rem;
      border-radius: var(--radius, 0.375rem);
      background: var(--site-surface-tinted, rgba(0, 0, 0, 0.02));
      border: 1px solid var(--site-border-subtle, var(--site-border, #e2e8f0));
      overflow: hidden;
      transition: border-color 0.15s ease;
    }

    details.disclosure-panel[open],
    details.pub-abstract-details[open],
    details.project-details-expand[open],
    details.course-desc-details[open] {
      border-color: var(--site-accent, var(--site-primary));
    }

    summary.disclosure-toggle,
    summary.pub-abstract-toggle,
    summary.project-details-toggle,
    summary.course-desc-toggle {
      padding: 0.4rem 0.75rem;
      font-size: 0.8rem;
      font-weight: 600;
      cursor: pointer;
      user-select: none;
      list-style: none;
      display: inline-flex;
      width: auto;
      align-items: center;
      gap: 0.4rem;
      color: var(--site-accent, var(--site-primary));
      background: var(--site-surface-tinted, rgba(0, 0, 0, 0.03));
      border-radius: var(--radius, 0.375rem);
      outline: none;
      transition: background 0.15s ease, color 0.15s ease;
    }

    summary.disclosure-toggle::-webkit-details-marker,
    summary.pub-abstract-toggle::-webkit-details-marker,
    summary.project-details-toggle::-webkit-details-marker,
    summary.course-desc-toggle::-webkit-details-marker {
      display: none;
    }

    summary.disclosure-toggle:hover,
    summary.pub-abstract-toggle:hover,
    summary.project-details-toggle:hover,
    summary.course-desc-toggle:hover {
      background: var(--site-surface-2, rgba(0, 0, 0, 0.06));
    }

    .disclosure-chevron,
    .chevron-icon,
    .toggle-icon {
      display: inline-block;
      transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
      line-height: 1;
      flex-shrink: 0;
    }

    details[open] > summary .disclosure-chevron,
    details[open] > summary .chevron-icon,
    details[open] > summary .toggle-icon {
      transform: rotate(180deg);
    }

    @keyframes accordionSlideDown {
      from {
        opacity: 0;
        transform: translateY(-5px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .disclosure-content,
    .pub-abstract-body,
    .project-details-body,
    .course-desc-body {
      padding: 0.85rem 1rem;
      border-top: 1px solid var(--site-border-subtle, var(--site-border, #e2e8f0));
      font-size: 0.9rem;
      line-height: 1.65;
      color: var(--site-text-primary, var(--site-text));
      background: var(--site-surface-1, var(--site-bg));
      animation: accordionSlideDown 0.2s cubic-bezier(0.16, 1, 0.3, 1);
    }

    /* ── Interactive Filter Bar ── */
    .filter-bar-container {
      display: flex;
      align-items: center;
      margin-bottom: 1.25rem;
      gap: 0.5rem;
    }

    .filter-tabs-desktop {
      display: flex;
      flex-wrap: wrap;
      gap: 0.4rem;
      align-items: center;
    }

    .filter-tab-btn {
      font-family: inherit;
      font-size: 0.8rem;
      font-weight: 600;
      padding: 0.3rem 0.8rem;
      border-radius: 9999px;
      border: 1px solid var(--site-border-subtle, var(--site-border));
      background: var(--site-surface-1, var(--site-bg));
      color: var(--site-text-secondary, var(--site-muted-fg));
      cursor: pointer;
      transition: all 0.15s ease;
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      outline: none;
    }

    .filter-tab-btn:hover {
      border-color: var(--site-accent, var(--site-primary));
      color: var(--site-text-primary, var(--site-text));
    }

    .filter-tab-btn.active {
      background: var(--site-accent, var(--site-primary));
      color: var(--site-accent-fg, var(--site-primary-fg, #ffffff));
      border-color: var(--site-accent, var(--site-primary));
      box-shadow: 0 2px 8px -1px rgba(0, 0, 0, 0.25);
    }

    .filter-count {
      font-size: 0.725rem;
      opacity: 0.85;
    }

    .filter-select-mobile {
      display: none;
      width: 100%;
    }

    .filter-dropdown {
      width: 100%;
      padding: 0.5rem 0.85rem;
      font-family: inherit;
      font-size: 0.875rem;
      font-weight: 600;
      border-radius: var(--radius, 0.375rem);
      border: 1px solid var(--site-border-subtle, var(--site-border));
      background: var(--site-surface-1, var(--site-bg));
      color: var(--site-text-primary, var(--site-text));
      outline: none;
    }

    @media (max-width: 639px) {
      .filter-tabs-desktop { display: none; }
      .filter-select-mobile { display: block; }
    }

    /* ── Accessible Interactive Buttons & Links ── */
    .pub-link-btn,
    .download-btn,
    .social-pill {
      font-family: inherit;
      font-size: 0.785rem;
      font-weight: 600;
      padding: 0.25rem 0.65rem;
      border-radius: var(--radius, 0.375rem);
      text-decoration: none;
      background-color: var(--site-surface-1, var(--site-bg));
      color: var(--site-accent, var(--site-primary));
      border: 1px solid var(--site-border-subtle, var(--site-border));
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      transition: all 0.15s ease-in-out;
      cursor: pointer;
    }

    .pub-link-btn:hover,
    .download-btn:hover,
    .social-pill:hover {
      background-color: var(--site-accent, var(--site-primary));
      color: var(--site-accent-fg, var(--site-primary-fg, #ffffff));
      border-color: var(--site-accent, var(--site-primary));
      transform: translateY(-1px);
    }

    .btn-action {
      font-family: inherit;
      font-size: 0.875rem;
      font-weight: 600;
      padding: 0.5rem 1.15rem;
      border-radius: var(--radius, 0.375rem);
      background: var(--site-accent, var(--site-primary));
      color: var(--site-accent-fg, var(--site-primary-fg, #ffffff));
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      gap: 0.45rem;
      transition: all 0.15s ease;
      border: 1px solid var(--site-accent, var(--site-primary));
    }

    .btn-action:hover {
      opacity: 0.94;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px -2px rgba(0, 0, 0, 0.25);
    }

    .btn-outline {
      font-family: inherit;
      font-size: 0.875rem;
      font-weight: 600;
      padding: 0.5rem 1.15rem;
      border-radius: var(--radius, 0.375rem);
      background: var(--site-surface-1, transparent);
      color: var(--site-text-primary, var(--site-text));
      border: 1px solid var(--site-border-subtle, var(--site-border));
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      gap: 0.45rem;
      transition: all 0.15s ease;
    }

    .btn-outline:hover {
      border-color: var(--site-accent, var(--site-primary));
      color: var(--site-accent, var(--site-primary));
      transform: translateY(-1px);
    }

    a:not(.btn):not(.btn-action):not(.btn-outline):not(.pub-link-btn):not(.download-btn):not(.social-pill):not(.nav-pill):not(.nav-brand):not(.filter-tab-btn) {
      color: var(--site-accent, var(--site-primary));
      text-decoration: none;
      transition: color 0.15s ease, text-decoration-color 0.15s ease;
    }

    a:not(.btn):not(.btn-action):not(.btn-outline):not(.pub-link-btn):not(.download-btn):not(.social-pill):not(.nav-pill):not(.nav-brand):not(.filter-tab-btn):hover {
      text-decoration: underline;
    }

    a:focus-visible, button:focus-visible, select:focus-visible {
      outline: 2px solid var(--site-focus, var(--site-accent));
      outline-offset: 2px;
    }

    /* ── Badges, Chips & Pills ── */
    .pub-citation-chip {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      font-size: 0.75rem;
      font-weight: 600;
      padding: 0.15rem 0.5rem;
      border-radius: var(--radius, 0.375rem);
      background: var(--site-surface-tinted, rgba(0, 0, 0, 0.04));
      color: var(--site-text-secondary, var(--site-muted-fg));
      border: 1px solid var(--site-border-subtle, var(--site-border, #e2e8f0));
    }

    .download-category-chip {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      font-size: 0.75rem;
      font-weight: 600;
      padding: 0.2rem 0.55rem;
      border-radius: var(--radius, 0.375rem);
      background: var(--site-surface-2, var(--site-muted));
      color: var(--site-accent, var(--site-primary));
      border: 1px solid var(--site-border-subtle, var(--site-border));
    }

    .meta-chip {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      font-size: 0.75rem;
      font-weight: 500;
      padding: 0.15rem 0.5rem;
      border-radius: var(--radius, 0.375rem);
      background: var(--site-surface-tinted, rgba(0, 0, 0, 0.03));
      color: var(--site-text-secondary, var(--site-muted-fg));
      border: 1px solid var(--site-border-subtle, var(--site-border));
    }

    /* ── Accessible Semantic Dialog Surface ── */
    body.profolio-dialog-open {
      overflow: hidden;
    }

    dialog.profolio-dialog {
      border: 1px solid var(--site-border-strong, var(--site-border));
      border-radius: var(--radius, 0.5rem);
      background: var(--site-surface-1, var(--site-bg));
      color: var(--site-text-primary, var(--site-text));
      padding: 0;
      max-width: 720px;
      width: calc(100% - 2rem);
      max-height: 85vh;
      box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.4), 0 0 0 1px var(--site-border-subtle, rgba(255,255,255,0.05));
      position: fixed;
      inset: 0;
      margin: auto;
      z-index: 9999;
    }

    dialog.profolio-dialog::backdrop {
      background: rgba(0, 0, 0, 0.65);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
    }

    .profolio-dialog-surface {
      display: flex;
      flex-direction: column;
      max-height: 85vh;
    }

    .profolio-dialog-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 1rem;
      padding: 1.5rem 1.75rem 1rem 1.75rem;
      border-bottom: 1px solid var(--site-border-subtle, var(--site-border));
    }

    .profolio-dialog-heading-wrap {
      flex: 1;
      min-width: 0;
    }

    .profolio-dialog-badge {
      display: inline-block;
      font-size: 0.725rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      padding: 0.15rem 0.5rem;
      border-radius: calc(var(--radius, 0.375rem) - 2px);
      background: var(--site-surface-tinted, rgba(0, 0, 0, 0.05));
      color: var(--site-accent, var(--site-primary));
      margin-bottom: 0.4rem;
    }

    .profolio-dialog-title {
      font-size: 1.25rem;
      font-weight: 700;
      line-height: 1.35;
      color: var(--site-text-primary, var(--site-text));
      margin: 0;
    }

    .profolio-dialog-subtitle {
      font-size: 0.875rem;
      color: var(--site-text-secondary, var(--site-muted-fg));
      margin-top: 0.25rem;
      line-height: 1.45;
    }

    .profolio-dialog-close-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 1px solid var(--site-border-subtle, transparent);
      background: var(--site-surface-2, transparent);
      color: var(--site-text-secondary, var(--site-muted-fg));
      cursor: pointer;
      padding: 0;
      flex-shrink: 0;
      transition: background 0.15s ease, color 0.15s ease;
    }

    .profolio-dialog-close-btn:hover {
      background: var(--site-surface-elevated, rgba(0, 0, 0, 0.1));
      color: var(--site-text-primary, var(--site-text));
    }

    .profolio-dialog-close-btn:focus-visible {
      outline: 2px solid var(--site-focus, var(--site-accent));
      outline-offset: 2px;
    }

    .profolio-dialog-body {
      padding: 1.5rem 1.75rem;
      overflow-y: auto;
      flex: 1;
      font-size: 0.95rem;
      line-height: 1.65;
      color: var(--site-text-primary, var(--site-text));
    }

    .profolio-dialog-footer {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      flex-wrap: wrap;
      padding: 1rem 1.75rem;
      border-top: 1px solid var(--site-border-subtle, var(--site-border));
      background: var(--site-surface-tinted, rgba(0, 0, 0, 0.02));
    }

    @media (max-width: 640px) {
      dialog.profolio-dialog {
        width: 100%;
        max-width: 100%;
        height: 100%;
        max-height: 100vh;
        border-radius: 0;
        border: none;
      }
      .profolio-dialog-surface {
        max-height: 100vh;
        height: 100%;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      dialog.profolio-dialog {
        transition: none;
      }
    }

    /* ── Filter Toolbar ── */
    .filter-bar-container {
      margin: 1rem 0 1.5rem 0;
      display: flex;
      align-items: center;
    }

    .filter-tabs-desktop {
      display: flex;
      align-items: center;
      gap: 0.35rem;
      flex-wrap: wrap;
    }

    .filter-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      height: 34px;
      padding: 0 0.85rem;
      font-size: 0.825rem;
      font-weight: 500;
      border-radius: var(--radius, 0.375rem);
      border: 1px solid var(--site-border-subtle, var(--site-border));
      background: var(--site-surface-1, var(--site-bg));
      color: var(--site-text-secondary, var(--site-muted-fg));
      cursor: pointer;
      transition: all 0.15s ease;
      box-sizing: border-box;
      line-height: 1;
    }

    .filter-btn:hover {
      background: var(--site-surface-2, rgba(0, 0, 0, 0.06));
      color: var(--site-text-primary, var(--site-text));
      border-color: var(--site-border-strong, var(--site-border));
    }

    .filter-btn.active, .filter-btn[aria-pressed="true"] {
      background: var(--site-accent, var(--site-primary));
      color: var(--site-accent-fg, var(--site-primary-fg, #ffffff));
      border-color: var(--site-accent, var(--site-primary));
      font-weight: 600;
    }

    .filter-btn:focus-visible {
      outline: 2px solid var(--site-focus, var(--site-accent));
      outline-offset: 2px;
    }

    /* ── Publications: Academic Clean List (No Box Outlines) ── */
    .publications-list {
      display: flex;
      flex-direction: column;
    }

    .pub-item {
      padding: 1.35rem 0;
      border-bottom: 1px solid var(--site-border-subtle, var(--site-border, #e2e8f0));
      background: transparent;
      position: relative;
    }

    .pub-item:first-child {
      padding-top: 0.25rem;
    }

    .pub-item:last-child {
      border-bottom: none;
    }

    .pub-title {
      font-weight: 700;
      font-size: 1.1rem;
      line-height: 1.4;
      margin-bottom: 0.35rem;
      color: var(--site-text-primary, var(--site-text));
    }

    .pub-title a {
      color: inherit;
      text-decoration: none;
      transition: color 0.15s ease;
    }

    .pub-title a:hover {
      color: var(--site-accent, var(--site-primary));
    }

    .pub-authors {
      font-size: 0.925rem;
      color: var(--site-text-secondary, var(--site-muted-fg));
      margin-bottom: 0.2rem;
      line-height: 1.5;
    }

    .pub-venue {
      font-size: 0.875rem;
      color: var(--site-text-secondary, var(--site-muted-fg));
      font-style: italic;
      margin-bottom: 0.5rem;
    }

    .pub-meta-line {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      flex-wrap: wrap;
      font-size: 0.8rem;
      color: var(--site-text-secondary, var(--site-muted-fg));
      margin-bottom: 0.65rem;
    }

    .pub-featured-mark {
      color: var(--site-accent, var(--site-primary));
      font-weight: 700;
    }

    .pub-type-mark {
      font-weight: 500;
      opacity: 0.9;
    }

    .pub-citations-mark {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      font-size: 0.775rem;
      opacity: 0.85;
    }

    .pub-action-links {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .pub-link-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      font-size: 0.8rem;
      font-weight: 600;
      padding: 0.35rem 0.7rem;
      border-radius: var(--radius, 0.375rem);
      background: var(--site-surface-tinted, rgba(0, 0, 0, 0.04));
      color: var(--site-accent, var(--site-primary));
      border: 1px solid var(--site-border-subtle, var(--site-border));
      text-decoration: none;
      cursor: pointer;
      transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
    }

    .pub-link-btn:hover {
      background: var(--site-surface-2, rgba(0, 0, 0, 0.08));
      border-color: var(--site-border-strong, var(--site-border));
      color: var(--site-text-primary, var(--site-text));
    }

    .pub-link-btn:focus-visible {
      outline: 2px solid var(--site-focus, var(--site-accent));
      outline-offset: 2px;
    }

    /* ── Research Interests: Ordered Topic Grid ── */
    .research-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 1.25rem;
      margin-top: 1.25rem;
    }

    .research-topic-card {
      background: var(--site-surface-1, var(--site-bg));
      border: 1px solid var(--site-border-subtle, var(--site-border));
      border-radius: var(--radius, 0.375rem);
      padding: 1.25rem 1.5rem;
      display: flex;
      flex-direction: column;
      box-shadow: 0 2px 8px -2px rgba(0, 0, 0, 0.04);
    }

    .research-topic-header {
      display: flex;
      align-items: baseline;
      gap: 0.65rem;
      margin-bottom: 0.5rem;
    }

    .research-topic-num {
      font-family: var(--font-mono, monospace);
      font-size: 0.85rem;
      font-weight: 700;
      color: var(--site-accent, var(--site-primary));
      opacity: 0.85;
    }

    .research-topic-title {
      font-size: 1.05rem;
      font-weight: 700;
      color: var(--site-text-primary, var(--site-text));
      margin: 0;
    }

    .research-topic-desc {
      font-size: 0.925rem;
      line-height: 1.6;
      color: var(--site-text-primary, var(--site-text));
      flex: 1;
    }

    .research-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 0.4rem;
      margin-top: 0.75rem;
    }

    .keyword-pill {
      font-size: 0.75rem;
      padding: 0.15rem 0.5rem;
      background: var(--site-surface-2, var(--site-muted));
      border-radius: var(--radius, 0.375rem);
      color: var(--site-accent, var(--site-primary));
      font-weight: 500;
    }

    /* ── Experience: Fixed Grid Timeline ── */
    .timeline {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 1.75rem;
      margin-top: 1rem;
    }

    .timeline-item {
      display: grid;
      grid-template-columns: 18px 1fr;
      gap: 1.25rem;
      position: relative;
    }

    .timeline-marker {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .timeline-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: var(--site-accent, var(--site-primary));
      box-shadow: 0 0 0 3px var(--site-surface-0, var(--site-bg));
      margin-top: 0.38rem;
      flex-shrink: 0;
      z-index: 2;
    }

    .timeline-line {
      position: absolute;
      top: 0.38rem;
      bottom: -1.75rem;
      width: 2px;
      background: var(--site-border-subtle, var(--site-border));
      z-index: 1;
    }

    .timeline-item:last-child .timeline-line {
      display: none;
    }

    .timeline-content {
      flex: 1;
      min-width: 0;
    }

    .timeline-header {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      flex-wrap: wrap;
    }

    .timeline-role {
      font-weight: 700;
      font-size: 1.05rem;
      color: var(--site-text-primary, var(--site-text));
    }

    .badge-status-current {
      display: inline-flex;
      align-items: center;
      font-size: 0.7rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      padding: 0.12rem 0.5rem;
      border-radius: 9999px;
      background: var(--site-surface-tinted, rgba(0, 0, 0, 0.06));
      color: var(--site-accent, var(--site-primary));
      border: 1px solid var(--site-accent, var(--site-primary));
      line-height: 1.2;
    }

    .timeline-org {
      font-size: 0.95rem;
      font-weight: 600;
      color: var(--site-accent, var(--site-primary));
      margin-top: 0.15rem;
    }

    .timeline-meta {
      font-size: 0.85rem;
      color: var(--site-text-secondary, var(--site-muted-fg));
      margin: 0.3rem 0 0.5rem 0;
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    .timeline-desc {
      font-size: 0.925rem;
      line-height: 1.6;
      color: var(--site-text-primary, var(--site-text));
    }

    /* ── Downloads: Compact Horizontal Resource Rows ── */
    .downloads-list {
      display: flex;
      flex-direction: column;
      gap: 0.65rem;
      margin-top: 1rem;
    }

    .resource-row {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 0.85rem 1.25rem;
      background: var(--site-surface-1, var(--site-bg));
      border: 1px solid var(--site-border-subtle, var(--site-border));
      border-radius: var(--radius, 0.375rem);
      transition: border-color 0.15s ease, background 0.15s ease;
    }

    .resource-row:hover {
      border-color: var(--site-border-strong, var(--site-border));
    }

    .resource-icon-badge {
      font-size: 0.7rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      padding: 0.3rem 0.55rem;
      border-radius: calc(var(--radius, 0.375rem) - 2px);
      background: var(--site-surface-2, var(--site-muted));
      color: var(--site-accent, var(--site-primary));
      flex-shrink: 0;
    }

    .resource-body {
      flex: 1;
      min-width: 0;
    }

    .resource-title {
      font-weight: 700;
      font-size: 0.975rem;
      color: var(--site-text-primary, var(--site-text));
      line-height: 1.35;
    }

    .resource-meta-subline {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      font-size: 0.8rem;
      color: var(--site-text-secondary, var(--site-muted-fg));
      margin-top: 0.15rem;
    }

    .resource-detail-btn {
      background: none;
      border: none;
      padding: 0;
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--site-accent, var(--site-primary));
      cursor: pointer;
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    .resource-detail-btn:hover {
      opacity: 0.8;
    }

    .resource-action {
      flex-shrink: 0;
    }

    @media (max-width: 640px) {
      .resource-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.75rem;
      }
      .resource-action {
        width: 100%;
      }
      .resource-action .download-btn {
        display: block;
        width: 100%;
        text-align: center;
      }
    }

    /* ── Skills & Methodologies: Hierarchy Redesign ── */
    .skills-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 1.25rem;
      margin-top: 1rem;
    }

    .skill-group-card {
      background: var(--site-surface-1, var(--site-bg));
      border: 1px solid var(--site-border-subtle, var(--site-border));
      border-radius: var(--radius, 0.375rem);
      padding: 1.25rem 1.5rem;
    }

    .skill-group-title {
      font-weight: 700;
      font-size: 1rem;
      margin-bottom: 0.75rem;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid var(--site-border-subtle, var(--site-border));
      color: var(--site-text-primary, var(--site-text));
    }

    .skills-list {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 0.65rem;
    }

    .skill-entry {
      background: var(--site-surface-tinted, rgba(0, 0, 0, 0.02));
      border: 1px solid var(--site-border-subtle, var(--site-border));
      border-radius: var(--radius, 0.375rem);
      padding: 0.5rem 0.75rem;
      display: flex;
      flex-direction: column;
      gap: 0.3rem;
    }

    .skill-entry-row {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 0.5rem;
    }

    .skill-name {
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--site-text-primary, var(--site-text));
    }

    .skill-level-label {
      font-size: 0.75rem;
      color: var(--site-text-secondary, var(--site-muted-fg));
      font-weight: 400;
    }

    .skill-dots {
      display: flex;
      gap: 4px;
      align-items: center;
    }

    .skill-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--site-border-strong, #cbd5e1);
      opacity: 0.5;
    }

    .skill-dot.filled {
      background: var(--site-accent, var(--site-primary));
      opacity: 1;
    }

    /* ── Teaching & Courses: Compact Uniform Grid ── */
    .courses-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 1.25rem;
      margin-top: 1rem;
    }

    .course-card {
      background: var(--site-surface-1, var(--site-bg));
      border: 1px solid var(--site-border-subtle, var(--site-border));
      border-radius: var(--radius, 0.375rem);
      padding: 1.25rem 1.5rem;
      box-shadow: 0 2px 8px -2px rgba(0, 0, 0, 0.04);
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }

    .course-card-body {
      flex: 1;
    }

    .course-header {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      flex-wrap: wrap;
      margin-bottom: 0.4rem;
    }

    .badge-course-code {
      font-family: var(--font-mono, monospace);
      font-size: 0.75rem;
      font-weight: 700;
      padding: 0.15rem 0.5rem;
      border-radius: calc(var(--radius, 0.375rem) - 2px);
      background: var(--site-surface-2, var(--site-muted));
      color: var(--site-accent, var(--site-primary));
    }

    .course-title {
      font-size: 1.05rem;
      font-weight: 700;
      color: var(--site-text-primary, var(--site-text));
      margin: 0;
      line-height: 1.35;
      flex: 1;
      min-width: 180px;
    }

    .course-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 0.4rem;
      margin: 0.5rem 0;
    }

    .course-summary-clamped {
      font-size: 0.875rem;
      line-height: 1.55;
      color: var(--site-text-secondary, var(--site-muted-fg));
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      margin-top: 0.4rem;
    }

    .course-links {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
      margin-top: 1rem;
      padding-top: 0.75rem;
      border-top: 1px solid var(--site-border-subtle, var(--site-border));
    }

    /* ── Projects & Grants: Clean Preview Frames ── */
    .projects-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
      gap: 1.25rem;
      margin-top: 1rem;
    }

    .project-card {
      background: var(--site-surface-1, var(--site-bg));
      border: 1px solid var(--site-border-subtle, var(--site-border));
      border-radius: var(--radius, 0.375rem);
      padding: 1.25rem 1.5rem;
      box-shadow: 0 2px 8px -2px rgba(0, 0, 0, 0.04);
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }

    .project-card-body {
      flex: 1;
    }

    .project-header-row {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 0.6rem;
      margin-bottom: 0.35rem;
    }

    .project-title {
      font-size: 1.1rem;
      font-weight: 700;
      color: var(--site-text-primary, var(--site-text));
      margin: 0;
      line-height: 1.35;
      flex: 1;
    }

    .badge-status-active {
      font-size: 0.7rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      padding: 0.12rem 0.5rem;
      border-radius: 9999px;
      background: var(--site-surface-tinted, rgba(0, 0, 0, 0.06));
      color: var(--site-accent, var(--site-primary));
      border: 1px solid var(--site-accent, var(--site-primary));
      flex-shrink: 0;
    }

    .project-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 0.4rem;
      margin: 0.4rem 0;
    }

    .project-teaser-clamped {
      font-size: 0.9rem;
      line-height: 1.55;
      color: var(--site-text-secondary, var(--site-muted-fg));
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      margin-top: 0.4rem;
    }

    .project-thumb-frame {
      margin-top: 0.75rem;
      border-radius: calc(var(--radius, 0.375rem) - 2px);
      overflow: hidden;
      aspect-ratio: 16 / 9;
      background: var(--site-surface-0, var(--site-bg));
      border: 1px solid var(--site-border-subtle, var(--site-border));
    }

    .project-thumb-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .project-dialog-figure {
      margin: 0 0 1rem 0;
      border-radius: var(--radius, 0.375rem);
      overflow: hidden;
      background: var(--site-surface-0, var(--site-bg));
      border: 1px solid var(--site-border-subtle, var(--site-border));
    }

    .project-dialog-img {
      width: 100%;
      max-height: 420px;
      object-fit: contain;
      display: block;
      background: rgba(0, 0, 0, 0.05);
    }

    .project-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      flex-wrap: wrap;
      margin-top: 1rem;
      padding-top: 0.75rem;
      border-top: 1px solid var(--site-border-subtle, var(--site-border));
    }

    .project-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .project-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 0.35rem;
    }

    /* ── Contact Matrix: Robust Wrapping ── */
    .contact-matrix-grid,
    .contact-channels-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
      gap: 1rem;
      margin-top: 1rem;
    }

    .contact-card,
    .contact-channel-card {
      display: flex;
      align-items: flex-start;
      gap: 0.85rem;
      padding: 1rem 1.25rem;
      background: var(--site-surface-1, var(--site-bg));
      border: 1px solid var(--site-border-subtle, var(--site-border));
      border-radius: var(--radius, 0.375rem);
      box-shadow: 0 2px 8px -2px rgba(0, 0, 0, 0.04);
    }

    .contact-card-icon,
    .contact-channel-icon {
      font-size: 1.1rem;
      color: var(--site-accent, var(--site-primary));
      margin-top: 0.15rem;
      flex-shrink: 0;
    }

    .contact-card-body,
    .contact-channel-body {
      flex: 1;
      min-width: 0;
    }

    .contact-card-label,
    .contact-channel-label {
      font-size: 0.75rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--site-text-secondary, var(--site-muted-fg));
      display: block;
      margin-bottom: 0.15rem;
    }

    .contact-card-val,
    .contact-channel-val {
      font-size: 0.925rem;
      font-weight: 500;
      color: var(--site-text-primary, var(--site-text));
      overflow-wrap: break-word;
      word-break: break-word;
    }

    .contact-card-val a,
    .contact-channel-val a {
      color: inherit;
      text-decoration: none;
      transition: color 0.15s ease;
    }

    .contact-card-val a:hover,
    .contact-channel-val a:hover {
      color: var(--site-accent, var(--site-primary));
    }

    /* Section spacing */
    section[data-section-type] {
      margin-bottom: 4.5rem;
    }

    section[data-section-type]:last-child {
      margin-bottom: 2rem;
    }

    /* ── Highlights Grid (About Me) ── */
    .highlights-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
      gap: 0.85rem;
      margin-top: 1.5rem;
    }

    .highlight-stat-card {
      background: var(--site-surface-1, var(--site-bg));
      border: 1px solid var(--site-border-subtle, var(--site-border));
      border-radius: var(--radius, 0.375rem);
      padding: 0.85rem 1.1rem;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      box-shadow: 0 2px 6px -1px rgba(0, 0, 0, 0.04);
    }

    .highlight-label {
      font-size: 0.725rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--site-text-secondary, var(--site-muted-fg));
      display: flex;
      align-items: center;
      gap: 0.35rem;
    }

    .highlight-value {
      font-size: 1.15rem;
      font-weight: 700;
      color: var(--site-accent, var(--site-primary));
    }

    /* ── Research Vision Callout ── */
    .research-vision-statement {
      padding: 1.15rem 1.35rem;
      margin-bottom: 1.5rem;
      border-radius: var(--radius, 0.375rem);
      background: var(--site-surface-tinted, rgba(0, 0, 0, 0.02));
      border-left: 3px solid var(--site-accent, var(--site-primary));
      border-top: 1px solid var(--site-border-subtle, var(--site-border));
      border-right: 1px solid var(--site-border-subtle, var(--site-border));
      border-bottom: 1px solid var(--site-border-subtle, var(--site-border));
      font-size: 0.985rem;
      line-height: 1.65;
      color: var(--site-text-primary, var(--site-text));
    }

    /* ── Display Options (Presentation & Emphasis) ── */

    /* Emphasis */
    .emphasis-subtle {
      background: var(--site-surface-2);
      border: 1px solid var(--site-border-subtle);
      padding: 2rem;
      border-radius: var(--radius);
    }

    .emphasis-strong {
      background: var(--site-accent);
      color: var(--site-accent-fg);
      padding: 2.5rem;
      border-radius: var(--radius);
    }
    .emphasis-strong .section-title { color: var(--site-accent-fg); }
    .emphasis-strong a:not(.btn-action):not(.pub-link-btn) { color: var(--site-accent-fg); text-decoration: underline; }
    .emphasis-strong .pub-item, .emphasis-strong .experience-item, .emphasis-strong .project-card, .emphasis-strong .skill-pill { border-color: rgba(255,255,255,0.2); }

    .emphasis-none { padding: 0; background: transparent; border: none; }

    /* Presentation: Publications */
    .presentation-compact_bibliography .pub-item {
      margin-bottom: 0.75rem;
      padding-left: 2rem;
      text-indent: -2rem;
      font-size: 0.9em;
    }
    .presentation-cards .publications-list .pub-group {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 1.5rem;
    }
    .presentation-cards .publications-list > .pub-item {
      display: inline-block;
      width: calc(50% - 1.5rem);
      margin: 0.75rem;
    }
    .presentation-cards .pub-item {
      border: 1px solid var(--site-border-subtle);
      padding: 1.25rem;
      border-radius: var(--radius);
      background: var(--site-surface-1);
    }

    /* Presentation: Experience */
    .presentation-timeline .experience-list {
      border-left: 2px solid var(--site-border-subtle);
      padding-left: 1.5rem;
      margin-left: 0.75rem;
    }
    .presentation-timeline .experience-item {
      position: relative;
      margin-bottom: 2.5rem;
    }
    .presentation-timeline .experience-item::before {
      content: "";
      position: absolute;
      left: -1.9rem;
      top: 0.3rem;
      width: 0.75rem;
      height: 0.75rem;
      background: var(--site-accent);
      border-radius: 50%;
    }
    .presentation-cards .experience-list {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 1.5rem;
    }
    .presentation-cards .experience-item {
      border: 1px solid var(--site-border-subtle);
      padding: 1.25rem;
      border-radius: var(--radius);
      background: var(--site-surface-1);
    }
    .presentation-minimal .experience-item {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin-bottom: 1.5rem;
      padding-bottom: 1.5rem;
      border-bottom: 1px solid var(--site-border-subtle);
    }
    @media (min-width: 640px) {
      .presentation-minimal .experience-item {
        flex-direction: row;
        gap: 2rem;
      }
      .presentation-minimal .experience-item > div:first-child { min-width: 180px; }
    }

    /* Presentation: Projects */
    .projects-featured-layout {
      display: flex;
      flex-direction: column;
      gap: 2rem;
    }
    .projects-lead .lead-project-card {
      border: 2px solid var(--site-accent);
      transform: scale(1.02);
    }
    .projects-supporting-list {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 1.25rem;
    }
    .projects-list {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
    }
    .projects-list .project-card {
      display: flex;
      flex-direction: column;
    }
    @media (min-width: 768px) {
      .projects-list .project-card {
        flex-direction: row;
        gap: 1.5rem;
      }
      .projects-list .project-thumb-frame {
        width: 300px;
        flex-shrink: 0;
      }
    }

    /* Presentation: Skills */
    .skills-section.presentation-tags .skills-list {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }
    .skills-section.presentation-tags .skill-tag {
      padding: 0.25rem 0.75rem;
      background: var(--site-surface-2);
      border: 1px solid var(--site-border-subtle);
      border-radius: 9999px;
      font-size: 0.85rem;
      font-weight: 500;
      display: inline-block;
    }
    .skills-section.presentation-progress .skill-progress-entry {
      margin-bottom: 1rem;
    }
    .skills-section.presentation-progress .skill-progress-header {
      display: flex;
      justify-content: space-between;
      font-size: 0.85rem;
      margin-bottom: 0.35rem;
      font-weight: 600;
    }
    .skills-section.presentation-progress .skill-progress-bar-bg {
      height: 6px;
      background: var(--site-surface-2);
      border-radius: 9999px;
      overflow: hidden;
    }
    .skills-section.presentation-progress .skill-progress-bar-fill {
      height: 100%;
      background: var(--site-accent);
      border-radius: 9999px;
    }

    ${generatePresentationCss()}
  `;
}

// ─── Dynamic Canvas Background CSS ──────────────────────────────────────────

export function generateBackgroundCss(style: string | null | undefined, _palette: ColorPalette): string {
  const bgStyle = style || "solid";

  switch (bgStyle) {
    case "gradient-linear":
      return `
        body {
          background: linear-gradient(180deg, var(--site-bg) 0%, var(--site-muted) 100%) !important;
          background-attachment: fixed !important;
        }
      `;
    case "gradient-radial":
      return `
        body {
          background: radial-gradient(ellipse at 50% 15%, var(--site-muted) 0%, var(--site-bg) 75%) !important;
          background-attachment: fixed !important;
        }
      `;
    case "mesh":
      return `
        body {
          background-color: var(--site-bg) !important;
          background-image:
            radial-gradient(at 10% 10%, var(--site-muted) 0px, transparent 50%),
            radial-gradient(at 90% 10%, var(--site-border) 0px, transparent 40%),
            radial-gradient(at 50% 90%, var(--site-muted) 0px, transparent 50%) !important;
          background-attachment: fixed !important;
        }
      `;
    case "animated-gradient":
      return `
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        body {
          background: linear-gradient(-45deg, var(--site-bg), var(--site-muted), var(--site-bg), var(--site-border)) !important;
          background-size: 400% 400% !important;
          animation: gradientShift 18s ease infinite !important;
        }
      `;
    case "geometric-pattern":
      return `
        body {
          background-color: var(--site-bg) !important;
          background-image: radial-gradient(var(--site-border) 1.25px, transparent 1.25px) !important;
          background-size: 28px 28px !important;
        }
      `;
    case "particles":
      return `
        body {
          background-color: var(--site-bg) !important;
          background-image:
            radial-gradient(var(--site-accent) 0.85px, transparent 0.85px),
            radial-gradient(var(--site-primary) 0.65px, transparent 0.65px) !important;
          background-size: 48px 48px, 96px 96px !important;
          background-position: 0 0, 24px 24px !important;
        }
      `;
    case "solid":
    default:
      return `
        body {
          background-color: var(--site-bg);
        }
      `;
  }
}

// ─── 60fps Micro-Motion & Reduced-Motion Engine ──────────────────────────────

export function generateAnimationCss(level: string | null | undefined): string {
  const animLevel = level || "subtle";

  const reducedMotionFallback = `
    @media (prefers-reduced-motion: reduce) {
      .profolio-site-root,
      .profolio-site-root *,
      .profolio-site-root *::before,
      .profolio-site-root *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
      }
    }
  `;

  if (animLevel === "none") {
    return `
      .profolio-site-root,
      .profolio-site-root * {
        transition: none !important;
        animation: none !important;
      }
      ${reducedMotionFallback}
    `;
  }

  if (animLevel === "expressive" || animLevel === "moderate") {
    return `
      @keyframes dramaticEntrance {
        0% {
          opacity: 0;
          transform: translate3d(0, 16px, 0) scale(0.99);
        }
        100% {
          opacity: 1;
          transform: translate3d(0, 0, 0) scale(1);
        }
      }
      .profolio-site-root .profolio-hero,
      .profolio-site-root .hero {
        animation: dramaticEntrance 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
      }
      .profolio-site-root .card,
      .profolio-site-root .project-card,
      .profolio-site-root .pub-item,
      .profolio-site-root .course-card,
      .profolio-site-root .skill-group-card,
      .profolio-site-root .download-card {
        transition: transform 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275), border-color 0.2s ease, box-shadow 0.25s ease;
      }
      .profolio-site-root .card:hover,
      .profolio-site-root .project-card:hover,
      .profolio-site-root .download-card:hover {
        transform: translate3d(0, -4px, 0);
        box-shadow: 0 16px 32px -8px rgba(0, 0, 0, 0.12);
        border-color: var(--site-primary);
      }
      ${reducedMotionFallback}
    `;
  }

  // Default: Subtle (150ms micro-transitions)
  return `
    @keyframes fadeIn {
      from { opacity: 0; transform: translate3d(0, 8px, 0); }
      to { opacity: 1; transform: translate3d(0, 0, 0); }
    }
    .profolio-site-root .profolio-hero,
    .profolio-site-root .hero {
      animation: fadeIn 0.35s ease-out both;
    }
    .profolio-site-root .card,
    .profolio-site-root .project-card,
    .profolio-site-root .pub-item,
    .profolio-site-root .course-card,
    .profolio-site-root .skill-group-card,
    .profolio-site-root .download-card {
      transition: transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
    }
    .profolio-site-root .card:hover,
    .profolio-site-root .project-card:hover,
    .profolio-site-root .download-card:hover {
      transform: translate3d(0, -2px, 0);
      box-shadow: 0 8px 20px -4px rgba(0, 0, 0, 0.08);
    }
    ${reducedMotionFallback}
  `;
}

// ─── Sticky Top Navigation Bar ──────────────────────────────────────────────

export function generateStickyNavbar(
  profile: ProfileData,
  sections: SectionData[],
  palette?: ColorPalette
): string {
  return renderSiteNavigation(profile, sections, palette);
}

export function generateNavbarStyles(palette: ColorPalette): string {
  const isDark = palette.isDark ?? false;
  const navBg = isDark ? "rgba(9, 13, 22, 0.88)" : "rgba(255, 255, 255, 0.88)";
  const navBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(226, 232, 240, 0.8)";
  const dropdownBg = isDark ? "#111726" : "#ffffff";
  const dropdownBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(203, 213, 225, 0.9)";

  return `
    .sticky-nav {
      position: sticky;
      top: 0;
      z-index: 1000;
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      background-color: ${navBg};
      border-bottom: 1px solid ${navBorder};
      width: 100%;
      transition: all 0.2s ease;
    }

    .nav-container {
      max-width: var(--max-w-content, 960px);
      margin: 0 auto;
      padding: 0.65rem 1.5rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1.5rem;
    }

    .nav-brand {
      font-weight: 700;
      font-size: 1rem;
      color: var(--site-accent, var(--site-primary));
      text-decoration: none;
      white-space: nowrap;
      letter-spacing: -0.01em;
    }

    .nav-links,
    .nav-links-desktop {
      display: flex;
      align-items: center;
      gap: 0.4rem;
    }

    .nav-link,
    .nav-pill {
      font-size: 0.825rem;
      font-weight: 600;
      color: var(--site-text-secondary, var(--site-muted-fg));
      padding: 0.35rem 0.75rem;
      border-radius: 9999px;
      text-decoration: none;
      transition: all 0.15s ease;
      background: transparent;
      border: 1px solid transparent;
      white-space: nowrap;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
    }

    .nav-link:hover,
    .nav-pill:hover {
      color: var(--site-text-primary, var(--site-text));
      background: var(--site-surface-tinted, rgba(0, 0, 0, 0.04));
      border-color: var(--site-border-subtle, transparent);
    }

    .nav-link.active,
    .nav-pill.active {
      color: var(--site-accent, var(--site-primary));
      background: var(--site-surface-1, var(--site-bg));
      border-color: var(--site-border-subtle, var(--site-border));
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    }

    .nav-dropdown-wrap {
      position: relative;
    }

    .dropdown-chevron {
      transition: transform 0.2s ease;
    }

    .nav-dropdown-wrap:hover .dropdown-chevron,
    .nav-dropdown-wrap:focus-within .dropdown-chevron {
      transform: rotate(180deg);
    }

    .nav-dropdown-menu {
      position: absolute;
      top: calc(100% + 0.35rem);
      right: 0;
      min-width: 160px;
      background: ${dropdownBg};
      border: 1px solid ${dropdownBorder};
      border-radius: var(--radius, 0.375rem);
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3);
      padding: 0.4rem;
      display: none;
      flex-direction: column;
      gap: 0.2rem;
      z-index: 1001;
    }

    .nav-dropdown-wrap:hover .nav-dropdown-menu,
    .nav-dropdown-wrap:focus-within .nav-dropdown-menu {
      display: flex;
    }

    .dropdown-link {
      font-size: 0.825rem;
      font-weight: 500;
      padding: 0.4rem 0.75rem;
      border-radius: calc(var(--radius, 0.375rem) - 2px);
      color: var(--site-text-primary, var(--site-text));
      text-decoration: none;
      transition: background 0.15s ease;
    }

    .dropdown-link:hover {
      background: var(--site-surface-2, var(--site-muted));
      color: var(--site-accent, var(--site-primary));
    }

    .mobile-menu-toggle {
      display: none;
      background: transparent;
      border: 1px solid var(--site-border-subtle, var(--site-border));
      border-radius: 6px;
      padding: 0.4rem 0.5rem;
      cursor: pointer;
      flex-direction: column;
      gap: 4px;
    }

    .hamburger-bar {
      display: block;
      width: 18px;
      height: 2px;
      background: var(--site-text-primary, var(--site-text));
      border-radius: 1px;
    }

    .mobile-nav-drawer {
      display: none;
      padding: 0.75rem 1.5rem 1.25rem 1.5rem;
      border-top: 1px solid ${navBorder};
      background: ${dropdownBg};
    }

    .mobile-nav-links {
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
    }

    .mobile-nav-link {
      font-size: 0.9rem;
      font-weight: 600;
      padding: 0.5rem 0.75rem;
      border-radius: 6px;
      color: var(--site-text-primary, var(--site-text));
      text-decoration: none;
    }

    .mobile-nav-link:hover, .mobile-nav-link.active {
      background: var(--site-surface-2, var(--site-muted));
      color: var(--site-accent, var(--site-primary));
    }

    @media (max-width: 767px) {
      .nav-links,
      .nav-links-desktop { display: none; }
      .mobile-menu-toggle { display: flex; }
      .mobile-nav-drawer.open { display: block; }
    }
  `;
}

// ─── Hero CSS Generator ──────────────────────────────────────────────────────

export function generateHeroCss(hero?: HeroOverrides): string {
  let css = `
    .profolio-site-root .profolio-hero {
      margin-bottom: 2.5rem;
      position: relative;
      z-index: 1;
    }
    .profolio-site-root .profolio-hero.layout-card {
      margin-bottom: 3rem !important;
    }
  `;
  if (!hero) return css;

  // 0. Avatar Shape Geometry (High Specificity to override recipe defaults)
  if (hero.avatarShape) {
    const radiusMap: Record<string, string> = {
      circle: "50%",
      squircle: "28%",
      rounded: "16px",
      square: "0px",
      hexagon: "25%",
      none: "0px",
    };
    const rad = radiusMap[hero.avatarShape] || "50%";
    css += `
      .profolio-site-root .avatar,
      .profolio-site-root .avatar-placeholder,
      .profolio-site-root .avatar-container,
      .profolio-site-root .avatar-wrapper .avatar,
      .profolio-site-root .avatar-wrapper .avatar-placeholder,
      .profolio-site-root .avatar-wrapper img {
        border-radius: ${rad} !important;
      }
    `;
  }

  // 1. Avatar Border & Elevation
  const borderSizes: Record<string, string> = { thin: "2px", medium: "4px", thick: "6px", double: "3px double" };
  const borderColor = hero.avatarBorderColor && isValidHexColor(hero.avatarBorderColor)
    ? hero.avatarBorderColor
    : "var(--site-accent, var(--site-primary))";

  const allAvatarTargets = `
    .profolio-site-root .avatar,
    .profolio-site-root .avatar-placeholder,
    .profolio-site-root .avatar-container,
    .profolio-site-root .avatar-wrapper .avatar,
    .profolio-site-root .avatar-wrapper .avatar-placeholder,
    .profolio-site-root .avatar-wrapper img
  `;

  if (hero.avatarBorder === "none") {
    css += `
      ${allAvatarTargets} {
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
        padding: 0 !important;
        background: none !important;
      }
    `;
  } else if (hero.avatarBorder) {
    if (hero.avatarBorder === "accent") {
      css += `${allAvatarTargets} { border: 4px solid ${borderColor} !important; outline: none !important; } `;
    } else if (hero.avatarBorder === "gradient") {
      const gradientPresets: Record<string, string> = {
        sunset: "linear-gradient(135deg, #f97316, #ec4899, #8b5cf6)",
        aurora: "linear-gradient(135deg, #06b6d4, #10b981, #6366f1)",
        cyber: "linear-gradient(135deg, #06b6d4, #3b82f6, #a855f7)",
        gold: "linear-gradient(135deg, #d97706, #f59e0b, #fde68a)",
        "primary-accent": "linear-gradient(135deg, var(--site-primary), var(--site-accent))",
      };
      const grad = gradientPresets[hero.avatarBorderGradient || ""] || "linear-gradient(135deg, var(--site-primary), var(--site-accent))";
      css += `${allAvatarTargets} { padding: 3px !important; background: ${grad} !important; border: none !important; outline: none !important; } `;
    } else if (hero.avatarBorder === "dashed") {
      css += `${allAvatarTargets} { border: 3px dashed ${borderColor} !important; outline: none !important; } `;
    } else if (hero.avatarBorder === "double") {
      css += `${allAvatarTargets} { border: 3px double ${borderColor} !important; } `;
    } else if (borderSizes[hero.avatarBorder]) {
      css += `${allAvatarTargets} { border: ${borderSizes[hero.avatarBorder]} solid ${borderColor} !important; outline: none !important; } `;
    }
  }

  // 2. Avatar Shadows
  if (hero.avatarShadow) {
    if (hero.avatarShadow === "none") {
      css += `${allAvatarTargets} { box-shadow: none !important; } `;
    } else if (hero.avatarShadow === "subtle") {
      css += `${allAvatarTargets} { box-shadow: 0 4px 14px -2px rgba(0, 0, 0, 0.08) !important; } `;
    } else if (hero.avatarShadow === "elevated") {
      css += `${allAvatarTargets} { box-shadow: 0 12px 28px -4px rgba(0, 0, 0, 0.18) !important; } `;
    } else if (hero.avatarShadow === "3d") {
      css += `${allAvatarTargets} { box-shadow: 0 2px 4px rgba(0,0,0,0.06), 0 6px 14px rgba(0,0,0,0.08), 0 16px 28px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.15) !important; } `;
    } else if (hero.avatarShadow === "glow") {
      css += `${allAvatarTargets} { box-shadow: 0 0 24px -2px var(--site-accent, var(--site-primary)) !important; } `;
    }
  }

  // 3. Avatar Animations & 3D Tilt
  css += `
    @keyframes profolioAvatarFloat {
      0%, 100% { transform: translate3d(0, 0, 0); }
      50% { transform: translate3d(0, -6px, 0); }
    }
    @keyframes profolioAvatarPulseGlow {
      0%, 100% { box-shadow: 0 0 10px 0 var(--site-accent, var(--site-primary)); }
      50% { box-shadow: 0 0 26px 4px var(--site-accent, var(--site-primary)); }
    }
    @keyframes profolioAvatarSpinRing {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    .profolio-site-root .avatar-anim-float {
      animation: profolioAvatarFloat 3.5s ease-in-out infinite;
    }
    .profolio-site-root .avatar-anim-pulse-glow {
      animation: profolioAvatarPulseGlow 2.5s ease-in-out infinite;
    }
    .profolio-site-root .avatar-anim-spin-ring {
      animation: profolioAvatarSpinRing 12s linear infinite;
    }
    .profolio-site-root .avatar-3d-tilt {
      transform-style: preserve-3d;
      perspective: 800px;
      transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    }
    .profolio-site-root .avatar-3d-tilt:hover {
      transform: perspective(800px) rotateX(6deg) rotateY(-6deg) translateZ(12px) scale3d(1.03, 1.03, 1.03) !important;
    }
    @media (prefers-reduced-motion: reduce) {
      .profolio-site-root .avatar-anim-float,
      .profolio-site-root .avatar-anim-pulse-glow,
      .profolio-site-root .avatar-anim-spin-ring,
      .profolio-site-root .avatar-3d-tilt {
        animation: none !important;
        transform: none !important;
        transition: none !important;
      }
    }
  `;

  // 4. Avatar 3D Convex Bevel Lens Overlay
  if (hero.avatarBevel && hero.avatarBevel !== "none") {
    css += `
      .profolio-site-root .avatar-container {
        position: relative;
        display: inline-block;
        line-height: 0;
      }
      .profolio-site-root .avatar-bevel-lens {
        position: absolute;
        inset: 0;
        border-radius: inherit;
        pointer-events: none;
        z-index: 2;
      }
    `;
  }

  // Hero Background (Banner)
  if (hero.heroLayout === "banner" && hero.heroBackgroundImageUrl) {
    const safeUrl = toSafeCssUrl(hero.heroBackgroundImageUrl);
    if (safeUrl) {
      const rawOverlay = hero.heroBackgroundOverlay;
      const overlay = (!rawOverlay || rawOverlay === "none" || rawOverlay === "transparent")
        ? "rgba(0,0,0,0)"
        : rawOverlay;
      const validPositions = ["cover", "contain", "top", "center", "bottom", "fixed"];
      const position = validPositions.includes(hero.heroBackgroundPosition || "")
        ? hero.heroBackgroundPosition
        : "center";

      css += `
        .profolio-site-root .profolio-hero.layout-banner {
          background-image: linear-gradient(${overlay}, ${overlay}), url('${safeUrl}') !important;
          background-size: cover !important;
          background-position: center !important;
          background-repeat: no-repeat !important;
          ${position === "fixed" ? "background-attachment: fixed !important;" : `background-position: ${position} !important;`}
          color: #ffffff !important;
        }
        ${position === "fixed" ? `
        .profolio-site-root .profolio-hero.layout-banner,
        .profolio-site-root .profolio-hero.layout-banner:hover {
          background-attachment: fixed !important;
          transform: none !important;
          transition: transform 0s !important;
          will-change: auto !important;
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
          isolation: isolate;
        }
        ` : ""}
        .profolio-site-root .profolio-hero.layout-banner .hero-info h1,
        .profolio-site-root .profolio-hero.layout-banner .hero-info .title,
        .profolio-site-root .profolio-hero.layout-banner .hero-info .bio {
          color: #ffffff !important;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.85), 0 1px 3px rgba(0, 0, 0, 0.95);
        }
        .profolio-site-root .profolio-hero.layout-banner .hero-info {
          background: linear-gradient(180deg, rgba(0, 0, 0, 0.35) 0%, rgba(0, 0, 0, 0.55) 100%);
          border-radius: var(--radius-card, 12px);
          padding: 1.5rem;
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
        }
        .profolio-site-root .profolio-hero.layout-banner .social-link-pill,
        .profolio-site-root .profolio-hero.layout-banner .social-pill,
        .profolio-site-root .profolio-hero.layout-banner .btn-primary {
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
        }
      `;
    }
  }
  return css;
}

// ─── 2.5D Viewport Edge Bevel CSS Generator ──────────────────────────────────

function buildEdgeGlassGradient(
  direction: "to right" | "to left",
  color: string,
  tier: "light" | "medium" | "high"
): string {
  // Peak intensities at the extreme viewport edge (0%)
  const maxAlphaMap: Record<string, Record<"light" | "medium" | "high", number>> = {
    white: { light: 0.22, medium: 0.32, high: 0.44 },
    black: { light: 0.38, medium: 0.52, high: 0.68 },
    cyan: { light: 0.38, medium: 0.52, high: 0.68 },
    gold: { light: 0.36, medium: 0.50, high: 0.65 },
    purple: { light: 0.38, medium: 0.52, high: 0.68 },
  };

  const rgbMap: Record<string, string> = {
    white: "255, 255, 255",
    black: "0, 0, 0",
    cyan: "6, 182, 212",
    gold: "245, 158, 11",
    purple: "168, 85, 247",
  };

  // Smooth monotonic curve stops (ease-out curve):
  // Starts at full color intensity (0%), gently slopes down, feathers to near-zero at 86%, and reaches pure 0% at 100%.
  // Zero white lines, zero dark lip, zero abrupt cutoff.
  const curveStops = [
    { pos: 0, mult: 1.0 },
    { pos: 10, mult: 0.8 },
    { pos: 22, mult: 0.58 },
    { pos: 36, mult: 0.38 },
    { pos: 52, mult: 0.2 },
    { pos: 70, mult: 0.08 },
    { pos: 86, mult: 0.02 },
    { pos: 100, mult: 0.0 },
  ];

  if (color === "accent" || color.startsWith("#")) {
    const colorExpr = color === "accent"
      ? "var(--site-accent, var(--site-primary, #3b82f6))"
      : color;
    const maxPercent = tier === "high" ? 68 : tier === "medium" ? 52 : 38;
    const stopsStr = curveStops
      .map(({ pos, mult }) => {
        const pct = Math.round(maxPercent * mult);
        if (pct === 0) return `transparent ${pos}%`;
        return `color-mix(in srgb, ${colorExpr} ${pct}%, transparent) ${pos}%`;
      })
      .join(", ");
    return `linear-gradient(${direction}, ${stopsStr})`;
  }

  const rgb = rgbMap[color] || rgbMap.white;
  const maxAlpha = (maxAlphaMap[color] || maxAlphaMap.white)[tier];

  const stopsStr = curveStops
    .map(({ pos, mult }) => {
      const alpha = mult === 0 ? 0 : Number((maxAlpha * mult).toFixed(3));
      if (alpha === 0) return `rgba(${rgb}, 0) ${pos}%`;
      return `rgba(${rgb}, ${alpha}) ${pos}%`;
    })
    .join(", ");

  return `linear-gradient(${direction}, ${stopsStr})`;
}

export function generatePageBevelCss(
  pageBevel?: string | null,
  pageBevelColor?: string | null
): string {
  if (!pageBevel || pageBevel === "none") return "";

  const color = (pageBevelColor || "white").toLowerCase();

  // Three refined, non-intrusive intensity tiers:
  // "light" / "subtle-rim" -> Subtle (12px)
  // "medium" / "curved-glass" -> Balanced (18px)
  // "high" / "mobile-bezel" -> Pronounced (26px)
  let width = "12px";
  let tier: "light" | "medium" | "high" = "light";

  if (pageBevel === "high" || pageBevel === "mobile-bezel") {
    width = "26px";
    tier = "high";
  } else if (pageBevel === "medium" || pageBevel === "curved-glass") {
    width = "18px";
    tier = "medium";
  } else {
    width = "12px";
    tier = "light";
  }

  const leftLight = buildEdgeGlassGradient("to right", color, tier);
  const rightDark = buildEdgeGlassGradient("to left", color, tier);

  return `
    /* 2.5D Viewport Edge Bevel (Monotonic Smooth Fade from Full Intensity to Zero) */
    .profolio-site-root::before,
    .profolio-site-root::after {
      content: '';
      position: fixed;
      top: 0;
      bottom: 0;
      height: 100vh;
      height: 100dvh;
      min-height: 100%;
      width: ${width};
      pointer-events: none;
      z-index: 99999;
      -webkit-transform: translate3d(0, 0, 0);
      transform: translate3d(0, 0, 0);
      will-change: transform;
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;
    }
    .profolio-site-root::before {
      left: 0;
      background: ${leftLight};
    }
    .profolio-site-root::after {
      right: 0;
      background: ${rightDark};
    }
  `;
}

/**
 * Resolves element-level overrides for a section with strict priority:
 * 1. sectionOverridesById[sectionId] (ID-specific override)
 * 2. sections[sectionId] (legacy section ID key in sections object)
 * 3. sections[sectionType] (legacy section-type key)
 */
export function resolveSectionOverride(
  sectionId?: string | null,
  sectionType?: string | null,
  layoutOverrides?: LayoutOverrides | Record<string, SectionElementOverrides> | Record<string, unknown> | null
): SectionElementOverrides | undefined {
  if (!layoutOverrides) return undefined;
  const lo = layoutOverrides as LayoutOverrides;
  const rawDict = layoutOverrides as Record<string, SectionElementOverrides>;

  if (sectionId) {
    if (lo.sectionOverridesById?.[sectionId]) {
      return lo.sectionOverridesById[sectionId];
    }
    if (lo.sections?.[sectionId]) {
      return lo.sections[sectionId];
    }
    if (rawDict[sectionId]) {
      return rawDict[sectionId];
    }
  }
  if (sectionType) {
    if (lo.sections?.[sectionType]) {
      return lo.sections[sectionType];
    }
    if (rawDict[sectionType]) {
      return rawDict[sectionType];
    }
  }
  return undefined;
}

// ─── Universal Section Header & Element CSS Generator ────────────────────────

export function generateSectionElementCss(
  sections?: SectionData[],
  layoutOverrides?: LayoutOverrides | Record<string, unknown> | null
): string {
  let css = `
    /* Universal Section Header Decorations */
    .profolio-site-root .section-header {
      margin-bottom: 1.75rem;
      position: relative;
    }
    .profolio-site-root .section-header.style-underlined h2 {
      position: relative;
      padding-bottom: 0.5rem;
      border-bottom: 2px solid var(--site-border);
    }
    .profolio-site-root .section-header.style-underlined h2::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 60px;
      height: 2px;
      background: var(--site-primary);
    }
    .profolio-site-root .section-header.style-accent-left {
      border-left: 4px solid var(--site-primary);
      padding-left: 1rem;
      margin-left: 0;
    }
    .profolio-site-root .section-header.style-accent-left h2 {
      margin: 0;
      line-height: 1.25;
    }
    .profolio-site-root .section-header.style-pill h2 {
      display: inline-block;
      padding: 0.25rem 0.85rem;
      background: var(--site-muted);
      color: var(--site-text-strong);
      border-radius: 9999px;
      font-size: 0.95rem;
      font-weight: 700;
      border: 1px solid var(--site-border);
    }
    .profolio-site-root .section-header.style-hidden {
      display: none !important;
    }
    .profolio-site-root .section-header.style-centered {
      text-align: center !important;
    }
    .profolio-site-root .section-header.style-centered h2 {
      margin-left: auto !important;
      margin-right: auto !important;
      display: inline-block;
    }
    .profolio-site-root .section-header.style-centered.style-underlined h2::after,
    .profolio-site-root .section-header.style-underlined[style*="center"] h2::after {
      left: 50% !important;
      transform: translateX(-50%) !important;
    }

    @keyframes profolioSectionFloat {
      0%, 100% { transform: translate3d(0, 0, 0); }
      50% { transform: translate3d(0, -4px, 0); }
    }
    @keyframes profolioSectionPulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.88; }
    }
    /* ── Section Motion Content & Keyframe Animations ── */
    .profolio-site-root .section-motion-content {
      width: 100%;
    }
    .profolio-site-root .section-anim-ambient-float,
    .profolio-site-root .section-motion-content.section-anim-ambient-float {
      animation: profolioSectionFloat 6s ease-in-out infinite;
    }
    .profolio-site-root .section-anim-subtle-pulse,
    .profolio-site-root .section-motion-content.section-anim-subtle-pulse {
      animation: profolioSectionPulse 4s ease-in-out infinite;
    }

    /* ── Scroll Reveal Effects ── */
    .profolio-site-root .section-scroll-fade-in,
    .profolio-site-root .section-motion-content.section-scroll-fade-in {
      opacity: 0;
      transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1);
      will-change: opacity;
    }
    .profolio-site-root .section-scroll-fade-in.is-visible,
    .profolio-site-root .section-motion-content.section-scroll-fade-in.is-visible {
      opacity: 1;
    }

    .profolio-site-root .section-scroll-slide-up,
    .profolio-site-root .section-motion-content.section-scroll-slide-up {
      opacity: 0;
      transform: translate3d(0, 36px, 0);
      transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
      will-change: opacity, transform;
    }
    .profolio-site-root .section-scroll-slide-up.is-visible,
    .profolio-site-root .section-motion-content.section-scroll-slide-up.is-visible {
      opacity: 1;
      transform: none;
    }

    @media (prefers-reduced-motion: reduce) {
      .profolio-site-root .section-anim-ambient-float,
      .profolio-site-root .section-anim-subtle-pulse,
      .profolio-site-root .section-motion-content.section-anim-ambient-float,
      .profolio-site-root .section-motion-content.section-anim-subtle-pulse {
        animation: none !important;
        transform: none !important;
      }
      .profolio-site-root .section-scroll-fade-in,
      .profolio-site-root .section-scroll-slide-up,
      .profolio-site-root .section-motion-content.section-scroll-fade-in,
      .profolio-site-root .section-motion-content.section-scroll-slide-up {
        opacity: 1 !important;
        transform: none !important;
        transition: none !important;
      }
    }

    /* ── Accessibility & Focus Utilities ── */
    .profolio-site-root .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border-width: 0;
    }
    .profolio-site-root .profolio-carousel:focus-visible {
      outline: 2px solid var(--site-primary);
      outline-offset: 2px;
    }

    /* ── Item Grid Columns ── */
    .profolio-site-root .section-items-grid {
      display: grid;
      width: 100%;
      gap: 1.25rem;
    }
    .profolio-site-root .section-items-grid.cols-1 {
      grid-template-columns: 1fr !important;
    }
    .profolio-site-root .section-items-grid.cols-2 {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
    .profolio-site-root .section-items-grid.cols-3 {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    }
    .profolio-site-root .section-items-grid.cols-auto-fit {
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)) !important;
    }
    @media (max-width: 768px) {
      .profolio-site-root .section-items-grid {
        grid-template-columns: 1fr !important;
      }
    }

    /* ── Split Layout with Companion Media ── */
    .profolio-site-root .section-split-wrapper {
      display: flex;
      gap: 2rem;
      align-items: flex-start;
      width: 100%;
    }
    .profolio-site-root .section-split-wrapper.split-pos-right {
      flex-direction: row;
    }
    .profolio-site-root .section-split-wrapper.split-pos-left {
      flex-direction: row-reverse;
    }
    .profolio-site-root .section-split-wrapper.ratio-50-50 .section-content-pane,
    .profolio-site-root .section-split-wrapper.ratio-50-50 .section-media-pane {
      flex: 1 1 50%;
      min-width: 0;
    }
    .profolio-site-root .section-split-wrapper.ratio-60-40 .section-content-pane {
      flex: 1 1 60%;
      min-width: 0;
    }
    .profolio-site-root .section-split-wrapper.ratio-60-40 .section-media-pane {
      flex: 1 1 40%;
      min-width: 0;
    }
    .profolio-site-root .section-split-wrapper.ratio-70-30 .section-content-pane {
      flex: 1 1 70%;
      min-width: 0;
    }
    .profolio-site-root .section-split-wrapper.ratio-70-30 .section-media-pane {
      flex: 1 1 30%;
      min-width: 0;
    }
    .profolio-site-root .section-split-wrapper.ratio-40-60 .section-content-pane {
      flex: 1 1 40%;
      min-width: 0;
    }
    .profolio-site-root .section-split-wrapper.ratio-40-60 .section-media-pane {
      flex: 1 1 60%;
      min-width: 0;
    }
    .profolio-site-root .section-split-wrapper.ratio-30-70 .section-content-pane {
      flex: 1 1 30%;
      min-width: 0;
    }
    .profolio-site-root .section-split-wrapper.ratio-30-70 .section-media-pane {
      flex: 1 1 70%;
      min-width: 0;
    }
    @media (max-width: 640px) {
      .profolio-site-root .section-split-wrapper {
        flex-direction: column !important;
        gap: 1.5rem !important;
      }
      .profolio-site-root .section-split-wrapper .section-content-pane,
      .profolio-site-root .section-split-wrapper .section-media-pane {
        flex: 1 1 100% !important;
        width: 100% !important;
      }
    }

    /* ── Companion Media & Rich Embeds ── */
    .profolio-site-root .section-companion-media {
      width: 100%;
      position: relative;
    }
    .profolio-site-root .companion-figure {
      width: 100%;
      margin: 0;
      overflow: hidden;
    }
    .profolio-site-root .companion-figure img {
      width: 100%;
      height: auto;
      display: block;
      object-fit: cover;
    }
    .profolio-site-root .companion-figure figcaption {
      padding: 0.5rem 0;
      font-size: 0.8rem;
      color: var(--site-text-secondary, var(--site-muted-fg));
      text-align: center;
    }
    .profolio-site-root .companion-gallery-stacked {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      width: 100%;
    }
    .profolio-site-root .companion-gallery-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 0.75rem;
      width: 100%;
    }
    .profolio-site-root .companion-gallery-row {
      display: flex;
      gap: 0.75rem;
      overflow-x: auto;
      width: 100%;
    }
    .profolio-site-root .companion-video-container {
      position: relative;
      width: 100%;
      padding-bottom: 56.25%;
      height: 0;
      overflow: hidden;
      border-radius: var(--radius, 8px);
      background: #000;
    }
    .profolio-site-root .companion-video-container iframe,
    .profolio-site-root .companion-video-container video {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: none;
    }
    .profolio-site-root .companion-video-container.aspect-4-3 {
      padding-bottom: 75%;
    }
    .profolio-site-root .companion-video-container.aspect-1-1 {
      padding-bottom: 100%;
    }
    .profolio-site-root .companion-video-container.aspect-3-4 {
      padding-bottom: 133.33%;
    }
    .profolio-site-root .section-subtitle {
      margin-top: 0.25rem !important;
      margin-bottom: 1.25rem !important;
      line-height: 1.65 !important;
      font-size: 1rem !important;
      max-width: 100% !important;
    }
    .profolio-site-root .profolio-carousel {
      position: relative;
      width: 100%;
      overflow: hidden;
      border-radius: var(--radius, 8px);
    }
    .profolio-site-root .profolio-carousel-track {
      display: flex;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scroll-behavior: smooth;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }
    .profolio-site-root .profolio-carousel-track::-webkit-scrollbar {
      display: none;
    }
    .profolio-site-root .profolio-carousel-slide {
      flex: 0 0 100%;
      scroll-snap-align: start;
      position: relative;
    }
    .profolio-site-root .profolio-carousel-slide img {
      width: 100%;
      display: block;
      object-fit: cover;
    }
    .profolio-site-root .profolio-carousel-caption {
      padding: 0.5rem 0.75rem;
      font-size: 0.8rem;
      background: rgba(0, 0, 0, 0.65);
      color: #ffffff;
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      backdrop-filter: blur(4px);
    }
    .profolio-site-root .profolio-carousel-btn {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 10;
      width: 34px;
      height: 34px;
      border-radius: 50%;
      background: rgba(15, 23, 42, 0.65);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      border: 1px solid rgba(255, 255, 255, 0.25);
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-size: 18px;
      line-height: 1;
      padding: 0;
      transition: all 0.2s ease;
      user-select: none;
    }
    .profolio-site-root .profolio-carousel-btn:hover {
      background: rgba(15, 23, 42, 0.85);
      transform: translateY(-50%) scale(1.08);
    }
    .profolio-site-root .profolio-carousel-btn.prev {
      left: 10px;
    }
    .profolio-site-root .profolio-carousel-btn.next {
      right: 10px;
    }
    .profolio-site-root .profolio-carousel-dots {
      position: absolute;
      bottom: 10px;
      left: 0;
      right: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 6px;
      z-index: 10;
      pointer-events: none;
    }
    .profolio-site-root .profolio-carousel-dot {
      pointer-events: auto;
      width: 8px;
      height: 8px;
      border-radius: 9999px;
      background: rgba(255, 255, 255, 0.45);
      border: 1px solid rgba(0, 0, 0, 0.2);
      padding: 0;
      cursor: pointer;
      transition: all 0.25s ease;
    }
    .profolio-site-root .profolio-carousel-dot.active {
      width: 22px;
      background: #ffffff;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    }
  `;

  if (!sections || !layoutOverrides) return css;

  for (const section of sections) {
    if (!section.visible) continue;

    // ID-first resolution with type-based fallback for robust addressing
    const secOverrides = resolveSectionOverride(
      section.id,
      section.type,
      layoutOverrides as LayoutOverrides
    );

    if (!secOverrides) continue;

    const safeId = toSafeCssIdentifier(section.id);
    const safeType = toSafeCssIdentifier(section.type);

    const isIdSpecific = Boolean(
      (layoutOverrides as LayoutOverrides)?.sectionOverridesById?.[section.id] ||
      (layoutOverrides as LayoutOverrides)?.sections?.[section.id]
    );

    const baseSelectors = [
      `.profolio-site-root #section-${safeId}`,
      `#section-${safeId}`,
    ];

    if (!isIdSpecific) {
      baseSelectors.push(
        `.profolio-site-root #${safeType}`,
        `.profolio-site-root [data-section-type="${safeType}"]`
      );
    }
    const selector = baseSelectors.join(", ");

    const commonCardClasses = [
      ".card",
      ".pub-card",
      ".pub-item",
      ".research-card",
      ".timeline-item",
      ".course-card",
      ".project-card",
      ".bento-cell",
      ".bento-card",
      ".highlight-card",
      ".download-card",
      ".download-item",
      ".skill-group",
      ".skill-card",
      ".item-block",
    ];

    const buildDescendants = (classes: string[]) =>
      baseSelectors.flatMap((s) => classes.map((c) => `${s} ${c}`)).join(", ");

    // Card Container Detection (Full border, custom surface, or background graphic)
    const hasCardContainer =
      (secOverrides.sectionBorder && secOverrides.sectionBorder === "full") ||
      (secOverrides.sectionBackground && secOverrides.sectionBackground !== "transparent") ||
      Boolean(secOverrides.sectionBackgroundImageUrl);

    if (hasCardContainer) {
      css += `${selector} { overflow: hidden !important; border-radius: var(--radius, 12px) !important; } `;
      if (!secOverrides.sectionPadding || secOverrides.sectionPadding === "none") {
        css += `${selector} { padding: 2.5rem 2rem !important; } `;
        css += `@media (max-width: 640px) { ${selector} { padding: 1.5rem 1.25rem !important; } } `;
      }
    }

    // Border Dividers
    if (secOverrides.sectionBorder && secOverrides.sectionBorder !== "none") {
      const color =
        secOverrides.sectionBorderColor && isValidHexColor(secOverrides.sectionBorderColor)
          ? secOverrides.sectionBorderColor
          : "var(--site-border)";

      if (secOverrides.sectionBorder === "full") {
        css += `${selector} { border: 1px solid ${color} !important; border-radius: var(--radius, 12px); } `;
      } else if (secOverrides.sectionBorder === "top") {
        css += `${selector} { border-top: 1px solid ${color} !important; } `;
      } else if (secOverrides.sectionBorder === "bottom") {
        css += `${selector} { border-bottom: 1px solid ${color} !important; } `;
      } else if (secOverrides.sectionBorder === "both") {
        css += `${selector} { border-top: 1px solid ${color} !important; border-bottom: 1px solid ${color} !important; } `;
      }
    }

    // Background Surface Treatment
    if (secOverrides.sectionBackground && secOverrides.sectionBackground !== "transparent") {
      if (secOverrides.sectionBackground === "elevated") {
        css += `${selector} { background: var(--site-surface-elevated) !important; box-shadow: 0 4px 12px -2px rgba(0, 0, 0, 0.08); border-radius: var(--radius, 12px); } `;
      } else if (secOverrides.sectionBackground === "tinted") {
        css += `${selector} { background: var(--site-muted) !important; border-radius: var(--radius, 12px); } `;
      } else if (secOverrides.sectionBackground === "glass") {
        css += `${selector} { background: var(--site-surface-elevated) !important; backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); border: 1px solid var(--site-border) !important; border-radius: var(--radius, 12px); } `;
      } else if (
        secOverrides.sectionBackground === "custom" &&
        secOverrides.sectionBackgroundColor &&
        isValidHexColor(secOverrides.sectionBackgroundColor)
      ) {
        const secIsDark = isColorDark(secOverrides.sectionBackgroundColor);
        const secText = secIsDark ? "#f8fafc" : "#0f172a";
        const secMuted = secIsDark ? "#cbd5e1" : "#475569";
        const secBorder = secIsDark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.1)";
        css += `
          ${selector} {
            background: ${secOverrides.sectionBackgroundColor} !important;
            border-radius: var(--radius, 12px);
            --site-text-primary: ${secText};
            --site-text-secondary: ${secMuted};
            --site-border-subtle: ${secBorder};
            color: ${secText} !important;
          }
          ${selector} .section-header h2,
          ${selector} .pub-title,
          ${selector} .item-title,
          ${selector} .experience-role,
          ${selector} .teaching-title,
          ${selector} .project-title {
            color: ${secText} !important;
          }
          ${selector} .item-meta,
          ${selector} .pub-authors,
          ${selector} .pub-venue,
          ${selector} p {
            color: ${secMuted} !important;
          }
        `;
      }
    }

    // Section Background Image & Effects
    if (secOverrides.sectionBackgroundImageUrl) {
      const safeUrl = toSafeCssUrl(secOverrides.sectionBackgroundImageUrl);
      if (safeUrl) {
        const overlay = normalizeOverlayColor(secOverrides.sectionBackgroundOverlay);
        const legacyPos = secOverrides.sectionBackgroundPosition;

        // Background Size / Fit (never emit size as position)
        let bgSize = "cover";
        if (secOverrides.sectionBackgroundFit) {
          bgSize = secOverrides.sectionBackgroundFit === "repeat" ? "auto" : secOverrides.sectionBackgroundFit;
        } else if (legacyPos === "contain") {
          bgSize = "contain";
        } else if (legacyPos === "cover") {
          bgSize = "cover";
        }

        // Background Position (focal point: 9-position anchor matrix)
        let bgPosition = "center";
        const focalMap: Record<string, string> = {
          "top-left": "0% 0%",
          "top-center": "50% 0%",
          top: "top",
          "top-right": "100% 0%",
          "center-left": "0% 50%",
          left: "0% 50%",
          center: "center",
          "center-right": "100% 50%",
          right: "100% 50%",
          "bottom-left": "0% 100%",
          "bottom-center": "50% 100%",
          bottom: "bottom",
          "bottom-right": "100% 100%",
        };
        if (legacyPos && focalMap[legacyPos]) {
          bgPosition = focalMap[legacyPos];
        }

        // Background Attachment & Parallax
        const isFixed =
          secOverrides.sectionBackgroundAttachment === "fixed" ||
          legacyPos === "fixed" ||
          secOverrides.sectionScrollEffect === "parallax";
        const bgAttachment = isFixed ? "fixed" : "scroll";

        // Background Repeat (supports watermark/textures)
        let bgRepeat = "no-repeat";
        if (secOverrides.sectionBackgroundRepeat) {
          bgRepeat = secOverrides.sectionBackgroundRepeat;
        } else if (secOverrides.sectionBackgroundFit === "repeat") {
          bgRepeat = "repeat";
        }

        css += `
          ${selector},
          ${baseSelectors.map((s) => `${s}:hover`).join(", ")} {
            background-image: linear-gradient(${overlay}, ${overlay}), url('${safeUrl}') !important;
            background-size: ${bgSize} !important;
            background-position: ${bgPosition} !important;
            background-repeat: ${bgRepeat} !important;
            background-attachment: ${bgAttachment} !important;
            background-color: transparent !important;
            border-radius: var(--radius, 12px);
            position: relative;
            ${isFixed ? "-webkit-backface-visibility: hidden; backface-visibility: hidden; isolation: isolate;" : ""}
          }
          ${selector} .section-header h2,
          ${selector} .pub-title,
          ${selector} .item-title,
          ${selector} .experience-role,
          ${selector} .teaching-title,
          ${selector} .project-title {
            text-shadow: 0 2px 8px rgba(0, 0, 0, 0.85);
          }
        `;

        if (isFixed) {
          // When background-attachment is fixed, CSS Transforms Level 1 mandates that ANY non-none transform
          // on the element or containing block demotes background-attachment: fixed to scroll relative to the
          // element, which causes the background image to suddenly rescale to the element box and scroll with
          // the page when hovered. We strictly neutralize transform on the section (including :hover, .bento-card, etc.)
          // and its inner cards so that fixed parallax remains rock-solid during mouse movement and hover interactions.
          const fixedSuppressionTargets = [
            ...baseSelectors,
            ...baseSelectors.map((s) => `${s}:hover`),
            ...baseSelectors.map((s) => `${s}.bento-card`),
            ...baseSelectors.map((s) => `${s}.bento-card:hover`),
            ...baseSelectors.map((s) => `${s}.section-card`),
            ...baseSelectors.map((s) => `${s}.section-card:hover`),
            ...baseSelectors.map((s) => `${s}.card`),
            ...baseSelectors.map((s) => `${s}.card:hover`),
            ...baseSelectors.map((s) => `${s} > .section-motion-content`),
            ...baseSelectors.map((s) => `${s} > .section-motion-content:hover`),
            ...baseSelectors.flatMap((s) => [
              `${s} .card:hover`,
              `${s} .bento-card:hover`,
              `${s} .bento-cell:hover`,
              `${s} .project-card:hover`,
              `${s} .download-card:hover`,
              `${s} .pub-item:hover`,
              `${s} .pub-card:hover`,
              `${s} .research-card:hover`,
              `${s} .course-card:hover`,
              `${s} .timeline-item:hover`,
              `${s} .item-block:hover`,
            ]),
          ].join(", ");

          css += `
            ${fixedSuppressionTargets} {
              transform: none !important;
              transition: transform 0s !important;
              will-change: auto !important;
            }
          `;
        }

        // Make inner containers translucent so the section background image shows through.
        // Properly distribute each base selector so inner card targets don't match the section itself.
        const innerCardSelectors = buildDescendants([
          ".card",
          ".pub-card",
          ".pub-item",
          ".research-card",
          ".project-card",
          ".timeline-item",
          ".course-card",
          ".highlight-card",
        ]);

        css += `
          ${innerCardSelectors} {
            background-color: color-mix(in srgb, var(--card-bg, var(--site-surface-1, #ffffff)) 85%, transparent) !important;
            backdrop-filter: blur(8px) !important;
            -webkit-backdrop-filter: blur(8px) !important;
          }
        `;

        // For bento themes where the <section> IS the card (.bento-card),
        // the shorthand 'background: var(--surface-elevated)' would reset background-image.
        // Override background-color to transparent on bento sections so our background-image shows.
        const bentoCardSelectors = baseSelectors
          .flatMap((s) => [`${s}.bento-card`, `${s}.section-card`])
          .join(", ");

        css += `
          ${bentoCardSelectors} {
            background-color: transparent !important;
          }
        `;
      }
    }

    // Section Idle & Scroll Animations (applied to motion wrapper)
    const hasScrollReveal =
      secOverrides.sectionScrollEffect === "fade-in" || secOverrides.sectionScrollEffect === "slide-up";
    const hasFixedBackground =
      Boolean(secOverrides.sectionBackgroundImageUrl) &&
      (secOverrides.sectionBackgroundAttachment === "fixed" ||
        secOverrides.sectionBackgroundPosition === "fixed" ||
        secOverrides.sectionScrollEffect === "parallax");
    const motionSelector = [
      ...baseSelectors.map((s) => `${s} > .section-motion-content`),
      ...baseSelectors,
    ].join(", ");
    if (secOverrides.sectionIdleAnimation === "subtle-pulse" && !hasScrollReveal) {
      css += `${motionSelector}.section-anim-subtle-pulse { animation: profolioSectionPulse 4s ease-in-out infinite; } `;
    } else if (
      secOverrides.sectionIdleAnimation === "ambient-float" &&
      !hasScrollReveal &&
      !hasFixedBackground
    ) {
      css += `${motionSelector}.section-anim-ambient-float { animation: profolioSectionFloat 6s ease-in-out infinite; } `;
    }

    // Card treatment applies to the section surface (if no custom background image) and common inner cards.
    const cardTargets = [
      ...(secOverrides.sectionBackgroundImageUrl ? [] : baseSelectors),
      ...baseSelectors.flatMap((s) => commonCardClasses.map((c) => `${s} ${c}`)),
    ].join(", ");

    if (secOverrides.cardStyle === "flat") {
      css += `${cardTargets} { background: transparent !important; box-shadow: none !important; } `;
    } else if (secOverrides.cardStyle === "elevated") {
      css += `${cardTargets} { background: var(--site-surface-1) !important; box-shadow: 0 10px 24px -14px rgba(15, 23, 42, 0.38) !important; } `;
    } else if (secOverrides.cardStyle === "bordered") {
      css += `${cardTargets} { background: var(--site-surface-1) !important; border: 1px solid var(--site-border-subtle, var(--site-border)) !important; box-shadow: none !important; } `;
    } else if (secOverrides.cardStyle === "glass") {
      css += `${cardTargets} { background: color-mix(in srgb, var(--site-surface-1) 82%, transparent) !important; border: 1px solid var(--site-border-subtle, var(--site-border)) !important; backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); } `;
    }

    // Inner Spacing Padding
    if (secOverrides.sectionPadding && secOverrides.sectionPadding !== "none") {
      const paddingMap: Record<string, string> = {
        sm: "1.5rem 1.25rem",
        md: "2.5rem 2rem",
        lg: "4rem 2.5rem",
        xl: "6rem 3rem",
      };
      const p = paddingMap[secOverrides.sectionPadding];
      if (p) {
        css += `${selector} { padding: ${p} !important; } `;
        css += `@media (max-width: 640px) { ${selector} { padding: 1.25rem 1rem !important; } } `;
      }
    }

    // Content Alignment
    if (secOverrides.contentAlign === "left" || secOverrides.contentAlign === "center") {
      css += `${selector} { text-align: ${secOverrides.contentAlign} !important; } `;
    }

    // ── Per-Section Color Overrides (Dual-Layer Specificity) ──
    const colorVars: string[] = [];
    if (secOverrides.sectionTextColor && isValidHexColor(secOverrides.sectionTextColor)) {
      colorVars.push(`--site-text: ${secOverrides.sectionTextColor} !important;`);
      colorVars.push(`--site-text-primary: ${secOverrides.sectionTextColor} !important;`);
      colorVars.push(`--scholar-text: ${secOverrides.sectionTextColor} !important;`);
    }
    if (secOverrides.sectionHeadingColor && isValidHexColor(secOverrides.sectionHeadingColor)) {
      colorVars.push(`--site-text-strong: ${secOverrides.sectionHeadingColor} !important;`);
      colorVars.push(`--site-heading: ${secOverrides.sectionHeadingColor} !important;`);
      colorVars.push(`--site-secondary: ${secOverrides.sectionHeadingColor} !important;`);
      colorVars.push(`--bento-text: ${secOverrides.sectionHeadingColor} !important;`);
    }
    if (secOverrides.sectionMutedColor && isValidHexColor(secOverrides.sectionMutedColor)) {
      colorVars.push(`--site-text-secondary: ${secOverrides.sectionMutedColor} !important;`);
      colorVars.push(`--site-muted-fg: ${secOverrides.sectionMutedColor} !important;`);
      colorVars.push(`--site-text-muted: ${secOverrides.sectionMutedColor} !important;`);
      colorVars.push(`--scholar-muted: ${secOverrides.sectionMutedColor} !important;`);
    }
    if (secOverrides.sectionAccentColor && isValidHexColor(secOverrides.sectionAccentColor)) {
      const accentFg = getAccessibleForeground(secOverrides.sectionAccentColor);
      colorVars.push(`--site-primary: ${secOverrides.sectionAccentColor} !important;`);
      colorVars.push(`--site-accent: ${secOverrides.sectionAccentColor} !important;`);
      colorVars.push(`--bento-accent: ${secOverrides.sectionAccentColor} !important;`);
      colorVars.push(`--scholar-accent: ${secOverrides.sectionAccentColor} !important;`);
      colorVars.push(`--site-primary-fg: ${accentFg} !important;`);
      colorVars.push(`--site-accent-fg: ${accentFg} !important;`);
    }
    if (secOverrides.sectionCardBg && isSafeCssColor(secOverrides.sectionCardBg)) {
      colorVars.push(`--site-surface-1: ${secOverrides.sectionCardBg} !important;`);
      colorVars.push(`--site-surface-2: ${secOverrides.sectionCardBg} !important;`);
      colorVars.push(`--card-bg: ${secOverrides.sectionCardBg} !important;`);
      colorVars.push(`--site-surface-elevated: ${secOverrides.sectionCardBg} !important;`);
      colorVars.push(`--bento-bg: ${secOverrides.sectionCardBg} !important;`);
      colorVars.push(`--grid-card-bg: ${secOverrides.sectionCardBg} !important;`);
      if (isValidHexColor(secOverrides.sectionCardBg)) {
        const cardFg = getAccessibleForeground(secOverrides.sectionCardBg);
        colorVars.push(`--card-fg: ${cardFg} !important;`);
        colorVars.push(`--bento-card-fg: ${cardFg} !important;`);
      }
    }
    if (secOverrides.sectionCardBorder && isSafeCssColor(secOverrides.sectionCardBorder)) {
      colorVars.push(`--card-border: ${secOverrides.sectionCardBorder} !important;`);
      colorVars.push(`--site-border: ${secOverrides.sectionCardBorder} !important;`);
      colorVars.push(`--bento-border: ${secOverrides.sectionCardBorder} !important;`);
      colorVars.push(`--grid-card-border: ${secOverrides.sectionCardBorder} !important;`);
      colorVars.push(`--scholar-border: ${secOverrides.sectionCardBorder} !important;`);
    }
    if (colorVars.length > 0) {
      css += `${selector} { ${colorVars.join(" ")} } `;
    }

    // Direct Element Overrides (guaranteeing text contrast against custom background graphics)
    if (secOverrides.sectionHeadingColor && isValidHexColor(secOverrides.sectionHeadingColor)) {
      const headingTargets = buildDescendants([
        "h1", "h2", "h3", "h4", "h5", "h6",
        ".section-title", ".card-title", ".pub-title", ".pub-title a",
        ".research-card h3", ".timeline-role", ".course-title",
        ".project-title", ".skill-category-title", "strong"
      ]);
      css += `${headingTargets} { color: ${secOverrides.sectionHeadingColor} !important; } `;
    }
    if (secOverrides.sectionTextColor && isValidHexColor(secOverrides.sectionTextColor)) {
      const textTargets = [
        ...baseSelectors,
        ...baseSelectors.flatMap((s) => [
          `${s} p`, `${s} li`, `${s} .section-desc`, `${s} .pub-description`,
          `${s} .timeline-desc`, `${s} .course-desc`, `${s} .project-desc`,
          `${s} .research-card p`, `${s} .about-body`, `${s} .bio-text`, `${s} .item-desc`
        ])
      ].join(", ");
      css += `${textTargets} { color: ${secOverrides.sectionTextColor} !important; } `;
    }
    if (secOverrides.sectionMutedColor && isValidHexColor(secOverrides.sectionMutedColor)) {
      const mutedTargets = buildDescendants([
        ".section-subtitle", ".text-muted", ".pub-authors", ".pub-meta",
        ".pub-venue", ".pub-date", ".timeline-meta", ".timeline-org",
        ".course-meta", ".project-meta", "figcaption", "small",
        "time", ".date", ".badge", ".keyword-pill"
      ]);
      css += `${mutedTargets} { color: ${secOverrides.sectionMutedColor} !important; } `;
    }
    if (secOverrides.sectionAccentColor && isValidHexColor(secOverrides.sectionAccentColor)) {
      const accentFg = getAccessibleForeground(secOverrides.sectionAccentColor);
      // Inline textual links (excluding action buttons and badges with background fills)
      const linkTargets = buildDescendants([
        "a:not(.btn):not(.btn-action):not(.btn-outline):not(.pub-link-btn):not(.download-btn):not(.social-pill):not(.hero-chip):not(.badge-primary):not(.badge-accent)",
        "a:not(.btn):not(.btn-action):not(.btn-outline):not(.pub-link-btn):not(.download-btn):not(.social-pill):not(.hero-chip):not(.badge-primary):not(.badge-accent):visited",
        ".pub-link",
        ".accent",
      ]);
      const dotTargets = buildDescendants([".timeline-dot"]);
      css += `${linkTargets} { color: ${secOverrides.sectionAccentColor} !important; } `;
      css += `${dotTargets} { background-color: ${secOverrides.sectionAccentColor} !important; box-shadow: 0 0 0 2px ${secOverrides.sectionAccentColor} !important; } `;

      // Primary filled action buttons/badges
      const filledButtonClasses = [
        ".btn:not(.btn-outline)",
        ".btn-action",
        ".download-btn",
        ".badge-primary",
        ".badge-accent",
      ];
      const filledButtonTargets = buildDescendants(filledButtonClasses);
      const filledButtonHoverTargets = buildDescendants(filledButtonClasses.map((c) => `${c}:hover`));
      css += `${filledButtonTargets} { background-color: ${secOverrides.sectionAccentColor} !important; color: ${accentFg} !important; border-color: ${secOverrides.sectionAccentColor} !important; } `;
      css += `${filledButtonHoverTargets} { opacity: 0.92 !important; color: ${accentFg} !important; background-color: ${secOverrides.sectionAccentColor} !important; } `;

      // Outline and pill buttons
      const outlineButtonClasses = [
        ".pub-link-btn",
        ".btn-outline",
        ".social-pill",
        ".hero-chip",
      ];
      const outlineButtonTargets = buildDescendants(outlineButtonClasses);
      const outlineButtonHoverTargets = buildDescendants(outlineButtonClasses.map((c) => `${c}:hover`));
      css += `${outlineButtonTargets} { color: ${secOverrides.sectionAccentColor} !important; border-color: ${secOverrides.sectionAccentColor} !important; } `;
      css += `${outlineButtonHoverTargets} { background-color: ${secOverrides.sectionAccentColor} !important; color: ${accentFg} !important; border-color: ${secOverrides.sectionAccentColor} !important; } `;
    }
    if (secOverrides.sectionCardBg && isSafeCssColor(secOverrides.sectionCardBg)) {
      const cardBgTargets = buildDescendants(commonCardClasses);
      css += `${cardBgTargets} { background-color: ${secOverrides.sectionCardBg} !important; } `;
    }
    if (secOverrides.sectionCardBorder && isSafeCssColor(secOverrides.sectionCardBorder)) {
      const cardBorderTargets = buildDescendants(commonCardClasses);
      css += `${cardBorderTargets} { border-color: ${secOverrides.sectionCardBorder} !important; } `;
    }

    // ── Spacing Before & Spacing After ──
    const spacingMap: Record<string, string> = {
      none: "0",
      sm: "1.5rem",
      md: "3rem",
      lg: "5rem",
      xl: "8rem",
    };
    if (secOverrides.spacingBefore && spacingMap[secOverrides.spacingBefore]) {
      css += `${selector} { margin-top: ${spacingMap[secOverrides.spacingBefore]} !important; display: flow-root; } `;
    }
    if (secOverrides.spacingAfter && spacingMap[secOverrides.spacingAfter]) {
      css += `${selector} { margin-bottom: ${spacingMap[secOverrides.spacingAfter]} !important; display: flow-root; } `;
    }

    // ── Section Width Rules (Two-Layer Shell & Content Container) ──
    if (secOverrides.sectionWidth) {
      const containerTargets = baseSelectors.map((s) => `${s} .section-content-container`).join(", ");
      if (secOverrides.sectionWidth === "narrow") {
        css += `${selector} { max-width: 720px !important; margin-left: auto !important; margin-right: auto !important; width: 100% !important; } `;
        css += `${containerTargets} { max-width: 720px !important; margin-left: auto !important; margin-right: auto !important; width: 100% !important; } `;
      } else if (secOverrides.sectionWidth === "expanded") {
        css += `
          ${selector} {
            width: 100vw !important;
            max-width: 100vw !important;
            position: relative !important;
            left: 50% !important;
            right: 50% !important;
            margin-left: -50vw !important;
            margin-right: -50vw !important;
          }
          ${containerTargets} {
            max-width: 1280px !important;
            margin-left: auto !important;
            margin-right: auto !important;
            width: 100% !important;
            padding-left: 1.5rem !important;
            padding-right: 1.5rem !important;
          }
        `;
      } else if (secOverrides.sectionWidth === "full-bleed") {
        css += `
          ${selector} {
            width: 100vw !important;
            max-width: 100vw !important;
            position: relative !important;
            left: 50% !important;
            right: 50% !important;
            margin-left: -50vw !important;
            margin-right: -50vw !important;
          }
          ${containerTargets} {
            max-width: var(--max-w-content, 960px) !important;
            margin-left: auto !important;
            margin-right: auto !important;
            width: 100% !important;
            padding-left: 1.5rem !important;
            padding-right: 1.5rem !important;
          }
        `;
      }
    }
  }

  return css;
}

// ─── Dual-Ring Monogram Fallback in Universal Hero Renderer ─────────────────

export interface RenderAvatarOptions {
  photoUrl?: string | null;
  fullName?: string | null;
  avatarShape?: "circle" | "rounded" | "square" | "hexagon" | "squircle" | "none";
  avatarSize?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "max" | (string & {});
  avatarPosition?: "center" | "left" | "right";
  avatarBorder?: "none" | "thin" | "medium" | "thick" | "accent" | "gradient" | "dashed" | "double" | null;
  avatarBorderColor?: string | null;
  avatarBorderGradient?: "none" | "sunset" | "aurora" | "cyber" | "gold" | "primary-accent" | null;
  avatarShadow?: "none" | "subtle" | "elevated" | "3d" | "glow" | null;
  avatar3dEffect?: boolean | null;
  avatarBevel?: "none" | "light" | "medium" | "high" | "soft" | "chiseled" | "glass-lens" | "embossed" | null;
  avatarAnimation?: "none" | "float" | "pulse-glow" | "spin-ring" | null;
  avatarBgColor?: string | null;
  avatarBgGradient?: "none" | "glass" | "subtle-light" | "dark-slate" | "accent-tint" | null;
  avatarPadding?: "none" | "sm" | "md" | null;
  avatarVisible?: boolean;
}

export function renderAvatarHtml(options: RenderAvatarOptions): string {
  if (options.avatarVisible === false || options.avatarShape === "none") {
    return "";
  }

  const avatarShape = options.avatarShape || "circle";
  const avatarSize = options.avatarSize || "md";
  const avatarPos = options.avatarPosition || "center";
  const safePhotoUrl = options.photoUrl ? sanitizeUrl(options.photoUrl) : "";

  const sizeMap: Record<string, string> = {
    xs: "48px",
    sm: "64px",
    md: "96px",
    lg: "128px",
    xl: "160px",
    "2xl": "200px",
    "3xl": "240px",
    "4xl": "280px",
    "5xl": "320px",
    "6xl": "384px",
    max: "450px",
  };
  let size = "96px";
  if (avatarSize) {
    if (sizeMap[avatarSize]) {
      size = sizeMap[avatarSize];
    } else if (typeof avatarSize === "string" && (avatarSize.endsWith("px") || avatarSize.endsWith("rem") || avatarSize.endsWith("%"))) {
      size = avatarSize;
    } else if (typeof avatarSize === "string" && !isNaN(Number(avatarSize))) {
      size = `${avatarSize}px`;
    }
  }
  const radiusMap: Record<string, string> = {
    circle: "50%",
    squircle: "28%",
    rounded: "16px",
    square: "0px",
    hexagon: "25%",
    none: "0px",
  };
  const radius = radiusMap[avatarShape] || "50%";

  const isBorderless = options.avatarBorder === "none";

  // Smooth Shaded Edge Bevel (Pinback Button / Celluloid Button Badge Physics)
  let bevelShadow = "";
  let bevelBg = "";
  let badgeElevation = "";

  if (options.avatarBevel && options.avatarBevel !== "none") {
    // Physical button badge drop shadow for tactile elevation off the profile surface
    badgeElevation = "box-shadow: 0 4px 14px -2px rgba(0, 0, 0, 0.22), 0 2px 5px -1px rgba(0, 0, 0, 0.14);";

    // Celluloid Gloss: Directional diagonal dome sheen (135deg) across the upper curve,
    // with 100% crystal-clear transparent center (ZERO spotlight washout on face),
    // and subtle dark crescent shading at bottom-right for realistic dome curvature.
    bevelBg = "linear-gradient(135deg, rgba(255, 255, 255, 0.22) 0%, rgba(255, 255, 255, 0.08) 20%, rgba(255, 255, 255, 0) 45%, rgba(0, 0, 0, 0.03) 72%, rgba(0, 0, 0, 0.15) 100%)";

    if (options.avatarBevel === "light") {
      // Subtle Watch Crystal Rim
      bevelShadow = "inset 0 1px 2px 0 rgba(255, 255, 255, 0.25), inset 0 -1.5px 2px 0 rgba(0, 0, 0, 0.18), inset 0 0 0 1px rgba(255, 255, 255, 0.15)";
      bevelBg = "linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.04) 20%, rgba(255, 255, 255, 0) 45%, rgba(0, 0, 0, 0.02) 75%, rgba(0, 0, 0, 0.1) 100%)";
    } else if (options.avatarBevel === "high" || options.avatarBevel === "chiseled" || options.avatarBevel === "embossed") {
      // Deep Dome Crystal
      bevelShadow = "inset 0 2.5px 4px 0 rgba(255, 255, 255, 0.4), inset 0 -3px 5px 0 rgba(0, 0, 0, 0.35), inset 0 0 0 1.5px rgba(255, 255, 255, 0.25), inset 0 0 8px 1px rgba(0, 0, 0, 0.18)";
      bevelBg = "linear-gradient(135deg, rgba(255, 255, 255, 0.28) 0%, rgba(255, 255, 255, 0.1) 22%, rgba(255, 255, 255, 0) 45%, rgba(0, 0, 0, 0.04) 70%, rgba(0, 0, 0, 0.2) 100%)";
    } else {
      // "medium" | "soft" | "glass-lens" -> Pinback Button / Celluloid Button Badge (Recommended)
      bevelShadow = "inset 0 1.5px 2.5px 0 rgba(255, 255, 255, 0.32), inset 0 -2px 3.5px 0 rgba(0, 0, 0, 0.25), inset 0 0 0 1px rgba(255, 255, 255, 0.18), inset 0 0 5px 1px rgba(0, 0, 0, 0.12)";
    }
  }

  // Shadow styles
  let shadowStyle = "";
  if (options.avatarShadow === "none") {
    shadowStyle = "box-shadow: none !important;";
  } else if (options.avatarShadow === "subtle") {
    shadowStyle = "box-shadow: 0 4px 14px -2px rgba(0, 0, 0, 0.08) !important;";
  } else if (options.avatarShadow === "elevated") {
    shadowStyle = "box-shadow: 0 12px 28px -4px rgba(0, 0, 0, 0.18) !important;";
  } else if (options.avatarShadow === "3d") {
    shadowStyle = "box-shadow: 0 2px 4px rgba(0,0,0,0.06), 0 6px 14px rgba(0,0,0,0.08), 0 16px 28px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.15) !important;";
  } else if (options.avatarShadow === "glow") {
    shadowStyle = "box-shadow: 0 0 24px -2px var(--site-accent, var(--site-primary)) !important;";
  }

  // Inset bevel is applied strictly to .avatar-bevel-lens overlay so the <img> center stays 100% pristine and un-fogged

  // Border styles
  let borderStyle = "";
  if (isBorderless) {
    borderStyle = "border: none !important; outline: none !important; padding: 0 !important; background: none !important;";
    if (!options.avatarShadow && !bevelShadow) shadowStyle = "box-shadow: none !important;";
  } else if (options.avatarBorder) {
    const borderColor = options.avatarBorderColor && isValidHexColor(options.avatarBorderColor)
      ? options.avatarBorderColor
      : "var(--site-accent, var(--site-primary))";
    const borderSizes: Record<string, string> = { thin: "2px", medium: "4px", thick: "6px", double: "3px double" };

    if (options.avatarBorder === "accent") {
      borderStyle = `border: 4px solid ${borderColor} !important; outline: none !important;`;
    } else if (options.avatarBorder === "dashed") {
      borderStyle = `border: 3px dashed ${borderColor} !important; outline: none !important;`;
    } else if (options.avatarBorder === "double") {
      borderStyle = `border: 3px double ${borderColor} !important;`;
    } else if (options.avatarBorder === "gradient") {
      const gradientPresets: Record<string, string> = {
        sunset: "linear-gradient(135deg, #f97316, #ec4899, #8b5cf6)",
        aurora: "linear-gradient(135deg, #06b6d4, #10b981, #6366f1)",
        cyber: "linear-gradient(135deg, #06b6d4, #3b82f6, #a855f7)",
        gold: "linear-gradient(135deg, #d97706, #f59e0b, #fde68a)",
        "primary-accent": "linear-gradient(135deg, var(--site-primary), var(--site-accent))",
      };
      const grad = gradientPresets[options.avatarBorderGradient || ""] || "linear-gradient(135deg, var(--site-primary), var(--site-accent))";
      borderStyle = `padding: 3px !important; background: ${grad} !important; border: none !important; outline: none !important;`;
    } else if (borderSizes[options.avatarBorder]) {
      borderStyle = `border: ${borderSizes[options.avatarBorder]} solid ${borderColor} !important; outline: none !important;`;
    }
  }

  // Padding styles (for transparent logos)
  let paddingStyle = "";
  if (options.avatarPadding === "sm") {
    paddingStyle = "padding: 8px !important; box-sizing: border-box !important;";
  } else if (options.avatarPadding === "md") {
    paddingStyle = "padding: 16px !important; box-sizing: border-box !important;";
  }

  // Background styles
  let bgStyle = "";
  if (options.avatarBgColor && isValidHexColor(options.avatarBgColor)) {
    bgStyle = `background-color: ${options.avatarBgColor} !important;`;
  } else if (options.avatarBgGradient) {
    if (options.avatarBgGradient === "glass") {
      bgStyle = "background: var(--site-surface-1, rgba(255, 255, 255, 0.1)) !important; backdrop-filter: blur(12px) !important; -webkit-backdrop-filter: blur(12px) !important;";
    } else if (options.avatarBgGradient === "subtle-light") {
      bgStyle = "background: linear-gradient(135deg, #ffffff, #f1f5f9) !important;";
    } else if (options.avatarBgGradient === "dark-slate") {
      bgStyle = "background: linear-gradient(135deg, #0f172a, #1e293b) !important;";
    } else if (options.avatarBgGradient === "accent-tint") {
      bgStyle = "background: linear-gradient(135deg, var(--site-surface-2), var(--site-surface-1)) !important;";
    }
  }

  // Animation & 3D Tilt classes
  const classes = ["avatar"];
  if (options.avatar3dEffect) classes.push("avatar-3d-tilt");
  if (options.avatarAnimation && options.avatarAnimation !== "none") {
    classes.push(`avatar-anim-${options.avatarAnimation}`);
  }
  const classNames = classes.join(" ");

  const initials = (options.fullName || "P")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "P";

  const altText = options.fullName ? escapeHtml(options.fullName) : "Profile Avatar";

  const placeholderClasses = ["avatar-placeholder"];
  if (options.avatar3dEffect) placeholderClasses.push("avatar-3d-tilt");
  if (options.avatarAnimation && options.avatarAnimation !== "none") {
    placeholderClasses.push(`avatar-anim-${options.avatarAnimation}`);
  }
  if (!isBorderless) {
    placeholderClasses.push("monogram-dual-ring");
  }
  const placeholderClassNames = placeholderClasses.join(" ");

  let monogramBoxShadow = isBorderless
    ? (shadowStyle || "box-shadow: none !important;")
    : (shadowStyle || "box-shadow: 0 0 0 4px var(--site-bg), 0 0 0 6px var(--site-primary);");

  let monogramLensStyle = "";
  if (bevelShadow) {
    monogramLensStyle = `box-shadow: ${bevelShadow}; ${bevelBg ? `background: ${bevelBg};`: ''}`;
    if (!isBorderless) {
      monogramBoxShadow = `box-shadow: 0 0 0 4px var(--site-bg), 0 0 0 6px var(--site-primary) !important;`;
    }
  }

  const monogramBorder = isBorderless
    ? "border: none !important; outline: none !important; padding: 0 !important; background: none !important;"
    : (borderStyle || "border: 2px solid var(--site-border);");

  const monogramBg = bgStyle || "background: linear-gradient(135deg, var(--site-muted), var(--site-surface-elevated));";

  const extraImgStyles = [borderStyle, shadowStyle, bgStyle, paddingStyle].filter(Boolean).join(" ");
  const imgStyleAttr = `width: ${size}; height: ${size}; border-radius: ${radius} !important; object-fit: cover;${extraImgStyles ? " " + extraImgStyles : ""}`;

  const extraPlaceholderStyles = [monogramBg, "color: var(--site-primary);", "font-weight: 800;", monogramBorder, monogramBoxShadow, paddingStyle].filter(Boolean).join(" ");
  const placeholderStyleAttr = `width: ${size}; height: ${size}; border-radius: ${radius} !important; font-size: calc(${size} / 2.5); line-height: ${size}; display: flex; align-items: center; justify-content: center; ${extraPlaceholderStyles} letter-spacing: -0.05em;`;

  return `
    <div class="avatar-wrapper flex-${avatarPos}" style="display: flex; justify-content: ${
    avatarPos === "left" ? "flex-start" : avatarPos === "right" ? "flex-end" : "center"
  };">
      ${
        safePhotoUrl
          ? bevelShadow
            ? `<div class="avatar-container" style="position: relative; display: inline-block; width: ${size}; height: ${size}; border-radius: ${radius} !important; ${badgeElevation}"><img src="${safePhotoUrl}" alt="${altText}" class="${classNames}" loading="eager" fetchpriority="high" style="${imgStyleAttr}" /><div class="avatar-bevel-lens" style="position: absolute; inset: 0; border-radius: inherit; pointer-events: none; box-shadow: ${bevelShadow}; ${bevelBg ? `background: ${bevelBg}; ` : ''}z-index: 2;"></div></div>`
            : `<img src="${safePhotoUrl}" alt="${altText}" class="${classNames}" loading="eager" fetchpriority="high" style="${imgStyleAttr}" />`
          : bevelShadow
            ? `<div class="avatar-container" style="position: relative; display: inline-block; width: ${size}; height: ${size}; border-radius: ${radius} !important; ${badgeElevation}"><div class="${placeholderClassNames}" style="${placeholderStyleAttr}" aria-label="${altText}">${escapeHtml(
              initials
            )}</div><div class="avatar-bevel-lens" style="position: absolute; inset: 0; border-radius: inherit; pointer-events: none; ${monogramLensStyle} z-index: 2;"></div></div>`
            : `<div class="${placeholderClassNames}" style="${placeholderStyleAttr}" aria-label="${altText}">${escapeHtml(
              initials
            )}</div>`
      }
    </div>
  `;
}

export function formatPubType(type: string): string {
  switch (type) {
    case "journal":
      return "Journal Article";
    case "conference":
      return "Conference Paper";
    case "book_chapter":
      return "Book Chapter";
    case "thesis":
      return "Thesis / Dissertation";
    case "preprint":
      return "Preprint";
    default:
      return "Publication";
  }
}

export function formatDoiUrl(doi: string): string {
  const cleanDoi = doi.trim();
  if (!cleanDoi) return "";
  const lower = cleanDoi.toLowerCase();
  if (lower.includes("javascript:") || lower.includes("data:") || lower.includes("vbscript:")) {
    return "";
  }
  if (cleanDoi.startsWith("http://") || cleanDoi.startsWith("https://")) {
    return cleanDoi;
  }
  return `https://doi.org/${cleanDoi.replace(/^doi:\s*/i, "")}`;
}

export interface PublicationLinks {
  doiUrl?: string;
  publisherUrl?: string;
  pdfUrl?: string;
  codeUrl?: string;
}

export function resolvePublicationLinks(pub: {
  doi?: string | null;
  url?: string | null;
  publisherUrl?: string | null;
  pdfUrl?: string | null;
  codeUrl?: string | null;
}): PublicationLinks {
  const cleanDoi = pub.doi ? formatDoiUrl(String(pub.doi)) : "";
  const doiUrl = cleanDoi ? sanitizeUrl(cleanDoi) : undefined;
  const rawPublisher = pub.publisherUrl || pub.url;
  const publisherUrl = rawPublisher ? sanitizeUrl(String(rawPublisher)) : undefined;
  const pdfUrl = pub.pdfUrl ? sanitizeUrl(String(pub.pdfUrl)) : undefined;
  const codeUrl = pub.codeUrl ? sanitizeUrl(String(pub.codeUrl)) : undefined;

  const isSafe = (u?: string) => (u && !u.toLowerCase().includes("javascript:") ? u : undefined);

  return {
    doiUrl: isSafe(doiUrl),
    publisherUrl: isSafe(publisherUrl),
    pdfUrl: isSafe(pdfUrl),
    codeUrl: isSafe(codeUrl),
  };
}

export function formatDownloadCategory(category?: string): { label: string; icon: string } {
  switch (category) {
    case "lecture_notes":
      return { label: "Lecture Notes", icon: "📖" };
    case "slides":
      return { label: "Presentation Slides", icon: "📽️" };
    case "syllabus":
      return { label: "Syllabus / Guide", icon: "📋" };
    case "dataset":
      return { label: "Research Dataset", icon: "📊" };
    case "code_sample":
      return { label: "Code Sample", icon: "💻" };
    case "handout":
      return { label: "Handout", icon: "📄" };
    default:
      return { label: "Resource", icon: "📁" };
  }
}

export function formatProficiency(prof?: string): string {
  switch (prof?.toLowerCase()) {
    case "expert":
      return "Expert";
    case "advanced":
      return "Advanced";
    case "intermediate":
      return "Intermediate";
    case "familiar":
      return "Familiar";
    default:
      return prof ? prof.charAt(0).toUpperCase() + prof.slice(1) : "";
  }
}

export function formatLevelLabel(level?: string): string {
  switch (level?.toLowerCase()) {
    case "undergraduate":
      return "Undergraduate";
    case "postgraduate":
      return "Postgraduate / Masters";
    case "phd":
      return "Ph.D. Seminar";
    case "executive":
      return "Executive Education";
    default:
      return level ? level.charAt(0).toUpperCase() + level.slice(1) : "Course";
  }
}

export function formatTeachingRole(role?: string): string {
  switch (role?.toLowerCase()) {
    case "instructor":
      return "Lead Instructor";
    case "co_instructor":
      return "Co-Instructor";
    case "teaching_assistant":
      return "Teaching Assistant";
    case "guest_lecturer":
      return "Guest Lecturer";
    default:
      return role ? role.charAt(0).toUpperCase() + role.slice(1) : "Instructor";
  }
}

export function formatProjectRole(role?: string): string {
  switch (role?.toLowerCase()) {
    case "pi":
      return "Principal Investigator (PI)";
    case "co_pi":
      return "Co-Principal Investigator";
    case "lead_researcher":
      return "Lead Researcher";
    case "postdoc":
      return "Postdoctoral Scholar";
    case "collaborator":
      return "Collaborator";
    case "consultant":
      return "Consultant";
    default:
      return role ? role.charAt(0).toUpperCase() + role.slice(1) : "Contributor";
  }
}

export function formatPlatformLabel(platform?: string): string {
  switch (platform?.toLowerCase()) {
    case "scholar":
      return "Google Scholar";
    case "orcid":
      return "ORCID";
    case "researchgate":
      return "ResearchGate";
    case "linkedin":
      return "LinkedIn";
    case "github":
      return "GitHub";
    case "twitter":
      return "X / Twitter";
    case "youtube":
      return "YouTube";
    case "facebook":
      return "Facebook";
    case "instagram":
      return "Instagram";
    case "website":
      return "Personal Website";
    default:
      return platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : "Profile";
  }
}

export function renderHeroComponent(profile: ProfileData): string {
  const hero: HeroOverrides = {
    ...((profile.layoutOverrides as any) || {}),
    ...(profile.layoutOverrides?.hero || {}),
  };

  const layout = hero.heroLayout || "standard";
  const align = hero.nameAlign || "center";

  const showName = hero.nameVisible !== false;
  const showTitle = hero.titleVisible !== false;
  const showBio = hero.bioVisible !== false;

  const displayName = escapeHtml(hero.customName?.trim() || profile.fullName || "Scholar");

  const nameSizeMap: Record<string, string> = {
    compact: "1.25rem",
    sm: "1.5rem",
    md: "2rem",
    lg: "2.5rem",
    xl: "3rem",
    "2xl": "3.75rem",
    "3xl": "4.5rem",
    "4xl": "5.5rem",
  };
  let nameSize = "2.5rem";
  if (hero.nameSize) {
    if (nameSizeMap[hero.nameSize]) {
      nameSize = nameSizeMap[hero.nameSize];
    } else if (typeof hero.nameSize === "string" && (hero.nameSize.endsWith("rem") || hero.nameSize.endsWith("px") || hero.nameSize.endsWith("em"))) {
      nameSize = hero.nameSize;
    }
  }

  const weightMap: Record<string, string> = {
    normal: "400",
    medium: "500",
    semibold: "600",
    bold: "700",
    extrabold: "800",
    black: "900",
  };
  const fontWeight = weightMap[hero.nameWeight || "extrabold"] || "800";

  const trackingMap: Record<string, string> = {
    tight: "-0.035em",
    normal: "0",
    wide: "0.05em",
    widest: "0.12em",
  };
  const letterSpacing = trackingMap[hero.nameTracking || "normal"] || "-0.025em";

  let nameStyles = `font-size: ${nameSize}; margin-bottom: 0.5rem; line-height: 1.1; font-weight: ${fontWeight}; letter-spacing: ${letterSpacing};`;

  if (hero.nameStyle === "italic") {
    nameStyles += " font-style: italic;";
  } else if (hero.nameStyle === "uppercase") {
    nameStyles += " text-transform: uppercase;";
  } else if (hero.nameStyle === "underlined") {
    nameStyles += " text-decoration: underline; text-decoration-color: var(--site-primary, #3b82f6); text-underline-offset: 6px;";
  }

  if (hero.nameStyle === "gradient") {
    nameStyles += " background: linear-gradient(135deg, var(--site-primary, #3b82f6) 0%, var(--site-accent, #6366f1) 60%, var(--site-text-strong, #0f172a) 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;";
  } else if (hero.nameColor && isValidHexColor(hero.nameColor)) {
    nameStyles += ` color: ${hero.nameColor};`;
  } else {
    nameStyles += " color: var(--site-text-strong);";
  }

  const avatarHtml = renderAvatarHtml({
    photoUrl: profile.photoUrl,
    fullName: hero.customName?.trim() || profile.fullName,
    avatarShape: hero.avatarShape,
    avatarSize: hero.avatarSize,
    avatarPosition: hero.avatarPosition,
    avatarBorder: hero.avatarBorder,
    avatarBorderColor: hero.avatarBorderColor,
    avatarBorderGradient: hero.avatarBorderGradient,
    avatarShadow: hero.avatarShadow,
    avatar3dEffect: hero.avatar3dEffect,
    avatarBevel: hero.avatarBevel,
    avatarAnimation: hero.avatarAnimation,
    avatarBgColor: hero.avatarBgColor,
    avatarBgGradient: hero.avatarBgGradient,
    avatarPadding: hero.avatarPadding,
    avatarVisible: hero.avatarVisible,
  });

  const infoHtml = `
    <div class="hero-info text-${align}" style="text-align: ${align};">
      ${
        showName
          ? `<h1 class="hero-name" style="${nameStyles}">${displayName}</h1>`
          : ""
      }
      ${
        showTitle && profile.title
          ? `<div class="title" style="font-size: 1.2rem; font-weight: 500; margin-bottom: 1rem; color: var(--site-primary); opacity: 0.95;">${escapeHtml(
              profile.title
            )}</div>`
          : ""
      }
      ${
        showBio && profile.bio
          ? `<div class="bio" style="font-size: 1.05rem; opacity: 0.85; max-width: 720px; margin: 0 ${
              align === "center" ? "auto" : "0"
            }; line-height: 1.65; color: var(--site-text);">${escapeHtml(profile.bio)}</div>`
          : ""
      }
    </div>
  `;

  // Layout structuring
  let content = "";
  if (layout === "minimal") {
    content = infoHtml;
  } else if (layout === "split-left") {
    content = `<div style="display: flex; gap: 2.5rem; align-items: center; flex-wrap: wrap;">
      ${avatarHtml ? `<div style="flex-shrink: 0;">${avatarHtml}</div>` : ""}
      <div style="flex: 1; min-width: 280px;">${infoHtml}</div>
    </div>`;
  } else if (layout === "split-right") {
    content = `<div style="display: flex; gap: 2.5rem; align-items: center; flex-wrap: wrap; flex-direction: row-reverse;">
      ${avatarHtml ? `<div style="flex-shrink: 0;">${avatarHtml}</div>` : ""}
      <div style="flex: 1; min-width: 280px;">${infoHtml}</div>
    </div>`;
  } else {
    // standard, banner, card
    content = `
      <div style="display: flex; flex-direction: column; gap: 1.5rem;">
        ${avatarHtml}
        ${infoHtml}
      </div>
    `;
  }

  const paddingMap: Record<string, string> = {
    none: "0",
    sm: "1.25rem",
    md: "2.5rem",
    lg: "4rem",
    xl: "6rem",
  };
  const pad = paddingMap[hero.heroPadding || "md"] || "2.5rem";
  let wrapperStyle = `padding: ${pad} 1rem; display: block;`;

  if (layout === "card") {
    wrapperStyle +=
      " background: var(--site-surface-elevated); border: 1px solid var(--site-border); border-radius: var(--radius, 12px); box-shadow: 0 10px 25px -5px rgba(0,0,0,0.08); margin-bottom: 3rem;";
  } else {
    wrapperStyle += " margin-bottom: 2.5rem;";
  }

  const isFixed = layout === "banner" && hero.heroBackgroundPosition === "fixed";
  const fixedClass = isFixed ? " has-fixed-background" : "";
  const fixedAttr = isFixed ? ' data-parallax="fixed"' : "";

  return `
    <header class="profolio-hero layout-${layout}${fixedClass}" style="${wrapperStyle}"${fixedAttr}>
      ${content}
    </header>
  `;
}

// ─── Companion Media & Video Embed Parser ───────────────────────────────────

export function parseVideoEmbedUrl(url: string): { type: "youtube" | "vimeo" | "direct"; embedUrl: string } | null {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();

  // YouTube: watch, youtu.be, embed, shorts
  const ytMatch = trimmed.match(
    /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/i
  );
  if (ytMatch && ytMatch[1]) {
    return {
      type: "youtube",
      embedUrl: `https://www.youtube-nocookie.com/embed/${ytMatch[1]}`,
    };
  }

  // Vimeo
  const vimeoMatch = trimmed.match(
    /(?:vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/[^\/]*\/videos\/|album\/(?:\d+\/)?video\/|video\/|)(\d+)|player\.vimeo\.com\/video\/(\d+))/i
  );
  const vimeoId = vimeoMatch ? (vimeoMatch[1] || vimeoMatch[2]) : null;
  if (vimeoId) {
    return {
      type: "vimeo",
      embedUrl: `https://player.vimeo.com/video/${vimeoId}`,
    };
  }

  // Direct HTML5 video (.mp4, .webm, .ogg, .mov, .m4v)
  const safe = sanitizeUrl(trimmed);
  if (safe && /\.(mp4|webm|ogg|mov|m4v)($|\?)/i.test(safe)) {
    return {
      type: "direct",
      embedUrl: safe,
    };
  }

  return null;
}

export function renderSectionCompanionMedia(
  secOverrides?: SectionElementOverrides,
  sectionId = "section"
): string {
  if (!secOverrides || !secOverrides.mediaType || secOverrides.mediaType === "none") {
    return "";
  }

  const safeId = toSafeCssIdentifier(sectionId);
  const mediaType = secOverrides.mediaType;
  const aspectClass =
    secOverrides.mediaAspectRatio && secOverrides.mediaAspectRatio !== "auto"
      ? `aspect-${secOverrides.mediaAspectRatio}`
      : "";

  const radiusMap: Record<string, string> = {
    none: "0",
    sm: "4px",
    md: "8px",
    lg: "16px",
    full: "9999px",
  };
  const borderRadius = radiusMap[secOverrides.mediaBorderRadius || "md"] || "8px";

  const shadowMap: Record<string, string> = {
    none: "none",
    subtle: "0 2px 8px -1px rgba(0, 0, 0, 0.08)",
    elevated: "0 10px 25px -5px rgba(0, 0, 0, 0.12)",
    "3d": "0 16px 36px -8px rgba(0, 0, 0, 0.2), 0 4px 12px -2px rgba(0, 0, 0, 0.1)",
    glow: "0 0 24px rgba(56, 189, 248, 0.35)",
  };
  const boxShadow = shadowMap[secOverrides.mediaShadow || "subtle"] || "none";

  let bevelStyle = "";
  if (secOverrides.mediaBevel && secOverrides.mediaBevel !== "none") {
    const bevelOpacity =
      secOverrides.mediaBevel === "light"
        ? "0.4"
        : secOverrides.mediaBevel === "high"
        ? "0.9"
        : "0.65";
    bevelStyle = `box-shadow: ${boxShadow !== "none" ? boxShadow + ", " : ""}inset 0 1px 1px 0 rgba(255, 255, 255, ${bevelOpacity}), inset 0 -1px 1px 0 rgba(0, 0, 0, ${bevelOpacity});`;
  } else if (boxShadow !== "none") {
    bevelStyle = `box-shadow: ${boxShadow};`;
  }

  const figureStyle = `border-radius: ${borderRadius}; overflow: hidden; ${bevelStyle}`;

  // 1. Single Image
  if (mediaType === "image") {
    const item = secOverrides.mediaItems?.[0];
    if (!item?.url) return "";
    const safeUrl = sanitizeUrl(item.url);
    if (!safeUrl) return "";

    const imgTag = `<img src="${escapeHtml(safeUrl)}" alt="${escapeHtml(item.alt || "")}" style="width: 100%; height: auto; display: block; object-fit: cover;" loading="lazy" />`;
    const linkedImg = item.linkUrl
      ? `<a href="${escapeHtml(sanitizeUrl(item.linkUrl) || "#")}" target="_blank" rel="noopener noreferrer">${imgTag}</a>`
      : imgTag;

    return `
      <div class="section-companion-media media-type-image">
        <figure class="companion-figure ${aspectClass}" style="${figureStyle}">
          ${linkedImg}
          ${
            item.caption
              ? `<figcaption style="padding: 0.5rem 0.25rem; font-size: 0.8rem; opacity: 0.85; text-align: center;">${escapeHtml(item.caption)}</figcaption>`
              : ""
          }
        </figure>
      </div>
    `;
  }

  // 2. Image Gallery (Stacked, Grid, Row)
  if (mediaType === "image-gallery") {
    const items = (secOverrides.mediaItems || []).filter((it) => it.url && sanitizeUrl(it.url));
    if (items.length === 0) return "";

    const layout = secOverrides.mediaGalleryLayout || "stacked";
    const layoutClass = `companion-gallery-${layout}`;

    const figuresHtml = items
      .map((item) => {
        const safeUrl = sanitizeUrl(item.url);
        const imgTag = `<img src="${escapeHtml(safeUrl)}" alt="${escapeHtml(item.alt || "")}" style="width: 100%; height: auto; display: block; object-fit: cover;" loading="lazy" />`;
        const linkedImg = item.linkUrl
          ? `<a href="${escapeHtml(sanitizeUrl(item.linkUrl) || "#")}" target="_blank" rel="noopener noreferrer">${imgTag}</a>`
          : imgTag;

        return `
          <figure class="companion-figure ${aspectClass}" style="${figureStyle}">
            ${linkedImg}
            ${
              item.caption
                ? `<figcaption style="padding: 0.4rem 0.25rem; font-size: 0.78rem; opacity: 0.85; text-align: center;">${escapeHtml(item.caption)}</figcaption>`
                : ""
            }
          </figure>
        `;
      })
      .join("\n");

    return `
      <div class="section-companion-media media-type-gallery">
        <div class="${layoutClass}">
          ${figuresHtml}
        </div>
      </div>
    `;
  }

  // 3. Touch-Friendly Carousel with Interactive Navigation
  if (mediaType === "carousel") {
    const rawItems = (secOverrides.mediaItems || []).filter((it) => it.url && sanitizeUrl(it.url));
    const hasItems = rawItems.length > 0;
    const totalSlides = hasItems ? rawItems.length : 1;
    const hasMultiple = totalSlides > 1;

    const slidesHtml = hasItems
      ? rawItems
          .map((item, idx) => {
            const safeUrl = sanitizeUrl(item.url);
            const imgTag = `<img src="${escapeHtml(safeUrl)}" alt="${escapeHtml(item.alt || `Slide ${idx + 1}`)}" loading="lazy" />`;
            const linkedImg = item.linkUrl
              ? `<a href="${escapeHtml(sanitizeUrl(item.linkUrl) || "#")}" target="_blank" rel="noopener noreferrer">${imgTag}</a>`
              : imgTag;

            return `
              <div class="profolio-carousel-slide" id="slide-${safeId}-${idx}">
                ${linkedImg}
                ${item.caption ? `<div class="profolio-carousel-caption">${escapeHtml(item.caption)}</div>` : ""}
              </div>
            `;
          })
          .join("\n")
      : `
          <div class="profolio-carousel-slide profolio-carousel-placeholder" style="display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 240px; background: var(--site-muted, #1e293b); color: var(--site-text-muted, #94a3b8); text-align: center; padding: 2rem;">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="margin-bottom: 0.75rem; opacity: 0.75;">
              <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
              <circle cx="9" cy="9" r="2"/>
              <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
            </svg>
            <div style="font-size: 0.95rem; font-weight: 600; margin-bottom: 0.25rem;">Touch Carousel</div>
            <div style="font-size: 0.8rem; opacity: 0.8;">Add images in Section Media Manager</div>
          </div>
        `;

    const navButtonsHtml = hasMultiple
      ? `
        <button type="button" class="profolio-carousel-btn prev" aria-label="Previous slide"
          onclick="(function(btn){
            var c = btn.closest('.profolio-carousel');
            var t = c ? c.querySelector('.profolio-carousel-track') : null;
            if (t) {
              t.scrollBy({ left: -t.clientWidth, behavior: 'smooth' });
              var i = Math.max(0, Math.round((t.scrollLeft - t.clientWidth) / (t.clientWidth || 1)));
              var stat = c.querySelector('.profolio-carousel-status');
              if (stat) stat.textContent = 'Slide ' + (i + 1) + ' of ' + ${totalSlides};
            }
          })(this)">&#10094;</button>
        <button type="button" class="profolio-carousel-btn next" aria-label="Next slide"
          onclick="(function(btn){
            var c = btn.closest('.profolio-carousel');
            var t = c ? c.querySelector('.profolio-carousel-track') : null;
            if (t) {
              t.scrollBy({ left: t.clientWidth, behavior: 'smooth' });
              var i = Math.min(${totalSlides - 1}, Math.round((t.scrollLeft + t.clientWidth) / (t.clientWidth || 1)));
              var stat = c.querySelector('.profolio-carousel-status');
              if (stat) stat.textContent = 'Slide ' + (i + 1) + ' of ' + ${totalSlides};
            }
          })(this)">&#10095;</button>
      `
      : "";

    const dotsHtml = hasMultiple
      ? `
        <div class="profolio-carousel-dots">
          ${rawItems
            .map(
              (_, i) => `
            <button type="button" class="profolio-carousel-dot${i === 0 ? " active" : ""}" aria-label="Go to slide ${i + 1}"
              onclick="(function(btn){
                var c = btn.closest('.profolio-carousel');
                var t = c ? c.querySelector('.profolio-carousel-track') : null;
                if (t) {
                  t.scrollTo({ left: ${i} * t.clientWidth, behavior: 'smooth' });
                  var dots = c.querySelectorAll('.profolio-carousel-dot');
                  dots.forEach(function(d, idx){ d.classList.toggle('active', idx === ${i}); });
                  var stat = c.querySelector('.profolio-carousel-status');
                  if (stat) stat.textContent = 'Slide ${i + 1} of ${totalSlides}';
                }
              })(this)"></button>
          `
            )
            .join("")}
        </div>
      `
      : "";

    const onScrollAttr = hasMultiple
      ? `onscroll="(function(t){
          var c = t.closest('.profolio-carousel');
          if(!c) return;
          var i = Math.round(t.scrollLeft / (t.clientWidth || 1));
          var dots = c.querySelectorAll('.profolio-carousel-dot');
          dots.forEach(function(d, idx){ d.classList.toggle('active', idx === i); });
          var stat = c.querySelector('.profolio-carousel-status');
          if (stat) stat.textContent = 'Slide ' + (i + 1) + ' of ' + ${totalSlides};
        })(this)"`
      : "";

    const onKeyDownAttr = `onkeydown="(function(e){
      var c = e.currentTarget;
      var t = c ? c.querySelector('.profolio-carousel-track') : null;
      if (!t) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        t.scrollBy({ left: -t.clientWidth, behavior: 'smooth' });
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        t.scrollBy({ left: t.clientWidth, behavior: 'smooth' });
      }
    })(event)"`;

    return `
      <div class="section-companion-media media-type-carousel">
        <div class="profolio-carousel" id="carousel-${safeId}" style="${figureStyle}" tabindex="0" role="region" aria-label="Media carousel" aria-roledescription="carousel" ${onKeyDownAttr}>
          <div class="sr-only profolio-carousel-status" aria-live="polite" aria-atomic="true">Slide 1 of ${totalSlides}</div>
          <div class="profolio-carousel-track" ${onScrollAttr}>
            ${slidesHtml}
          </div>
          ${navButtonsHtml}
          ${dotsHtml}
        </div>
      </div>
    `;
  }

  // 4. Video Embed (YouTube, Vimeo, MP4)
  if (mediaType === "video" && secOverrides.mediaVideoUrl) {
    const parsed = parseVideoEmbedUrl(secOverrides.mediaVideoUrl);
    if (!parsed) return "";

    const videoAspect = aspectClass || "aspect-16-9";
    let playerHtml = "";

    if (parsed.type === "youtube" || parsed.type === "vimeo") {
      playerHtml = `
        <iframe
          src="${escapeHtml(parsed.embedUrl)}"
          title="Video Player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
          loading="lazy"
        ></iframe>
      `;
    } else {
      playerHtml = `
        <video controls playsinline preload="metadata">
          <source src="${escapeHtml(parsed.embedUrl)}" type="video/mp4">
          Your browser does not support HTML5 video.
        </video>
      `;
    }

    return `
      <div class="section-companion-media media-type-video">
        <div class="companion-video-container ${videoAspect}" style="${figureStyle}">
          ${playerHtml}
        </div>
      </div>
    `;
  }

  // 5. Ambient Graphic Animation
  if (mediaType === "animation") {
    return `
      <div class="section-companion-media media-type-animation">
        <div class="companion-figure ${aspectClass}" style="${figureStyle}; display: flex; align-items: center; justify-content: center; min-height: 180px; background: var(--site-muted);">
          <div class="section-anim-ambient-float" style="width: 64px; height: 64px; border-radius: 50%; background: linear-gradient(135deg, var(--site-primary), var(--site-accent)); opacity: 0.75;"></div>
        </div>
      </div>
    `;
  }

  return "";
}

function addClassToTag(tagHtml: string, newClass: string): string {
  if (/class=["']/i.test(tagHtml)) {
    return tagHtml.replace(/class=["']([^"']*)["']/i, (_match, cls) => {
      return `class="${cls} ${newClass}"`;
    });
  }
  return tagHtml.replace(/<([a-zA-Z0-9]+)/, `<$1 class="${newClass}"`);
}

export function decorateSectionHtml(
  rawSectionHtml: string,
  section: { id: string; type: string },
  overrides?: LayoutOverrides | Record<string, SectionElementOverrides> | null
): string {
  if (!rawSectionHtml) return "";

  // Extract <section ...> open tag, attributes, and </section> closing tag
  const sectionOpenMatch = rawSectionHtml.match(/<section([^>]*)>/i);
  const sectionCloseMatch = rawSectionHtml.lastIndexOf("</section>");

  if (!sectionOpenMatch || sectionCloseMatch === -1) {
    return rawSectionHtml;
  }

  const openTag = sectionOpenMatch[0];
  const openTagIndex = sectionOpenMatch.index ?? 0;
  const openTagEnd = openTagIndex + openTag.length;
  const preTag = rawSectionHtml.substring(0, openTagIndex);
  const innerHtml = rawSectionHtml.substring(openTagEnd, sectionCloseMatch);
  const postTag = rawSectionHtml.substring(sectionCloseMatch + "</section>".length);

  let finalOpenTag = openTag;
  if (!/class=["'][^"']*section-shell[^"']*["']/i.test(finalOpenTag)) {
    finalOpenTag = addClassToTag(finalOpenTag, "section-shell");
  }

  const secOverrides = resolveSectionOverride(section.id, section.type, overrides);
  if (!secOverrides) {
    const wrappedContentHtml = innerHtml.includes("section-content-container")
      ? innerHtml
      : `<div class="section-content-container">${innerHtml}</div>`;
    return `${preTag}${finalOpenTag}
      ${wrappedContentHtml}
    </section>${postTag}`;
  }

  // Extract .section-header to isolate it full-width across the top
  const headerMatch = innerHtml.match(/(<div\s+class=["'][^"']*section-header[^"']*["'][\s\S]*?<\/div>)/i);
  let headerHtml = "";
  let bodyHtml = innerHtml;

  if (headerMatch) {
    headerHtml = headerMatch[1];
    bodyHtml = innerHtml.replace(headerMatch[0], "").trim();
  }

  // Extract .filter-bar-container so it always spans 100% full width below the header,
  // preventing companion media / split wrappers from crushing or sitting on the same line as filter buttons.
  const filterMatch = bodyHtml.match(/(<div\s+class=["'][^"']*filter-bar-container[^"']*["'][\s\S]*?<\/div>)/i);
  let filterBarHtml = "";
  if (filterMatch) {
    filterBarHtml = filterMatch[1];
    bodyHtml = bodyHtml.replace(filterMatch[0], "").trim();
  }

  // Extract all <dialog> elements to ensure they remain at section root, never trapped in grid/split wrappers
  const dialogMatches = bodyHtml.match(/<dialog[\s\S]*?<\/dialog>/gi);
  let dialogsHtml = "";
  if (dialogMatches) {
    dialogsHtml = dialogMatches.join("\n");
    for (const d of dialogMatches) {
      bodyHtml = bodyHtml.replace(d, "").trim();
    }
  }

  // 1. Column Grid Transformation
  if (secOverrides.itemColumns) {
    const cols = secOverrides.itemColumns;
    const gridClass = `section-items-grid cols-${cols}`;

    // Common section item containers: .publications-list, .projects-grid, .timeline, .downloads-list, .skills-grid, .about-body, .contact-container, etc.
    const containerRegex = /<div\s+class=["']([^"']*(?:list|grid|timeline|about-body|contact-container)[^"']*)["']/i;
    if (containerRegex.test(bodyHtml)) {
      bodyHtml = bodyHtml.replace(containerRegex, (_match, classes) => {
        return `<div class="${classes} ${gridClass}"`;
      });
    } else {
      bodyHtml = `<div class="${gridClass}">${bodyHtml}</div>`;
    }
  }

  // 2. Companion Media & Split Layouts
  const mediaHtml = renderSectionCompanionMedia(secOverrides, section.id);
  if (mediaHtml) {
    // Companion media policy: For long list sections (publications, downloads, teaching, experience),
    // if mediaPosition is unset, default to "bottom" so cards aren't squished into a narrow column.
    const isListSection = ["publications", "downloads", "teaching", "experience"].includes(section.type);
    const defaultPos = isListSection ? "bottom" : "split-right";
    const pos = secOverrides.mediaPosition || defaultPos;
    const ratio = secOverrides.mediaSplitRatio || "60-40";

    if (pos === "top") {
      bodyHtml = `<div class="section-media-pane" style="margin-bottom: 1.5rem;">${mediaHtml}</div>${bodyHtml}`;
    } else if (pos === "bottom") {
      bodyHtml = `${bodyHtml}<div class="section-media-pane" style="margin-top: 1.5rem;">${mediaHtml}</div>`;
    } else {
      // split-right or split-left
      const splitPosClass = pos === "split-left" ? "split-pos-left" : "split-pos-right";
      bodyHtml = `
        <div class="section-split-wrapper ${splitPosClass} ratio-${ratio}">
          <div class="section-content-pane">
            ${bodyHtml}
          </div>
          <div class="section-media-pane">
            ${mediaHtml}
          </div>
        </div>
      `;
    }
  }

  // 3. Scroll Motion Class Decoration
  const motionClasses: string[] = [];

  const hasFixedBackground =
    Boolean(secOverrides.sectionBackgroundImageUrl) &&
    (secOverrides.sectionBackgroundAttachment === "fixed" ||
      secOverrides.sectionBackgroundPosition === "fixed" ||
      secOverrides.sectionScrollEffect === "parallax");

  if (hasFixedBackground) {
    finalOpenTag = addClassToTag(finalOpenTag, "has-fixed-background");
    finalOpenTag = finalOpenTag.replace(/>$/, ' data-parallax="fixed">');
  }

  if (secOverrides.sectionScrollEffect === "slide-up") {
    const scrollClass = "section-scroll-slide-up";
    if (!hasFixedBackground) {
      finalOpenTag = addClassToTag(finalOpenTag, scrollClass);
    }
    motionClasses.push(scrollClass);
  } else if (secOverrides.sectionScrollEffect === "fade-in") {
    const scrollClass = "section-scroll-fade-in";
    if (!hasFixedBackground) {
      finalOpenTag = addClassToTag(finalOpenTag, scrollClass);
    }
    motionClasses.push(scrollClass);
  }

  const hasScrollReveal = motionClasses.length > 0;
  if (!hasScrollReveal && secOverrides.sectionIdleAnimation === "subtle-pulse") {
    motionClasses.push("section-anim-subtle-pulse");
  } else if (
    !hasScrollReveal &&
    !hasFixedBackground &&
    secOverrides.sectionIdleAnimation === "ambient-float"
  ) {
    motionClasses.push("section-anim-ambient-float");
  }

  const topBlockHtml = [headerHtml, filterBarHtml].filter(Boolean).join("\n");
  const contentHtml = motionClasses.length
    ? `<div class="section-motion-content ${motionClasses.join(" ")}">
      ${topBlockHtml}
      ${bodyHtml}
    </div>`
    : `${topBlockHtml}
    ${bodyHtml}`;

  const wrappedContentHtml = contentHtml.includes("section-content-container")
    ? contentHtml
    : `<div class="section-content-container">${contentHtml}</div>`;

  return `${preTag}${finalOpenTag}
    ${wrappedContentHtml}
    ${dialogsHtml}
  </section>${postTag}`;
}

/**
 * Renders a standalone media section (Single image, gallery, carousel, video embed, or divider/spacer).
 */
export function renderStandaloneMediaSection(
  content: unknown,
  sectionId = "media",
  layoutOverrides?: LayoutOverrides | Record<string, SectionElementOverrides> | null,
  options?: { wrapperClass?: string; defaultTitle?: string; presentation?: string; emphasis?: string }
): string {
  const c = (content && typeof content === "object" ? content : {}) as Partial<MediaContent>;
  const safeId = toSafeCssIdentifier(sectionId);
  const heading = c.heading ? escapeHtml(String(c.heading)) : "";
  const description = c.description ? escapeHtml(String(c.description)) : "";
  const mediaType = c.mediaType || "image";
  const rawItems = Array.isArray(c.items) ? c.items : [];
  // Filter items: only keep items with non-empty URL and safe protocol
  const items = rawItems.filter(
    (it) => it && typeof it.url === "string" && it.url.trim().length > 0 && isSafeUrl(it.url.trim())
  );
  const videoUrl = c.videoUrl && isSafeUrl(String(c.videoUrl).trim()) ? String(c.videoUrl).trim() : "";
  const aspectRatio = c.aspectRatio || "auto";
  const galleryLayout = c.galleryLayout || "grid";
  const alignment = c.alignment || "center";
  const spacerHeightMap: Record<string, string> = {
    sm: "1.5rem",
    md: "3rem",
    lg: "5rem",
    xl: "8rem",
  };
  const spacerHeight = spacerHeightMap[c.spacerHeight || "md"] || "3rem";

  const secOverrides = resolveSectionOverride(sectionId, "media", layoutOverrides);
  const headerStyle = secOverrides?.headerStyle || "default";

  let headerHtml = "";
  if (heading && headerStyle !== "hidden") {
    headerHtml = `
      <div class="section-header style-${headerStyle}" style="text-align: ${alignment};">
        <h2>${heading}</h2>
        ${description ? `<p class="section-subtitle" style="text-align: ${alignment};">${description}</p>` : ""}
      </div>
    `;
  } else if (description) {
    headerHtml = `
      <p class="section-subtitle" style="text-align: ${alignment}; margin-bottom: 1.5rem;">${description}</p>
    `;
  }

  let mediaBodyHtml = "";
  if (mediaType === "spacer") {
    // Deliberate spacer mode
    mediaBodyHtml = `<div class="profolio-spacer" style="min-height: ${spacerHeight};" aria-hidden="true"></div>`;
  } else {
    // Build synthetic overrides for renderSectionCompanionMedia with filtered safe items
    const syntheticOverrides: SectionElementOverrides = {
      ...secOverrides,
      mediaType: mediaType,
      mediaAspectRatio: aspectRatio,
      mediaVideoUrl: videoUrl,
      mediaItems: items.map((it) => ({
        url: it.url,
        alt: it.alt,
        caption: it.caption,
        linkUrl: it.linkUrl && isSafeUrl(it.linkUrl.trim()) ? it.linkUrl.trim() : undefined,
      })),
      mediaGalleryLayout: galleryLayout,
    };

    mediaBodyHtml = renderSectionCompanionMedia(syntheticOverrides, sectionId);
  }

  // If no content rendered at all (e.g. incomplete media item with no URL, no video, no heading), omit from public render
  if (!mediaBodyHtml && !headerHtml) {
    return "";
  }

  const cls = options?.wrapperClass ? ` ${options.wrapperClass}` : "";

  return `
    <section id="section-${safeId}" data-section-type="media" class="section-shell media-section${cls}">
      <div class="section-content-container">
        ${headerHtml}
        ${mediaBodyHtml}
      </div>
    </section>
  `;
}

/**
 * Generates an IntersectionObserver client script to animate sections
 * configured with Scroll Motion ("slide-up" or "fade-in").
 */
export function generateScrollEffectsScript(): string {
  return `
    <script id="__profolio_scroll_effects__">
      (function() {
        var observer = null;
        function initScrollObservers() {
          if (observer) {
            observer.disconnect();
            observer = null;
          }
          var targets = document.querySelectorAll('.section-motion-content.section-scroll-fade-in, .section-motion-content.section-scroll-slide-up, .section-scroll-fade-in, .section-scroll-slide-up');
          if (!targets.length) return;

          if (!('IntersectionObserver' in window)) {
            targets.forEach(function(el) { el.classList.add('is-visible'); });
            return;
          }

          observer = new IntersectionObserver(function(entries) {
            entries.forEach(function(entry) {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-visible');
              }
            });
          }, {
            threshold: 0.08,
            rootMargin: '0px 0px -40px 0px'
          });

          targets.forEach(function(el) {
            var rect = el.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) {
              el.classList.add('is-visible');
            }
            observer.observe(el);
          });
        }

        function initFilterBars() {
          var bars = document.querySelectorAll('[data-filter-bar]');
          bars.forEach(function(bar) {
            var targetSelector = bar.getAttribute('data-filter-target');
            if (!targetSelector) return;
            var container = document.querySelector(targetSelector);
            if (!container) return;

            var filterBtns = bar.querySelectorAll('.filter-btn');
            var filterSelect = bar.querySelector('.filter-select');

            function applyFilter(category) {
              filterBtns.forEach(function(btn) {
                var isActive = btn.getAttribute('data-filter') === category;
                btn.classList.toggle('active', isActive);
                btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
              });
              if (filterSelect && filterSelect.value !== category) {
                filterSelect.value = category;
              }

              var items = container.querySelectorAll('[data-filter-category]');
              items.forEach(function(item) {
                var itemCat = item.getAttribute('data-filter-category');
                var match = category === 'all' || itemCat === category;
                item.style.display = match ? '' : 'none';
              });

              var groups = container.querySelectorAll('.pub-group');
              groups.forEach(function(grp) {
                var visibleChildren = grp.querySelectorAll('[data-filter-category]:not([style*="display: none"])');
                grp.style.display = visibleChildren.length > 0 ? '' : 'none';
              });
            }

            filterBtns.forEach(function(btn) {
              btn.addEventListener('click', function() {
                var cat = this.getAttribute('data-filter') || 'all';
                applyFilter(cat);
              });
            });

            if (filterSelect) {
              filterSelect.addEventListener('change', function() {
                applyFilter(this.value);
              });
            }
          });
        }

        function initScrollSpy() {
          var navLinks = document.querySelectorAll('.sticky-nav .nav-link, .sticky-nav .mobile-nav-link');
          if (!navLinks.length) return;

          var sections = [];
          navLinks.forEach(function(link) {
            var href = link.getAttribute('href');
            if (href && href.charAt(0) === '#' && href.length > 1) {
              var targetId = href.slice(1);
              var sec = document.getElementById(targetId) || document.getElementById('section-' + targetId);
              if (sec && sections.indexOf(sec) === -1) {
                sections.push(sec);
              }
            }
          });

          if (!sections.length) return;

          function onScroll() {
            var scrollPos = window.scrollY + 120;
            var currentSec = null;
            for (var i = 0; i < sections.length; i++) {
              var s = sections[i];
              if (s.offsetTop <= scrollPos) {
                currentSec = s;
              }
            }
            if (currentSec) {
              var secId = currentSec.id.replace(/^section-/, '');
              navLinks.forEach(function(link) {
                var href = link.getAttribute('href');
                var match = href === '#' + secId || href === '#' + currentSec.id;
                link.classList.toggle('active', match);
              });
            }
          }

          window.addEventListener('scroll', onScroll, { passive: true });
          onScroll();
        }

        function initMobileMenu() {
          var toggles = document.querySelectorAll('.mobile-menu-toggle');
          toggles.forEach(function(btn) {
            btn.addEventListener('click', function() {
              var nav = btn.closest('.sticky-nav');
              if (!nav) return;
              var drawer = nav.querySelector('.mobile-nav-drawer');
              if (!drawer) return;
              var isExpanded = btn.getAttribute('aria-expanded') === 'true';
              btn.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
              drawer.classList.toggle('open', !isExpanded);
              drawer.setAttribute('aria-hidden', isExpanded ? 'true' : 'false');
            });
          });

          var mobileLinks = document.querySelectorAll('.mobile-nav-link');
          mobileLinks.forEach(function(link) {
            link.addEventListener('click', function() {
              var drawer = link.closest('.mobile-nav-drawer');
              if (drawer) {
                drawer.classList.remove('open');
                drawer.setAttribute('aria-hidden', 'true');
                var nav = drawer.closest('.sticky-nav');
                if (nav) {
                  var btn = nav.querySelector('.mobile-menu-toggle');
                  if (btn) btn.setAttribute('aria-expanded', 'false');
                }
              }
            });
          });
        }

        function initProfolioDialogs() {
          var triggers = document.querySelectorAll('[data-dialog-trigger]');
          var lastActiveElement = null;

          triggers.forEach(function(trigger) {
            trigger.addEventListener('click', function(e) {
              e.preventDefault();
              var targetId = trigger.getAttribute('data-dialog-trigger');
              if (!targetId) return;
              var dialog = document.getElementById(targetId);
              if (!dialog) return;

              lastActiveElement = trigger;
              if (typeof dialog.showModal === 'function') {
                dialog.showModal();
              } else {
                dialog.setAttribute('open', '');
              }
              document.body.classList.add('profolio-dialog-open');

              var closeBtn = dialog.querySelector('[data-dialog-close]');
              if (closeBtn) {
                closeBtn.focus();
              }
            });
          });

          var dialogs = document.querySelectorAll('dialog.profolio-dialog');
          dialogs.forEach(function(dialog) {
            function closeDialog() {
              if (typeof dialog.close === 'function') {
                dialog.close();
              } else {
                dialog.removeAttribute('open');
              }
              document.body.classList.remove('profolio-dialog-open');
              if (lastActiveElement) {
                lastActiveElement.focus();
                lastActiveElement = null;
              }
            }

            dialog.addEventListener('close', function() {
              document.body.classList.remove('profolio-dialog-open');
              if (lastActiveElement) {
                lastActiveElement.focus();
                lastActiveElement = null;
              }
            });

            dialog.addEventListener('cancel', function(e) {
              e.preventDefault();
              closeDialog();
            });

            dialog.addEventListener('click', function(e) {
              var rect = dialog.getBoundingClientRect();
              var isInDialog = (
                rect.top <= e.clientY && e.clientY <= rect.top + rect.height &&
                rect.left <= e.clientX && e.clientX <= rect.left + rect.width
              );
              if (!isInDialog) {
                closeDialog();
              }
            });

            var closeBtns = dialog.querySelectorAll('[data-dialog-close]');
            closeBtns.forEach(function(btn) {
              btn.addEventListener('click', function(e) {
                e.preventDefault();
                closeDialog();
              });
            });
          });
        }

        function initParallax() {
          var elements = document.querySelectorAll('.has-fixed-background, .profolio-hero.layout-banner.has-fixed-background, [data-parallax="fixed"]');
          if (!elements.length) return;

          var isTouch = false;
          try {
            isTouch = window.matchMedia('(hover: none) and (pointer: coarse)').matches ||
                      ('ontouchstart' in window) ||
                      (navigator.maxTouchPoints > 0);
          } catch (e) {
            isTouch = false;
          }
          var inIframe = false;
          try {
            inIframe = window.self !== window.top;
          } catch (e) {
            inIframe = true;
          }

          var ticking = false;

          function updateParallax() {
            ticking = false;
            var vh = window.innerHeight || document.documentElement.clientHeight || 800;

            for (var i = 0; i < elements.length; i++) {
              var el = elements[i];
              var style = window.getComputedStyle(el);
              var attachment = style.getPropertyValue('background-attachment');

              // When background-attachment is natively fixed, browser compositor keeps the image stationary
              // This fallback only activates if an environment or container demotes attachment to scroll
              if (attachment === 'scroll') {
                var rect = el.getBoundingClientRect();
                if (rect.bottom >= -100 && rect.top <= vh + 100) {
                  var totalDist = vh + rect.height;
                  var progress = (vh - rect.top) / totalDist;
                  if (progress < 0) progress = 0;
                  if (progress > 1) progress = 1;

                  // Parallax Window mapping: smoothly shifts background-position from top (0%) to bottom (100%)
                  var percentY = (progress * 100).toFixed(1) + '%';
                  el.style.setProperty('background-position', 'center ' + percentY, 'important');
                }
              }
            }
          }

          function requestTick() {
            if (!ticking) {
              ticking = true;
              requestAnimationFrame(updateParallax);
            }
          }

          if (!window.__profolioParallaxRegistered) {
            window.addEventListener('scroll', requestTick, { passive: true });
            window.addEventListener('resize', requestTick, { passive: true });
            window.addEventListener('orientationchange', requestTick, { passive: true });
            window.__profolioParallaxRegistered = true;
          }

          requestTick();
        }

        function initAll() {
          initScrollObservers();
          initFilterBars();
          initScrollSpy();
          initMobileMenu();
          initProfolioDialogs();
          initParallax();
        }

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', initAll);
        } else {
          initAll();
        }

        window.__profolioInitScrollObservers = initScrollObservers;
        window.__profolioInitAll = initAll;
        window.__profolioInitDialogs = initProfolioDialogs;
        window.__profolioInitParallax = initParallax;
      })();
    </script>
  `;
}

// ─── Unified Rich Section Renderers ──────────────────────────────────────────

export function renderSectionHeader(
  heading: string,
  sectionId?: string,
  sectionType?: string,
  overrides?: Record<string, SectionElementOverrides> | LayoutOverrides | null
): string {
  const secOverrides =
    overrides && "sections" in overrides
      ? (overrides as LayoutOverrides).sections?.[sectionId || ""] ||
        (overrides as LayoutOverrides).sections?.[sectionType || ""]
      : (overrides as Record<string, SectionElementOverrides>)?.[sectionId || ""] ||
        (overrides as Record<string, SectionElementOverrides>)?.[sectionType || ""];

  const headerStyle = secOverrides?.headerStyle || "default";
  const customTitle = secOverrides?.customTitle
    ? escapeHtml(secOverrides.customTitle)
    : heading;

  return `
    <div class="section-header style-${headerStyle}">
      <h2>${customTitle}</h2>
    </div>
  `;
}

/**
 * Renders the About section with rich biography narrative and key highlight metrics.
 */
export function renderRichAboutSection(
  content: Record<string, unknown>,
  sectionId = "about",
  overrides?: Record<string, SectionElementOverrides> | LayoutOverrides | null,
  options?: { wrapperClass?: string; defaultTitle?: string; presentation?: string; emphasis?: string }
): string {
  const heading = content.heading
    ? escapeHtml(String(content.heading))
    : options?.defaultTitle || "About Me";
  const rawBody = content.body ? String(content.body) : "";
  const highlights = Array.isArray(content.highlights)
    ? (content.highlights as HighlightItem[])
    : [];
  const safeId = (sectionId || "about").replace(/[^a-zA-Z0-9_-]/g, "");

  const paragraphs = rawBody
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p style="margin-bottom: 0.75rem;">${renderMicroMarkdown(p)}</p>`)
    .join("\n");

  const highlightsHtml =
    highlights.length > 0
      ? `
    <div class="highlights-grid">
      ${highlights
        .map(
          (h) => `
        <div class="highlight-stat-card">
          <span class="highlight-label">${escapeHtml(String(h.label || ""))}</span>
          <span class="highlight-value">${renderMicroMarkdown(String(h.value || ""))}</span>
        </div>`
        )
        .join("\n")}
    </div>`
      : "";

  const cls = options?.wrapperClass ? ` ${options.wrapperClass}` : "";

  return `
    <section id="section-${safeId}" class="about-section${cls}" data-section-type="about">
      ${renderSectionHeader(heading, sectionId, "about", overrides)}
      <div class="about-content">
        ${paragraphs || "<p>Welcome to my academic profile.</p>"}
        ${highlightsHtml}
      </div>
    </section>`;
}

/**
 * Renders the Publications section with citation counts, featured badges, micro-links,
 * and progressive disclosure collapsible abstracts.
 */
export function renderRichPublicationsSection(
  content: Record<string, unknown>,
  sectionId = "publications",
  overrides?: Record<string, SectionElementOverrides> | LayoutOverrides | null,
  options?: { wrapperClass?: string; defaultTitle?: string; presentation?: string; emphasis?: string }
): string {
  const heading = content.heading
    ? escapeHtml(String(content.heading))
    : options?.defaultTitle || "Publications";
  const items = Array.isArray(content.items)
    ? (content.items as PublicationItem[])
    : [];
  const safeId = (sectionId || "publications").replace(/[^a-zA-Z0-9_-]/g, "");
  const cls = options?.wrapperClass ? ` ${options.wrapperClass}` : "";

  if (items.length === 0) {
    return `
      <section id="section-${safeId}" class="publications-section${cls}" data-section-type="publications">
        ${renderSectionHeader(heading, sectionId, "publications", overrides)}
        <p class="section-subtitle">No publications listed yet.</p>
      </section>`;
  }

  const sortedItems = [...items].sort((a: PublicationItem, b: PublicationItem) => {
    const yearA =
      typeof a.year === "number"
        ? a.year
        : typeof a.year === "string"
        ? parseInt(a.year, 10) || 0
        : 0;
    const yearB =
      typeof b.year === "number"
        ? b.year
        : typeof b.year === "string"
        ? parseInt(b.year, 10) || 0
        : 0;
    return yearB - yearA;
  });

  const dialogsHtml: string[] = [];

  const renderSinglePub = (pub: PublicationItem, idx: number): string => {
    const title = pub.title
      ? renderMicroMarkdown(String(pub.title))
      : "Untitled Publication";
    const authors = pub.authors ? escapeHtml(String(pub.authors)) : "";
    const venue = pub.venue ? escapeHtml(String(pub.venue)) : "";
    const type = pub.type ? escapeHtml(formatPubType(String(pub.type))) : "";
    const yearText = pub.year ? escapeHtml(String(pub.year)) : "";
    const { doiUrl, publisherUrl, pdfUrl, codeUrl } = resolvePublicationLinks(pub);
    const primaryUrl = publisherUrl || doiUrl || pdfUrl;

    const typeCategory = (pub.type ? formatPubType(String(pub.type)) : "Other")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "-");

    const metaParts: string[] = [];
    if (pub.featured) {
      metaParts.push('<span class="pub-featured-mark">★ Featured</span>');
    }
    if (type) {
      metaParts.push(`<span class="pub-type-mark">${type}</span>`);
    }
    if (pub.citationCount !== undefined && pub.citationCount !== null && pub.citationCount > 0) {
      metaParts.push(
        `<span class="pub-citations-mark">${renderSvgIcon("chart")} ${escapeHtml(String(pub.citationCount))} citations</span>`
      );
    }

    const actionLinksHtml: string[] = [];
    if (pub.abstract) {
      actionLinksHtml.push(
        `<button type="button" class="pub-link-btn pub-abstract-trigger" data-dialog-trigger="dialog-pub-${safeId}-${idx}" aria-haspopup="dialog">${renderSvgIcon("fileText")} Abstract</button>`
      );

      const pubDialog = renderDetailDialog({
        id: `pub-${safeId}-${idx}`,
        title: pub.title ? String(pub.title) : "Publication Abstract",
        subtitle: [authors, venue, yearText].filter(Boolean).join(" · "),
        categoryBadge: type || undefined,
        bodyHtml: `
          <div class="pub-dialog-content">
            <h4 style="font-size:0.85rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:var(--site-text-secondary); margin-bottom:0.6rem;">Abstract</h4>
            <div class="pub-dialog-abstract" style="line-height:1.7;">${renderMicroMarkdown(String(pub.abstract))}</div>
          </div>
        `,
        actionLinksHtml: [
          doiUrl ? `<a href="${doiUrl}" target="_blank" rel="noopener noreferrer" class="pub-link-btn">DOI ↗</a>` : "",
          publisherUrl ? `<a href="${publisherUrl}" target="_blank" rel="noopener noreferrer" class="pub-link-btn">Publisher ↗</a>` : "",
          pdfUrl ? `<a href="${pdfUrl}" target="_blank" rel="noopener noreferrer" class="pub-link-btn">PDF ↓</a>` : "",
          codeUrl ? `<a href="${codeUrl}" target="_blank" rel="noopener noreferrer" class="pub-link-btn code-link">Code ↗</a>` : "",
        ].filter(Boolean).join(" "),
      });
      dialogsHtml.push(pubDialog);
    }

    if (doiUrl) {
      actionLinksHtml.push(
        `<a href="${doiUrl}" target="_blank" rel="noopener noreferrer" class="pub-link-btn">DOI ↗</a>`
      );
    }
    if (publisherUrl) {
      actionLinksHtml.push(
        `<a href="${publisherUrl}" target="_blank" rel="noopener noreferrer" class="pub-link-btn">Publisher ↗</a>`
      );
    }
    if (pdfUrl) {
      actionLinksHtml.push(
        `<a href="${pdfUrl}" target="_blank" rel="noopener noreferrer" class="pub-link-btn">PDF ↓</a>`
      );
    }
    if (codeUrl) {
      actionLinksHtml.push(
        `<a href="${codeUrl}" target="_blank" rel="noopener noreferrer" class="pub-link-btn code-link">Code ↗</a>`
      );
    }

    const venueLine = [venue, yearText].filter(Boolean).join(", ");

    return `
      <div class="pub-item" data-filter-category="${typeCategory}">
        <div class="pub-title">
          ${primaryUrl ? `<a href="${primaryUrl}" target="_blank" rel="noopener noreferrer">${title}</a>` : title}
        </div>
        ${authors ? `<div class="pub-authors">${authors}</div>` : ""}
        ${venueLine ? `<div class="pub-venue">${venueLine}</div>` : ""}
        ${metaParts.length > 0 ? `<div class="pub-meta-line">${metaParts.join(" &bull; ")}</div>` : ""}
        ${actionLinksHtml.length > 0 ? `<div class="pub-action-links">${actionLinksHtml.join(" ")}</div>` : ""}
      </div>`;
  };

  let filterBarHtml = "";
  if (sortedItems.length >= 4) {
    const typeCounts: Record<string, number> = {};
    for (const item of sortedItems) {
      const t = item.type ? formatPubType(String(item.type)) : "Other";
      typeCounts[t] = (typeCounts[t] || 0) + 1;
    }
    const filterOptions = [
      { id: "all", label: "All", count: sortedItems.length },
      ...Object.entries(typeCounts).map(([key, count]) => ({
        id: key.toLowerCase().replace(/[^a-z0-9]/g, "-"),
        label: key,
        count,
      })),
    ];
    filterBarHtml = renderFilterBar(filterOptions, `#section-${safeId} .publications-list`, "all");
  }

  const displayStyle = (content.displayStyle as string) || "grouped_by_year";

  let bodyHtml = "";
  if (displayStyle === "list") {
    bodyHtml = sortedItems.map((pub, idx) => renderSinglePub(pub, idx)).join("\n");
  } else if (displayStyle === "grouped_by_type") {
    const groupedByType: Record<string, PublicationItem[]> = {};
    for (const item of sortedItems) {
      const typeKey = item.type ? formatPubType(String(item.type)) : "Other";
      if (!groupedByType[typeKey]) groupedByType[typeKey] = [];
      groupedByType[typeKey].push(item);
    }
    let globalIdx = 0;
    bodyHtml = Object.keys(groupedByType)
      .map(
        (typeKey) => `
        <div class="pub-group">
          <div class="pub-group-title" style="font-weight:700; font-size:1.1rem; margin:1.5rem 0 0.75rem 0; color:var(--site-accent, var(--site-primary));">${escapeHtml(typeKey)}</div>
          ${groupedByType[typeKey].map((pub) => renderSinglePub(pub, globalIdx++)).join("\n")}
        </div>`
      )
      .join("\n");
  } else {
    // grouped_by_year (default)
    const groupedByYear: Record<string, PublicationItem[]> = {};
    for (const item of sortedItems) {
      const yearKey = item.year ? String(item.year) : "Other";
      if (!groupedByYear[yearKey]) groupedByYear[yearKey] = [];
      groupedByYear[yearKey].push(item);
    }
    let globalIdx = 0;
    bodyHtml = Object.keys(groupedByYear)
      .sort((a, b) => {
        if (a === "Other") return 1;
        if (b === "Other") return -1;
        return Number(b) - Number(a);
      })
      .map(
        (year) => `
        <div class="pub-group">
          <div class="pub-group-title" style="font-weight:700; font-size:1.1rem; margin:1.5rem 0 0.75rem 0; color:var(--site-accent, var(--site-primary));">${escapeHtml(year)}</div>
          ${groupedByYear[year].map((pub) => renderSinglePub(pub, globalIdx++)).join("\n")}
        </div>`
      )
      .join("\n");
  }

  return `
    <section id="section-${safeId}" class="publications-section${cls}" data-section-type="publications">
      ${renderSectionHeader(heading, sectionId, "publications", overrides)}
      ${filterBarHtml}
      <div class="publications-list">
        ${bodyHtml}
      </div>
      ${dialogsHtml.join("\n")}
    </section>`;
}

/**
 * Renders the Research Interests section with vision statement callout, area descriptions,
 * and keyword #tags.
 */
export function renderRichResearchSection(
  content: Record<string, unknown>,
  sectionId = "research",
  overrides?: Record<string, SectionElementOverrides> | LayoutOverrides | null,
  options?: { wrapperClass?: string; defaultTitle?: string; presentation?: string; emphasis?: string }
): string {
  const heading = content.heading
    ? escapeHtml(String(content.heading))
    : options?.defaultTitle || "Research Interests";
  const areas = Array.isArray(content.areas)
    ? (content.areas as ResearchAreaItem[])
    : [];
  const safeId = (sectionId || "research").replace(/[^a-zA-Z0-9_-]/g, "");
  const summary = content.summary
    ? renderMicroMarkdown(String(content.summary))
    : "";
  const cls = options?.wrapperClass ? ` ${options.wrapperClass}` : "";

  if (!summary && areas.length === 0) {
    return `
      <section id="section-${safeId}" class="research-section${cls}" data-section-type="research">
        ${renderSectionHeader(heading, sectionId, "research", overrides)}
        <p class="section-subtitle">No research areas listed yet.</p>
      </section>`;
  }

  const areasHtml = areas
    .map((area: ResearchAreaItem, idx: number) => {
      const num = String(idx + 1).padStart(2, "0");
      const title = area.title ? escapeHtml(String(area.title)) : "Topic";
      const desc = area.description
        ? renderMicroMarkdown(String(area.description))
        : "";
      const keywords = Array.isArray(area.keywords) ? area.keywords : [];
      const tagsHtml =
        keywords.length > 0
          ? `<div class="research-tags">
              ${keywords.map((k) => `<span class="keyword-pill">#${escapeHtml(String(k))}</span>`).join(" ")}
            </div>`
          : "";

      return `
        <div class="research-topic-card">
          <div class="research-topic-header">
            <span class="research-topic-num">${num}</span>
            <h3 class="research-topic-title">${title}</h3>
          </div>
          ${desc ? `<div class="research-topic-desc">${desc}</div>` : ""}
          ${tagsHtml}
        </div>`;
    })
    .join("\n");

  return `
    <section id="section-${safeId}" class="research-section${cls}" data-section-type="research">
      ${renderSectionHeader(heading, sectionId, "research", overrides)}
      ${summary ? `<div class="research-vision-statement">${summary}</div>` : ""}
      <div class="research-grid">
        ${areasHtml}
      </div>
    </section>`;
}

/**
 * Renders the Experience & Appointments section with departments, locations, date badges,
 * and role descriptions.
 */
export function renderRichExperienceSection(
  content: Record<string, unknown>,
  sectionId = "experience",
  overrides?: Record<string, SectionElementOverrides> | LayoutOverrides | null,
  options?: { wrapperClass?: string; defaultTitle?: string; presentation?: string; emphasis?: string }
): string {
  const heading = content.heading
    ? escapeHtml(String(content.heading))
    : options?.defaultTitle || "Experience & Appointments";
  const items = Array.isArray(content.items)
    ? (content.items as ExperienceItem[])
    : [];
  const safeId = (sectionId || "experience").replace(/[^a-zA-Z0-9_-]/g, "");
  const cls = options?.wrapperClass ? ` ${options.wrapperClass}` : "";

  if (items.length === 0) {
    return `
      <section id="section-${safeId}" class="experience-section${cls}" data-section-type="experience">
        ${renderSectionHeader(heading, sectionId, "experience", overrides)}
        <p class="section-subtitle">No experience listed yet.</p>
      </section>`;
  }

  const timelineItems = items
    .map((item: ExperienceItem) => {
      const role = item.role ? escapeHtml(String(item.role)) : "Role";
      const org = item.organization ? escapeHtml(String(item.organization)) : "";
      const dept = item.department ? escapeHtml(String(item.department)) : "";
      const loc = item.location ? escapeHtml(String(item.location)) : "";
      const start = item.startDate ? escapeHtml(String(item.startDate)) : "";
      const end = item.current
        ? "Present"
        : item.endDate
        ? escapeHtml(String(item.endDate))
        : "Present";
      const desc = item.description
        ? renderMicroMarkdown(String(item.description))
        : "";

      const metaParts: string[] = [];
      if (start || end) metaParts.push(`<span>${renderSvgIcon("calendar")} ${start} &ndash; ${end}</span>`);
      if (dept) metaParts.push(`<span>${renderSvgIcon("institution")} ${dept}</span>`);
      if (loc) metaParts.push(`<span>${renderSvgIcon("mapPin")} ${loc}</span>`);

      return `
        <div class="timeline-item">
          <div class="timeline-marker" aria-hidden="true">
            <div class="timeline-dot"></div>
            <div class="timeline-line"></div>
          </div>
          <div class="timeline-content">
            <div class="timeline-header">
              <span class="timeline-role">${role}</span>
              ${item.current ? `<span class="badge-status-current">Current</span>` : ""}
            </div>
            ${org ? `<div class="timeline-org">@ ${org}</div>` : ""}
            ${metaParts.length > 0 ? `<div class="timeline-meta">${metaParts.join(" &bull; ")}</div>` : ""}
            ${desc ? `<div class="timeline-desc">${desc}</div>` : ""}
          </div>
        </div>`;
    })
    .join("\n");

  return `
    <section id="section-${safeId}" class="experience-section${cls}" data-section-type="experience">
      ${renderSectionHeader(heading, sectionId, "experience", overrides)}
      <div class="timeline">
        ${timelineItems}
      </div>
    </section>`;
}

/**
 * Renders the Teaching & Courses section with course codes, levels, roles, LMS portal links,
 * syllabus download links, and collapsible overview.
 */
export function renderRichTeachingSection(
  content: Record<string, unknown>,
  sectionId = "teaching",
  overrides?: Record<string, SectionElementOverrides> | LayoutOverrides | null,
  options?: { wrapperClass?: string; defaultTitle?: string; presentation?: string; emphasis?: string }
): string {
  const heading = content.heading
    ? escapeHtml(String(content.heading))
    : options?.defaultTitle || "Teaching & Courses";
  const items = Array.isArray(content.items)
    ? (content.items as TeachingItem[])
    : [];
  const safeId = (sectionId || "teaching").replace(/[^a-zA-Z0-9_-]/g, "");
  const cls = options?.wrapperClass ? ` ${options.wrapperClass}` : "";

  if (items.length === 0) {
    return `
      <section id="section-${safeId}" class="teaching-section${cls}" data-section-type="teaching">
        ${renderSectionHeader(heading, sectionId, "teaching", overrides)}
        <p class="section-subtitle">No courses listed yet.</p>
      </section>`;
  }

  let filterBarHtml = "";
  if (items.length >= 4) {
    const levelCounts: Record<string, number> = {};
    for (const item of items) {
      const lvl = item.level ? formatLevelLabel(String(item.level)) : "General";
      levelCounts[lvl] = (levelCounts[lvl] || 0) + 1;
    }
    const filterOptions = [
      { id: "all", label: "All", count: items.length },
      ...Object.entries(levelCounts).map(([key, count]) => ({
        id: key.toLowerCase().replace(/[^a-z0-9]/g, "-"),
        label: key,
        count,
      })),
    ];
    filterBarHtml = renderFilterBar(filterOptions, `#section-${safeId} .courses-grid`, "all");
  }

  const dialogsHtml: string[] = [];
  const courseCards = items
    .map((item: TeachingItem, idx: number) => {
      const code = item.courseCode ? escapeHtml(String(item.courseCode)) : "";
      const title = item.courseTitle
        ? escapeHtml(String(item.courseTitle))
        : "Course";
      const level = item.level
        ? escapeHtml(formatLevelLabel(String(item.level)))
        : "";
      const role = item.role
        ? escapeHtml(formatTeachingRole(String(item.role)))
        : "";
      const term = item.term ? escapeHtml(String(item.term)) : "";
      const inst = item.institution ? escapeHtml(String(item.institution)) : "";
      const desc = item.description
        ? renderMicroMarkdown(String(item.description))
        : "";
      const safeCourseUrl = item.courseUrl ? sanitizeUrl(String(item.courseUrl)) : "";
      const safeSyllabusUrl = item.syllabusUrl
        ? sanitizeUrl(String(item.syllabusUrl))
        : "";

      const courseCategory = (item.level ? formatLevelLabel(String(item.level)) : "General")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "-");

      const metaBadges: string[] = [];
      if (level) metaBadges.push(`<span class="meta-chip">${renderSvgIcon("graduationCap")} ${level}</span>`);
      if (role) metaBadges.push(`<span class="meta-chip">${renderSvgIcon("user")} ${role}</span>`);
      if (term) metaBadges.push(`<span class="meta-chip">${renderSvgIcon("calendar")} ${term}</span>`);
      if (inst) metaBadges.push(`<span class="meta-chip">${renderSvgIcon("institution")} ${inst}</span>`);

      const actionLinks: string[] = [];
      if (desc || safeSyllabusUrl) {
        actionLinks.push(
          `<button type="button" class="pub-link-btn course-dialog-trigger" data-dialog-trigger="dialog-course-${safeId}-${idx}" aria-haspopup="dialog">Overview &amp; Syllabus</button>`
        );

        const courseDialog = renderDetailDialog({
          id: `course-${safeId}-${idx}`,
          title: code ? `${code}: ${title}` : title,
          subtitle: [level, role, term, inst].filter(Boolean).join(" · "),
          categoryBadge: level || "Course",
          bodyHtml: `
            <div class="course-dialog-content">
              <h4 style="font-size:0.85rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:var(--site-text-secondary); margin-bottom:0.6rem;">Course Overview &amp; Prerequisites</h4>
              <div class="course-dialog-body-text" style="line-height:1.7;">${desc || "No extended overview provided."}</div>
            </div>
          `,
          actionLinksHtml: [
            safeCourseUrl ? `<a href="${safeCourseUrl}" target="_blank" rel="noopener noreferrer" class="pub-link-btn">Course Portal ↗</a>` : "",
            safeSyllabusUrl ? `<a href="${safeSyllabusUrl}" target="_blank" rel="noopener noreferrer" class="pub-link-btn">Syllabus PDF ↓</a>` : "",
          ].filter(Boolean).join(" "),
        });
        dialogsHtml.push(courseDialog);
      }

      if (safeCourseUrl) {
        actionLinks.push(
          `<a href="${safeCourseUrl}" target="_blank" rel="noopener noreferrer" class="pub-link-btn">Portal ↗</a>`
        );
      }
      if (safeSyllabusUrl) {
        actionLinks.push(
          `<a href="${safeSyllabusUrl}" target="_blank" rel="noopener noreferrer" class="pub-link-btn">Syllabus ↓</a>`
        );
      }

      return `
        <div class="course-card" data-filter-category="${courseCategory}">
          <div class="course-card-body">
            <div class="course-header">
              ${code ? `<span class="badge-course-code">${code}</span>` : ""}
              <h3 class="course-title">${title}</h3>
            </div>
            ${metaBadges.length > 0 ? `<div class="course-meta">${metaBadges.join(" ")}</div>` : ""}
            ${desc ? `<div class="course-summary-clamped">${desc}</div>` : ""}
          </div>
          ${actionLinks.length > 0 ? `<div class="course-links">${actionLinks.join(" ")}</div>` : ""}
        </div>`;
    })
    .join("\n");

  return `
    <section id="section-${safeId}" class="teaching-section${cls}" data-section-type="teaching">
      ${renderSectionHeader(heading, sectionId, "teaching", overrides)}
      ${filterBarHtml}
      <div class="courses-grid">
        ${courseCards}
      </div>
      ${dialogsHtml.join("\n")}
    </section>`;
}

/**
 * Renders the Projects & Grants section with sponsor chips, grant IDs, amounts,
 * active status, collaborators, and collapsible deep details/architecture diagrams.
 */
export function renderRichProjectsSection(
  content: Record<string, unknown>,
  sectionId = "projects",
  overrides?: Record<string, SectionElementOverrides> | LayoutOverrides | null,
  options?: { wrapperClass?: string; defaultTitle?: string; presentation?: string; emphasis?: string }
): string {
  const heading = content.heading
    ? escapeHtml(String(content.heading))
    : options?.defaultTitle || "Projects & Grants";
  const items = Array.isArray(content.items)
    ? (content.items as ProjectItem[])
    : [];
  const safeId = (sectionId || "projects").replace(/[^a-zA-Z0-9_-]/g, "");
  const cls = options?.wrapperClass ? ` ${options.wrapperClass}` : "";

  if (items.length === 0) {
    return `
      <section id="section-${safeId}" class="projects-section${cls}" data-section-type="projects">
        ${renderSectionHeader(heading, sectionId, "projects", overrides)}
        <p class="section-subtitle">No projects or grants listed yet.</p>
      </section>`;
  }

  let filterBarHtml = "";
  if (items.length >= 4) {
    const hasCurrent = items.some((i) => i.isCurrent);
    const hasCompleted = items.some((i) => !i.isCurrent);
    if (hasCurrent && hasCompleted) {
      const filterOptions = [
        { id: "all", label: "All", count: items.length },
        { id: "active", label: "Active", count: items.filter((i) => i.isCurrent).length },
        { id: "completed", label: "Completed", count: items.filter((i) => !i.isCurrent).length },
      ];
      filterBarHtml = renderFilterBar(filterOptions, `#section-${safeId} .projects-grid`, "all");
    }
  }

  const dialogsHtml: string[] = [];
  const renderProjectCard = (item: ProjectItem, idx: number, isLead = false) => {
    const title = item.title ? renderMicroMarkdown(String(item.title)) : "Project";
    const role = item.role ? escapeHtml(formatProjectRole(String(item.role))) : "";
    const sponsor = item.fundingAgency ? escapeHtml(String(item.fundingAgency)) : "";
    const amount = item.amount ? escapeHtml(String(item.amount)) : "";
    const grantNum = item.grantNumber ? escapeHtml(String(item.grantNumber)) : "";
    const start = item.startDate ? escapeHtml(String(item.startDate)) : "";
    const end = item.isCurrent ? "Active" : item.endDate ? escapeHtml(String(item.endDate)) : "";
    const collaborators = item.collaborators ? escapeHtml(String(item.collaborators)) : "";
    const safeProjectUrl = item.projectUrl ? sanitizeUrl(String(item.projectUrl)) : "";
    const safeImageUrl = item.imageUrl ? sanitizeUrl(String(item.imageUrl)) : "";
    const tags = Array.isArray(item.tags) ? (item.tags as string[]) : [];
    const projCategory = item.isCurrent ? "active" : "completed";

    const metaChips: string[] = [];
    if (role) metaChips.push(`<span class="meta-chip">${renderSvgIcon("user")} ${role}</span>`);
    if (sponsor) metaChips.push(`<span class="meta-chip">${renderSvgIcon("institution")} ${sponsor}</span>`);
    if (start || end) metaChips.push(`<span class="meta-chip">${renderSvgIcon("calendar")} ${start}${end ? ` &ndash; ${end}` : ""}</span>`);

    const tagPills = tags.map((t: string) => `<span class="keyword-pill">#${escapeHtml(String(t))}</span>`).join(" ");
    const rawDesc = item.description ? String(item.description) : "";
    const descHtml = rawDesc ? renderMicroMarkdown(rawDesc) : "";

    const cardActions: string[] = [];
    cardActions.push(`<button type="button" class="pub-link-btn project-detail-trigger project-dialog-trigger" data-dialog-trigger="dialog-proj-${safeId}-${idx}" aria-haspopup="dialog">View Details</button>`);
    if (safeProjectUrl) {
      cardActions.push(`<a href="${safeProjectUrl}" target="_blank" rel="noopener noreferrer" class="pub-link-btn">Website ↗</a>`);
    }

    const fullDialogMeta: string[] = [];
    if (role) fullDialogMeta.push(`<strong>Role:</strong> ${role}`);
    if (sponsor) fullDialogMeta.push(`<strong>Funding Agency:</strong> ${sponsor}`);
    if (grantNum) fullDialogMeta.push(`<strong>Grant #:</strong> ${grantNum}`);
    if (amount) fullDialogMeta.push(`<strong>Amount:</strong> ${amount}`);
    if (start || end) fullDialogMeta.push(`<strong>Timeline:</strong> ${start}${end ? ` &ndash; ${end}` : ""}`);
    if (collaborators) fullDialogMeta.push(`<strong>Collaborators:</strong> ${collaborators}`);

    const projDialog = renderDetailDialog({
      id: `proj-${safeId}-${idx}`,
      title: item.title ? String(item.title) : "Project Details",
      subtitle: [role, sponsor].filter(Boolean).join(" · "),
      categoryBadge: item.isCurrent ? "Active Project" : "Completed Project",
      bodyHtml: `
        <div class="project-dialog-content">
          ${safeImageUrl ? `<figure class="project-dialog-figure"><img src="${safeImageUrl}" alt="${escapeHtml(String(item.title || "Project Diagram"))}" class="project-dialog-img" loading="lazy" /><figcaption class="figure-caption">Architecture &amp; Research Diagram</figcaption></figure>` : ""}
          ${rawDesc ? `<div class="project-dialog-desc" style="line-height:1.7; margin-bottom:1rem;">${descHtml}</div>` : ""}
          ${fullDialogMeta.length > 0 ? `<div class="project-dialog-meta" style="font-size:0.9rem; line-height:1.6; color:var(--site-text-secondary);">${fullDialogMeta.join("<br>")}</div>` : ""}
          ${tagPills ? `<div class="project-dialog-tags" style="margin-top:1rem; display:flex; flex-wrap:wrap; gap:0.4rem;">${tagPills}</div>` : ""}
        </div>
      `,
      actionLinksHtml: safeProjectUrl ? `<a href="${safeProjectUrl}" target="_blank" rel="noopener noreferrer" class="pub-link-btn">Visit Project Website ↗</a>` : "",
    });
    dialogsHtml.push(projDialog);

    const cardClass = isLead ? "project-card lead-project-card" : "project-card";

    return `
      <div class="${cardClass}" data-filter-category="${projCategory}">
        <div class="project-card-body">
          <div class="project-header-row">
            <h3 class="project-title">${title}</h3>
            ${item.isCurrent ? `<span class="badge-status-active">Active</span>` : ""}
          </div>
          ${metaChips.length > 0 ? `<div class="project-meta">${metaChips.join(" ")}</div>` : ""}
          ${rawDesc ? `<div class="project-teaser-clamped">${descHtml}</div>` : ""}
          ${safeImageUrl ? `<div class="project-thumb-frame"><img src="${safeImageUrl}" alt="${escapeHtml(String(item.title || "Project preview"))}" class="project-thumb-img" loading="lazy" /></div>` : ""}
        </div>
        <div class="project-footer">
          <div class="project-actions">${cardActions.join(" ")}</div>
          ${tagPills ? `<div class="project-tags">${tagPills}</div>` : ""}
        </div>
      </div>`;
  };

  const presentation = options?.presentation || "grid";
  let layoutHtml = "";

  if (presentation === "featured" && items.length > 0) {
    const leadHtml = renderProjectCard(items[0], 0, true);
    const supportingHtml = items.slice(1).map((item, idx) => renderProjectCard(item, idx + 1, false)).join("\n");
    layoutHtml = `
      <div class="projects-featured-layout">
        <div class="projects-lead">${leadHtml}</div>
        ${supportingHtml ? `<div class="projects-supporting-list">${supportingHtml}</div>` : ""}
      </div>`;
  } else {
    const containerClass = presentation === "list" ? "projects-list" : "projects-grid";
    layoutHtml = `
      <div class="${containerClass}">
        ${items.map((item, idx) => renderProjectCard(item, idx, false)).join("\n")}
      </div>`;
  }

  return `
    <section id="section-${safeId}" class="projects-section${cls}" data-section-type="projects">
      ${renderSectionHeader(heading, sectionId, "projects", overrides)}
      ${filterBarHtml}
      ${layoutHtml}
      ${dialogsHtml.join("\n")}
    </section>`;
}

/**
 * Renders the Skills & Methodologies section with strict conditional proficiency levels.
 * (If level is selected: show it. If NOT selected: show clean pill with ZERO dots and NO empty parentheses).
 */
export function renderRichSkillsSection(
  content: Record<string, unknown>,
  sectionId = "skills",
  overrides?: Record<string, SectionElementOverrides> | LayoutOverrides | null,
  options?: { wrapperClass?: string; defaultTitle?: string; presentation?: string; emphasis?: string }
): string {
  const heading = content.heading
    ? escapeHtml(String(content.heading))
    : options?.defaultTitle || "Skills & Methodologies";
  const groups = Array.isArray(content.groups)
    ? (content.groups as SkillGroupItem[])
    : [];
  const safeId = (sectionId || "skills").replace(/[^a-zA-Z0-9_-]/g, "");
  const cls = options?.wrapperClass ? ` ${options.wrapperClass}` : "";

  if (groups.length === 0) {
    return `
      <section id="section-${safeId}" class="skills-section${cls}" data-section-type="skills">
        ${renderSectionHeader(heading, sectionId, "skills", overrides)}
        <p class="section-subtitle">No skill categories listed yet.</p>
      </section>`;
  }

  const groupCards = groups
    .map((group: SkillGroupItem) => {
      const categoryName = group.categoryName
        ? escapeHtml(String(group.categoryName))
        : "General";
      const skills = Array.isArray(group.skills)
        ? (group.skills as SkillItem[])
        : [];

      const skillItemsHtml = skills
        .map((skill: SkillItem) => {
          const name = skill.name ? escapeHtml(String(skill.name)) : "";
          if (!name) return "";
          const prof = skill.proficiency
            ? escapeHtml(String(skill.proficiency)).toLowerCase().trim()
            : "";

          let dotCount = 0;
          if (prof === "expert" || prof === "master") dotCount = 4;
          else if (prof === "advanced" || prof === "proficient") dotCount = 3;
          else if (prof === "intermediate" || prof === "competent") dotCount = 2;
          else if (prof === "familiar" || prof === "beginner" || prof === "novice") dotCount = 1;

          const presentation = options?.presentation || "grid";

          if (presentation === "tags") {
            return `<span class="skill-tag${prof ? ` level-${prof}` : ""}">${name}</span>`;
          }

          if (presentation === "progress") {
            const percent = dotCount * 25;
            return `
              <div class="skill-progress-entry">
                <div class="skill-progress-header">
                  <span class="skill-name">${name}</span>
                  ${prof ? `<span class="skill-level-label level-${prof}">${escapeHtml(formatProficiency(prof))}</span>` : ""}
                </div>
                ${prof ? `<div class="skill-progress-bar-bg"><div class="skill-progress-bar-fill level-${prof}" style="width: ${percent}%;"></div></div>` : ""}
              </div>`;
          }

          if (prof && dotCount > 0) {
            const dotsHtml = [1, 2, 3, 4]
              .map((n) => `<span class="skill-dot${n <= dotCount ? " filled" : ""}"></span>`)
              .join("");

            return `
              <div class="skill-entry skill-pill with-level level-${prof}">
                <div class="skill-entry-row">
                  <span class="skill-name">${name}</span>
                  <span class="skill-level-label level-${prof}">${escapeHtml(formatProficiency(prof))}</span>
                </div>
                <div class="skill-dots" aria-label="${escapeHtml(formatProficiency(prof))} proficiency (${dotCount} of 4)">
                  ${dotsHtml}
                </div>
              </div>`;
          }

          return `
            <div class="skill-entry skill-pill">
              <div class="skill-entry-row">
                <span class="skill-name">${name}</span>
              </div>
            </div>`;
        })
        .filter(Boolean)
        .join("\n");

      return `
        <div class="skill-group-card">
          <div class="skill-group-title">${categoryName}</div>
          <div class="skills-list">
            ${skillItemsHtml || "<span style='font-size:0.85rem; opacity:0.7;'>None listed.</span>"}
          </div>
        </div>`;
    })
    .join("\n");

  return `
    <section id="section-${safeId}" class="skills-section${cls}" data-section-type="skills">
      ${renderSectionHeader(heading, sectionId, "skills", overrides)}
      <div class="skills-grid">
        ${groupCards}
      </div>
    </section>`;
}

export function getDownloadCategorySvg(cat?: string): string {
  switch (cat) {
    case "lecture_notes":
      return renderSvgIcon("book");
    case "dataset":
      return renderSvgIcon("chart");
    case "code_sample":
      return renderSvgIcon("code");
    case "slides":
    case "syllabus":
    case "handout":
      return renderSvgIcon("fileText");
    default:
      return renderSvgIcon("fileText");
  }
}

/**
 * Renders the Downloads & Resources section with category badges (lecture notes, datasets, etc.),
 * format badges, file size, publish date, compact horizontal rows, and detail dialogs.
 */
export function renderRichDownloadsSection(
  content: Record<string, unknown>,
  sectionId = "downloads",
  overrides?: Record<string, SectionElementOverrides> | LayoutOverrides | null,
  options?: { wrapperClass?: string; defaultTitle?: string; presentation?: string; emphasis?: string }
): string {
  const heading = content.heading
    ? escapeHtml(String(content.heading))
    : options?.defaultTitle || "Downloads & Resources";
  const items = Array.isArray(content.items)
    ? (content.items as DownloadItem[])
    : [];
  const safeId = (sectionId || "downloads").replace(/[^a-zA-Z0-9_-]/g, "");
  const cls = options?.wrapperClass ? ` ${options.wrapperClass}` : "";

  if (items.length === 0) {
    return `
      <section id="section-${safeId}" class="downloads-section${cls}" data-section-type="downloads">
        ${renderSectionHeader(heading, sectionId, "downloads", overrides)}
        <p class="section-subtitle">No downloads listed yet.</p>
      </section>`;
  }

  let filterBarHtml = "";
  if (items.length >= 4) {
    const catCounts: Record<string, number> = {};
    for (const item of items) {
      const cat = formatDownloadCategory(item.category).label;
      catCounts[cat] = (catCounts[cat] || 0) + 1;
    }
    const filterOptions = [
      { id: "all", label: "All", count: items.length },
      ...Object.entries(catCounts).map(([key, count]) => ({
        id: key.toLowerCase().replace(/[^a-z0-9]/g, "-"),
        label: key,
        count,
      })),
    ];
    filterBarHtml = renderFilterBar(filterOptions, `#section-${safeId} .downloads-list`, "all");
  }

  const dialogsHtml: string[] = [];
  const downloadRows = items
    .map((item: DownloadItem, idx: number) => {
      const title = item.title
        ? escapeHtml(String(item.title))
        : "File Resource";
      const desc = item.description
        ? renderMicroMarkdown(String(item.description))
        : "";
      const format = item.fileFormat
        ? escapeHtml(String(item.fileFormat).toUpperCase())
        : "FILE";
      const size = item.fileSize ? escapeHtml(String(item.fileSize)) : "";
      const date = item.publishDate ? escapeHtml(String(item.publishDate)) : "";
      const fileName = item.fileName ? escapeHtml(String(item.fileName)) : "";
      const safeUrl = item.fileUrl ? sanitizeUrl(String(item.fileUrl)) : "";
      const cat = formatDownloadCategory(item.category);

      const dlCategory = cat.label.toLowerCase().replace(/[^a-z0-9]/g, "-");

      const metaParts: string[] = [];
      if (cat.label) metaParts.push(`<span>${escapeHtml(cat.label)}</span>`);
      if (size) metaParts.push(`<span>· ${size}</span>`);
      if (date) metaParts.push(`<span>· ${date}</span>`);

      const hasDetails = Boolean(desc || fileName);
      let detailsTriggerHtml = "";
      if (hasDetails) {
        detailsTriggerHtml = `<button type="button" class="resource-detail-btn" data-dialog-trigger="dialog-download-${safeId}-${idx}" aria-haspopup="dialog">Details</button>`;

        const dlDialog = renderDetailDialog({
          id: `download-${safeId}-${idx}`,
          title: item.title ? String(item.title) : "Resource Details",
          subtitle: [cat.label, format, size, date].filter(Boolean).join(" · "),
          categoryBadge: format,
          bodyHtml: `
            <div class="download-dialog-content">
              ${fileName ? `<div style="margin-bottom:0.75rem; font-size:0.85rem; color:var(--site-text-secondary); font-family:monospace; background:var(--site-surface-2, rgba(0,0,0,0.04)); padding:0.35rem 0.6rem; border-radius:4px; display:inline-block;">${fileName}</div>` : ""}
              ${desc ? `<div class="download-dialog-desc" style="line-height:1.7; font-size:0.925rem; color:var(--site-text-primary);">${desc}</div>` : "<p style='color:var(--site-text-secondary);'>No extended description provided.</p>"}
            </div>
          `,
          actionLinksHtml: safeUrl
            ? `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="pub-link-btn" style="background:var(--site-accent, var(--site-primary)); color:var(--site-on-accent, #fff); border-color:transparent;">Download File (${format}${size ? ` · ${size}` : ""}) ↓</a>`
            : "",
        });
        dialogsHtml.push(dlDialog);
      }

      return `
        <div class="resource-row download-card" data-filter-category="${dlCategory}">
          <span class="resource-icon-badge">${format}</span>
          <div class="resource-body">
            <div class="resource-title">
              ${safeUrl ? `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="download-link">${title}</a>` : title}
            </div>
            <div class="resource-meta-subline">
              ${metaParts.join(" ")}
              ${detailsTriggerHtml}
            </div>
          </div>
          <div class="resource-action">
            ${
              safeUrl
                ? `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="download-btn pub-link-btn">Download ${format} ↓</a>`
                : ""
            }
          </div>
        </div>`;
    })
    .join("\n");

  return `
    <section id="section-${safeId}" class="downloads-section${cls}" data-section-type="downloads">
      ${renderSectionHeader(heading, sectionId, "downloads", overrides)}
      ${filterBarHtml}
      <div class="downloads-list">
        ${downloadRows}
      </div>
      ${dialogsHtml.join("\n")}
    </section>`;
}

/**
 * Renders the Contact & Profiles section with direct communication channels
 * (phone, email, office, campus address, personal website) and social profile pills.
 */
export function renderRichContactSection(
  content: Record<string, unknown>,
  sectionId = "contact",
  overrides?: Record<string, SectionElementOverrides> | LayoutOverrides | null,
  options?: { wrapperClass?: string; defaultTitle?: string; presentation?: string; emphasis?: string }
): string {
  const heading = content.heading
    ? escapeHtml(String(content.heading))
    : options?.defaultTitle || "Contact & Profiles";
  const email = content.email ? String(content.email).trim() : "";
  const phone = content.phone ? String(content.phone).trim() : "";
  const office = content.office ? escapeHtml(String(content.office).trim()) : "";
  const address = content.address ? escapeHtml(String(content.address).trim()) : "";
  const website = content.website ? String(content.website).trim() : "";
  const socialLinks = Array.isArray(content.socialLinks)
    ? (content.socialLinks as SocialLinkItem[])
    : [];
  const safeId = (sectionId || "contact").replace(/[^a-zA-Z0-9_-]/g, "");
  const cls = options?.wrapperClass ? ` ${options.wrapperClass}` : "";

  const contactItems: string[] = [];

  if (office) {
    contactItems.push(`
      <div class="contact-channel-card">
        <span class="contact-channel-icon">${renderSvgIcon("building")}</span>
        <div class="contact-channel-body">
          <span class="contact-channel-label">Office</span>
          <span class="contact-channel-val">${office}</span>
        </div>
      </div>`);
  }

  if (address) {
    contactItems.push(`
      <div class="contact-channel-card">
        <span class="contact-channel-icon">${renderSvgIcon("mapPin")}</span>
        <div class="contact-channel-body">
          <span class="contact-channel-label">Address</span>
          <span class="contact-channel-val">${address}</span>
        </div>
      </div>`);
  }

  if (email) {
    const safeEmail = escapeHtml(email);
    contactItems.push(`
      <div class="contact-channel-card">
        <span class="contact-channel-icon">${renderSvgIcon("mail")}</span>
        <div class="contact-channel-body">
          <span class="contact-channel-label">Email</span>
          <span class="contact-channel-val"><a href="mailto:${safeEmail}">${safeEmail}</a></span>
        </div>
      </div>`);
  }

  if (phone) {
    const safePhone = escapeHtml(phone);
    contactItems.push(`
      <div class="contact-channel-card">
        <span class="contact-channel-icon">${renderSvgIcon("phone")}</span>
        <div class="contact-channel-body">
          <span class="contact-channel-label">Phone</span>
          <span class="contact-channel-val"><a href="tel:${safePhone}">${safePhone}</a></span>
        </div>
      </div>`);
  }

  if (website) {
    const safeWebUrl = sanitizeUrl(website);
    if (safeWebUrl) {
      const displayUrl = cleanDisplayUrl(website);
      contactItems.push(`
        <div class="contact-channel-card">
          <span class="contact-channel-icon">${renderSvgIcon("globe")}</span>
          <div class="contact-channel-body">
            <span class="contact-channel-label">Website</span>
            <span class="contact-channel-val"><a href="${safeWebUrl}" target="_blank" rel="noopener noreferrer">${escapeHtml(displayUrl)} ↗</a></span>
          </div>
        </div>`);
    }
  }

  const socialPills = socialLinks
    .map((s: SocialLinkItem) => {
      const platform = String(s.platform || "website");
      const url = String(s.url || "");
      const safeUrl = sanitizeUrl(url);
      if (!safeUrl) return "";

      const { iconName, label: defaultLabel } = resolvePlatformInfo(platform);
      const label = s.label
        ? escapeHtml(String(s.label))
        : defaultLabel;
      return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="social-pill pub-link-btn">${renderSvgIcon(iconName)} ${label} ↗</a>`;
    })
    .filter(Boolean)
    .join(" ");

  return `
    <section id="section-${safeId}" class="contact-section${cls}" data-section-type="contact">
      ${renderSectionHeader(heading, sectionId, "contact", overrides)}
      <div class="contact-container">
        <div class="contact-matrix">
          ${contactItems.length > 0 ? contactItems.join("\n") : "<p>Contact information available upon request.</p>"}
        </div>
        ${
          socialPills
            ? `<div class="contact-profiles-block" style="margin-top: 1.25rem;">
                 <div class="contact-channel-label" style="margin-bottom: 0.6rem; font-weight: 700; text-transform: uppercase; font-size: 0.75rem; color: var(--site-text-secondary, var(--site-muted-fg));">Profiles &amp; Networks</div>
                 <div class="social-pills-wrap" style="display:flex; flex-wrap:wrap; gap:0.5rem;">${socialPills}</div>
               </div>`
            : ""
        }
      </div>
    </section>`;
}

export function generatePresentationCss(): string {
  return `
    /* Presentation & Emphasis Variants */

    /* Publications */
    .publications-section.presentation-cards .publications-list,
    .publications-section.presentation-cards .pub-group {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 1.5rem;
    }
    .publications-section.presentation-cards .pub-item {
      border: 1px solid var(--site-border-subtle, var(--site-border));
      border-radius: var(--radius, 0.375rem);
      padding: 1.25rem;
      background: var(--site-surface-1, var(--site-bg));
      box-shadow: 0 2px 8px -2px rgba(0, 0, 0, 0.04);
    }
    .publications-section.presentation-cards .pub-item:last-child {
      border-bottom: 1px solid var(--site-border-subtle, var(--site-border));
    }
    .publications-section.presentation-compact_bibliography .pub-item {
      padding: 0.5rem 0;
      border-bottom: none;
    }
    .publications-section.presentation-compact_bibliography .pub-title,
    .publications-section.presentation-compact_bibliography .pub-authors,
    .publications-section.presentation-compact_bibliography .pub-venue {
      display: inline;
      margin-right: 0.5rem;
    }
    .publications-section.presentation-compact_bibliography .pub-title {
      font-size: 1rem;
      font-weight: 600;
    }
    .publications-section.presentation-compact_bibliography .pub-authors {
      font-size: 0.95rem;
    }
    .publications-section.presentation-compact_bibliography .pub-venue {
      font-size: 0.95rem;
    }
    .publications-section.presentation-compact_bibliography .pub-action-links {
      display: inline-flex;
    }

    /* About */
    .about-section.presentation-split .about-content {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 2rem;
    }
    @media (max-width: 768px) {
      .about-section.presentation-split .about-content {
        grid-template-columns: 1fr;
      }
    }
    .about-section.presentation-compact .about-bio {
      font-size: 1.1rem;
      font-weight: 400;
      line-height: 1.8;
      max-width: 800px;
      margin: 0 auto;
      text-align: center;
    }
    .about-section.presentation-compact .about-photo-wrapper {
      display: none;
    }

    /* Experience */
    .experience-section.presentation-cards .timeline {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 1.5rem;
    }
    .experience-section.presentation-cards .timeline-item {
      display: flex;
      flex-direction: column;
      border: 1px solid var(--site-border-subtle, var(--site-border));
      border-radius: var(--radius, 0.375rem);
      padding: 1.25rem;
      background: var(--site-surface-1, var(--site-bg));
    }
    .experience-section.presentation-cards .timeline-marker,
    .experience-section.presentation-cards .timeline-line {
      display: none;
    }
    .experience-section.presentation-minimal .timeline-item {
      padding: 0.75rem 0;
      border-bottom: 1px solid var(--site-border-subtle, var(--site-border));
      grid-template-columns: 1fr;
    }
    .experience-section.presentation-minimal .timeline-marker,
    .experience-section.presentation-minimal .timeline-line {
      display: none;
    }

    /* Skills */
    .skills-section.presentation-tags .skills-list {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }
    .skills-section.presentation-tags .skill-entry {
      width: auto;
      border-radius: 9999px;
      padding: 0.35rem 0.85rem;
    }
    .skills-section.presentation-progress .skill-entry {
      border: none;
      background: transparent;
      padding: 0;
      gap: 0.5rem;
    }
    .skills-section.presentation-progress .skill-entry::after {
      content: "";
      display: block;
      height: 6px;
      background: var(--site-border-subtle, var(--site-border));
      border-radius: 3px;
      width: 100%;
    }

    /* Downloads */
    .downloads-section.presentation-grid .downloads-list {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 1.25rem;
    }
    .downloads-section.presentation-grid .resource-row {
      flex-direction: column;
      align-items: flex-start;
    }
    .downloads-section.presentation-list .resource-row {
      border: none;
      border-bottom: 1px solid var(--site-border-subtle, var(--site-border));
      border-radius: 0;
      background: transparent;
    }

    /* Teaching */
    .teaching-section.presentation-compact .course-card {
      padding: 0.75rem 1rem;
      flex-direction: row;
      align-items: center;
      gap: 1rem;
    }
    .teaching-section.presentation-compact .course-header {
      margin-bottom: 0;
      flex: 1;
    }
    .teaching-section.presentation-compact .course-summary-clamped {
      display: none;
    }
    .teaching-section.presentation-cards .course-card {
      border: 1px solid var(--site-border-subtle, var(--site-border));
      background: var(--site-surface-1, var(--site-bg));
      box-shadow: 0 4px 12px -4px rgba(0, 0, 0, 0.08);
      transform: translateY(0);
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    .teaching-section.presentation-cards .course-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 16px -4px rgba(0, 0, 0, 0.1);
    }

    /* Projects */
    .projects-section.presentation-list .projects-list {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
    }
    .projects-section.presentation-list .project-card {
      display: flex;
      flex-direction: row;
      align-items: center;
    }
    .projects-section.presentation-featured .project-card:first-child {
      grid-column: 1 / -1;
      font-size: 1.1em;
    }
    .projects-section.presentation-featured .project-card:first-child .project-image-wrapper {
      height: 300px;
    }

    /* Contact */
    .contact-section.presentation-split .contact-container {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 2rem;
    }
    .contact-section.presentation-minimal .contact-details {
      text-align: center;
      justify-content: center;
    }
    .contact-section.presentation-minimal .contact-detail-item {
      justify-content: center;
    }

    /* Research */
    .research-section.presentation-compact .research-topic-card {
      padding: 0.75rem 1.25rem;
      border: none;
      border-bottom: 1px solid var(--site-border-subtle, var(--site-border));
      border-radius: 0;
      box-shadow: none;
      background: transparent;
    }
    .research-section.presentation-compact .research-topic-desc {
      display: none;
    }
    .research-section.presentation-cards .research-topic-card {
      border: 1px solid var(--site-border-strong, var(--site-border));
      box-shadow: 0 4px 12px -4px rgba(0, 0, 0, 0.08);
      background: var(--site-surface-1, var(--site-bg));
    }

    /* Media */
    .media-section.presentation-featured .media-gallery-grid {
      display: flex;
      flex-direction: column;
      gap: 2rem;
    }
    .media-section.presentation-featured .media-gallery-item:first-child {
      transform: scale(1.05);
      z-index: 2;
    }

    /* Emphasis (All sections) */
    .emphasis-subtle {
      opacity: 0.95;
    }
    .emphasis-strong {
      background: var(--site-surface-tinted, rgba(0,0,0,0.03));
      border: 2px solid var(--site-accent, var(--site-primary));
      padding: 2rem;
      border-radius: var(--radius, 0.5rem);
      box-shadow: 0 8px 24px -8px rgba(0, 0, 0, 0.12);
    }
  `;
}


